import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Badge, Button, Card, Input, Select, Stat, Tabs, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { PageScore } from '@/components/PageScore';
import { Experts } from '@/components/Experts';
import { Services } from '@/components/Services';
import { PageHealth } from '@/components/PageHealth';
import { ExpertPicker } from '@/components/ExpertPicker';
import { getExperts } from '@/data/client';
import { processImage } from '@/lib/image';
import { applyHeroImage, hasHeroPlaceholder } from '@/lib/hero';
import { articleStatus, isAbandonedInterview, supersededStoryIds, ARTICLE_STATUS_LABEL, ARTICLE_STATUS_HINT, ARTICLE_STATUS_ORDER, ARTICLE_STATUS_VARIANT, type ArticleStatusKey } from '@/lib/articleStatus';
import { useDictation, MicButton, LevelBars } from '@/lib/dictation';
import {
  completeInterview,
  createContentRequest, startInterviewNow,
  getInterviewSlots,
  getInterviewContacts,
  scheduleInterview,
  skipInterviewSchedule,
  getMyAvailability,
  setMyAvailability,
  type CmAvailability,
  createTopic,
  deleteArticle,
  deleteTopic,
  getAllTopics,
  getArticle,
  getArticles,
  attachTranscript,
  setInterviewProjects,
  assignPhotoProject,
  publishPhotoToCdn,
  type InterviewProject,
  type GenStory,
  searchClientReviews,
  attachReview,
  type ReviewMatch,
  getSocialPosts,
  generateSocialPosts,
  updateSocialPost,
  deleteSocialPost,
  getSocialFeedUrls,
  type SocialPost,
  type SocialPlatform,
  getDocumentUrl,
  getInterviewSettings,
  getTopics,
  qk,
  sendInterviewMessage,
  setArticleRejected,
  setArticleStage,
  updateArticle, writeArticleMeta,
  setTopicStatus,
  suggestTopics,
  uploadContentDocument,
  uploadContentPhoto,
  useTopic,
  getVault,
  getVaultSource,
  addVaultSource,
  uploadVaultFile,
  reanalyzeVaultSource,
  buildVaultAnchors,
  crawlSitemapPages,
  getCrawlFilter,
  setCrawlFilter,
  deleteVaultSource,
  deleteVaultPages,
  getGenerateContext,
  generateCaseStudy,
  getStoryProjects,
  generateBlog,
  generatePrompt,
  rewriteContent,
  getDnnConfig,
  setDnnConfig,
  getDnnCategories,
  testDnn,
  pushToDnn,
  DNN_TITLE_EXISTS,
  getDnnModules,
  getDnnHero,
  type DnnCategory,
  type DnnModule,
  publishGenerated,
  suggestBlogTopics,
  type SuggestedTopic,
  getBlogQueue,
  saveBlogTopic,
  askFaqPage,
  updateBlogTopic,
  deleteBlogTopic,
  regenerateBlogPiece,
  getBlogSources,
  getVaultStats,
  sendGapsToClient,
  getGapArticles,
  answerGap,
  declineGap,
  reopenGap,
  GAP_DECLINE_REASONS,
  type GapArticle,
  suggestTopicsFromInterviews,
  getSitemapPages,
  suggestTopicsForPage,
  sendTopicBatch,
  getBlogSettings,
  setBlogSettings,
  auditBlogDraft,
  reviseBlogDraft,
  type BlogWriter,
  type BlogSettings,
  type BlogSupport,
  assessTopicCoverage,
  type TopicCoverage,
  type BlogTopicOrigin,
  type InterviewTopicCandidate,
  type BlogQueueItem,
  type NearMatch,
  getCoveredTopics,
  importCoveredTopics,
  deleteCoveredTopic,
  generateWebHandoff,
  generateSitePage,
  checkSitePage,
  MAX_STORIES,
  getSitePageLayout,
  getSitePageRepoFiles,
  getSitePageRepoFile,
  getSitePageTemplates,
  setSitePageTemplate,
  SITE_PAGE_TYPES,
  type SitePageType,
  type LayoutSource,
  type LayoutUse,
  getHeroUsage, getHeroLocaleBrief, setHeroLocaleBrief, buildHeroPrompt as buildHeroPromptApi,
  startHeroImage, getHeroJob, getHeroJobsForArticle, getHeroLibrary, heroImageUrl, publishHeroImage, deleteHeroImage,
  type HeroJob,
  checkGithubConfig,
  type GithubCheck,
  getClientTemplates,
  setClientTemplate,
  getHouseStyle,
  setHouseStyle,
  type TemplateType,
  getSetupStatus,
  setAssetFolder,
  type SetupStatus,
  getContentPlatform,
  setContentPlatform,
  PLATFORM_FORMAT,
  type ContentPlatform,
  getGithubConfig,
  setGithubConfig,
  pushToSite,
  type GithubConfig,
  type ChatTurn,
  type InterviewStyle,
  type Topic,
  type VaultKind,
  type VaultStructured,
  type OutputFormat,
  type GroundingMode,
  type HandoffFile,
  type CaseStudyResult,
} from '@/data/client';
import { SitemapBuilder } from '@/components/SitemapBuilder';
import { SITE_MAP_KEY, getSiteMap } from '@/data/client';
import { useIsAdmin } from '@/auth/useIsAdmin';
import type { ArticleStage } from '@/data/types';
import { CaseStudyPhotos } from '@/components/studio/CaseStudyPhotos';
import { DraftGallery } from '@/components/studio/DraftGallery';
import { HtmlDraft, type DraftEditorHandle } from '@/components/DraftEditor';
import { caseStudyFromHtml, caseStudyHtml } from '@/lib/astro-case-study';
import { Hint } from '@/components/studio/Hint';
import { GeneratorV2, useGeneratorVersion } from '@/routes/generator/GeneratorV2';

interface Prefill { title?: string; jobType?: string; city?: string; topicId?: string }

const STAGES = ['Interview', 'Draft', 'Review', 'Published'];

/* What a library row IS. `article` covers generated pieces from before the kind
   was recorded, `interview` an intake that has not become one yet. */
const KIND_FILTER_LABEL: Record<string, string> = {
  case_study: 'Case studies',
  blog: 'Blogs',
  article: 'Articles',
  interview: 'Interviews',
};
const STATUS: Record<ArticleStage, { label: string; variant: 'success' | 'warning' | 'accent' | 'neutral' }> = {
  3: { label: 'Published', variant: 'success' },
  2: { label: 'In review', variant: 'warning' },
  1: { label: 'In draft', variant: 'accent' },
  0: { label: 'Interview', variant: 'neutral' },
};

function StatusPill({ a }: { a: { stage: number; pushedAt?: string } }) {
  const key = articleStatus(a);
  return (
    <span title={ARTICLE_STATUS_HINT[key]}>
      <Badge variant={ARTICLE_STATUS_VARIANT[key]} dot={key === 'draft' || key === 'review' || key === 'interview'}>
        {ARTICLE_STATUS_LABEL[key]}
      </Badge>
    </span>
  );
}

function Progress({ stage }: { stage: number }) {
  return (
    <div className="cq-prog" title={`Stage: ${STAGES[stage]}`}>
      {STAGES.map((s, i) => (
        <div key={s} className={`cq-prog__step ${i < stage ? 'is-done' : ''} ${i === stage ? 'is-now' : ''}`.trim()}>
          <span className="cq-prog__dot">{i < stage ? <Icon name="check" size={11} /> : i + 1}</span>
          <em>{s}</em>
        </div>
      ))}
    </div>
  );
}

/* The admin's status control. It offers the DERIVED status, not the raw stage,
   because that is what the row shows — a dropdown that cannot express "Pushed to
   site" leaves no way to correct the articles pushed before 0072 recorded it,
   which still read as drafts.

   Choosing "Pushed to site" stamps the delivery; choosing a workflow stage
   clears a hand-set one, which is how a wrong mark is undone. A real DNN push
   cannot be cleared this way — see the PATCH handler. */
function StageAdmin({ id, stage, pushedAt }: { id: string; stage: ArticleStage; pushedAt?: string }) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const current = articleStatus({ stage, pushedAt });
  const onChange = async (next: ArticleStatusKey) => {
    setSaving(true);
    try {
      if (next === 'pushed') {
        // Delivery is orthogonal to the stage, so the stage is left where it is.
        await setArticleStage(id, stage, true);
      } else {
        const nextStage = (next === 'published' ? 3 : next === 'review' ? 2 : next === 'draft' ? 1 : 0) as ArticleStage;
        await setArticleStage(id, nextStage, false);
      }
      qc.invalidateQueries({ queryKey: qk.articles });
      qc.invalidateQueries({ queryKey: ['article', id] });
    } finally {
      setSaving(false);
    }
  };
  return (
    <select
      className="lead-status"
      value={current}
      disabled={saving}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.value as ArticleStatusKey)}
      aria-label="Status"
      title={ARTICLE_STATUS_HINT[current]}
    >
      {ARTICLE_STATUS_ORDER.map((k) => <option key={k} value={k}>{ARTICLE_STATUS_LABEL[k]}</option>)}
    </select>
  );
}

/* ---- topic suggestions (SEO strategy) -------------------------------- */

/* Days for anything recent, a date once it stops being about elapsed time.
   A topic waiting eleven days is a nudge; one raised in August is history. */
function fmtTopicDate(iso: string): string {
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (!Number.isFinite(days)) return '';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 45) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function TopicCard({ t, onStart, admin, onSet, onRemove, busy }: {
  t: Topic;
  onStart: (t: Topic) => void;
  admin: boolean;
  onSet: (id: string, status: Topic['status']) => void;
  onRemove: (id: string) => void;
  busy: boolean;
}) {
  const meta = [t.projectType, t.city].filter(Boolean).join(' · ');
  return (
    <div className={`cq-topic ${t.status === 'used' || t.status === 'dismissed' ? 'cq-topic--muted' : ''}`.trim()}>
      <div className="cq-topic__body">
        <div className="cq-topic__top">
          <h4>{t.title}</h4>
          {t.source === 'ai' && <Badge variant="accent">AI</Badge>}
          {/* The four statuses mean different things to the two audiences. To
              an admin, "sent" is the one that matters — it is waiting on the
              client, and nothing in tritonIQ will move it along. */}
          {t.status === 'draft' && <Badge variant="warning">Needs review</Badge>}
          {admin && t.status === 'suggested' && <Badge variant="outline">Sent to client</Badge>}
          {t.status === 'used' && <Badge variant="success">Interview done</Badge>}
          {admin && t.status === 'dismissed' && <Badge variant="neutral">Dismissed</Badge>}
        </div>
        {admin && t.createdAt && (
          <p className="cq-topic__meta">
            {t.status === 'suggested' ? 'Waiting since ' : 'Raised '}{fmtTopicDate(t.createdAt)}
          </p>
        )}
        {meta && <p className="cq-topic__meta">{meta}</p>}
        {t.keywords && <p className="cq-topic__kw"><Icon name="search" size={12} /> {t.keywords}</p>}
        {(t.products || t.services) && <p className="cq-topic__sub">{[t.products, t.services].filter(Boolean).join(' · ')}</p>}
        {t.notes && <p className="cq-topic__notes">{t.notes}</p>}
      </div>
      <div className="cq-topic__actions">
        {t.status !== 'used' && (
          <Button variant="primary" size="sm" onClick={() => onStart(t)} iconRight={<Icon name="arrow-right" size={14} />}>Start interview</Button>
        )}
        {admin && (
          <>
            {t.status === 'draft' && <Button variant="ghost" size="sm" disabled={busy} onClick={() => onSet(t.id, 'suggested')} iconLeft={<Icon name="check" size={14} />}>Approve</Button>}
            {t.status !== 'dismissed' && t.status !== 'used' && <Button variant="ghost" size="sm" disabled={busy} onClick={() => onSet(t.id, 'dismissed')}>Dismiss</Button>}
            <IconBtn label="Delete topic" onClick={() => onRemove(t.id)} disabled={busy} />
          </>
        )}
      </div>
    </div>
  );
}

function IconBtn({ label, onClick, disabled, icon = 'trash' }: { label: string; onClick: () => void; disabled?: boolean; icon?: string }) {
  return (
    <button type="button" className="cq-topic__del" aria-label={label} onClick={onClick} disabled={disabled}>
      <Icon name={icon} size={14} />
    </button>
  );
}

const EMPTY_TOPIC = { title: '', projectType: '', city: '', keywords: '', products: '', services: '', notes: '' };

function TopicsPanel({ onStart, jobTypes }: { onStart: (t: Topic) => void; jobTypes: string[] }) {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const { data: topics = [] } = useQuery({
    queryKey: ['topics', isAdmin ? 'all' : 'suggested'],
    queryFn: isAdmin ? getAllTopics : getTopics,
  });
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(EMPTY_TOPIC);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  /* Both lists. The generator's "Vet an AI suggestion" reads ['allTopics'], so
     refreshing only ['topics'] left it showing whatever it held when the page
     loaded — usually nothing — and drafting topics never made them appear. */
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['topics'] });
    qc.invalidateQueries({ queryKey: ['allTopics'] });
  };
  const onSet = async (id: string, status: Topic['status']) => { setBusy(true); try { await setTopicStatus(id, status); refresh(); } finally { setBusy(false); } };
  const onRemove = async (id: string) => { setBusy(true); try { await deleteTopic(id); refresh(); } finally { setBusy(false); } };
  const add = async () => {
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      const r = await createTopic(form);
      setForm(EMPTY_TOPIC); setAdding(false); refresh();
      /* Advisory, after the fact: the brief is saved, and the person who typed
         it decides whether a close existing title changes anything. */
      if (r.near?.length) setMsg(`Added. Close to ${r.near[0].where === 'covered' ? 'a covered topic' : r.near[0].where === 'queue' ? 'a topic in the queue' : r.near[0].where === 'brief' ? 'another brief' : 'an article'}: “${r.near[0].title}”${r.near[0].detail ? ` (${r.near[0].detail})` : ''}.`);
    } finally { setBusy(false); }
  };
  const suggest = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await suggestTopics();
      refresh();
      /* The worker says WHICH of the three things happened; guessing here is
         what sent people to fix a profile that was fine. */
      setMsg(r.generated
        ? `Drafted ${r.generated} topic${r.generated === 1 ? '' : 's'} — review and approve below.`
        : r.note || 'No suggestions came back.');
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Suggestion failed.'); } finally { setBusy(false); }
  };

  // Clients with no suggested topics see nothing.
  if (!isAdmin && topics.length === 0) return null;

  /* Collapsed for admins once there is a list. The two counts worth seeing
     while it is shut are the two that are waiting on somebody: drafts waiting
     on Triton, and sent topics waiting on the client. */
  const needsReview = topics.filter((t) => t.status === 'draft').length;
  const waiting = topics.filter((t) => t.status === 'suggested').length;
  const open = !isAdmin || expanded || adding || !!msg;

  return (
    <Card padding={false} className="dash-leads">
      <div className="dash-panelhead dash-panelhead--pad">
        <button type="button" onClick={() => isAdmin && setExpanded((v) => !v)}
          style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', flex: 1, cursor: isAdmin ? 'pointer' : 'default' }}>
          <h2>
            {isAdmin && <><Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />{' '}</>}
            <Icon name="sparkles" size={16} /> Content topic ideas
            {isAdmin && topics.length > 0 && (
              <span className="dash-muted" style={{ fontWeight: 400, fontSize: 13 }}> · {topics.length}</span>
            )}
          </h2>
          <p>{!isAdmin
            ? 'Suggested stories from your Triton strategist — start an interview from any of these'
            : topics.length === 0
              ? 'SEO content briefs for this client — author them or let AI draft ideas from their data'
              : [
                needsReview ? `${needsReview} to review` : '',
                waiting ? `${waiting} sent, waiting on the client` : '',
              ].filter(Boolean).join(' · ') || 'Nothing outstanding'}</p>
        </button>
        {isAdmin && open && (
          <div className="cq-topic__head">
            <Button variant="outline" size="sm" onClick={suggest} disabled={busy} iconLeft={<Icon name="sparkles" size={15} />}>{busy ? 'Working…' : 'Suggest (AI)'}</Button>
            <Button variant="primary" size="sm" onClick={() => setAdding((v) => !v)} iconLeft={<Icon name="plus" size={15} />}>Add topic</Button>
          </div>
        )}
      </div>

      {open && (
      <div className="cq-topic__wrap">
        {isAdmin && (
          <p className="cq-topic__hint">
            <Icon name="alert-circle" size={13} /> AI suggestions use this client&rsquo;s business profile. Set it in <strong>Admin → Clients &rsquo; Profile</strong> so ideas match their real services and service areas.
          </p>
        )}
        {msg && <p className="reco__msg">{msg}</p>}
        {isAdmin && adding && (
          <div className="cq-topic__form">
            <div className="cq-fieldrow">
              <Input label="Title" required placeholder="e.g. Emergency AC rescue in Scottsdale" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <Select label="Project type" options={jobTypes} placeholder="Select" value={form.projectType} onChange={(e) => setForm({ ...form, projectType: e.target.value })} />
            </div>
            <div className="cq-fieldrow">
              <Input label="City / service area" placeholder="e.g. Scottsdale, AZ" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <Input label="Keywords" placeholder="comma-separated" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
            </div>
            <div className="cq-fieldrow">
              <Input label="Products" placeholder="brands / equipment" value={form.products} onChange={(e) => setForm({ ...form, products: e.target.value })} />
              <Input label="Services" placeholder="services to highlight" value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} />
            </div>
            <Textarea label="SEO angle / notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <div className="cq-topic__formactions">
              <button className="cq-cancel" onClick={() => { setAdding(false); setForm(EMPTY_TOPIC); }}>Cancel</button>
              <Button variant="primary" size="sm" onClick={add} disabled={busy || !form.title.trim()}>Add topic</Button>
            </div>
          </div>
        )}
        {topics.length === 0 ? (
          <p className="dash-muted" style={{ padding: '4px' }}>No topics yet. {isAdmin ? 'Add one or let AI suggest a set.' : ''}</p>
        ) : (
          <div className="cq-topic__grid">
            {topics.map((t) => <TopicCard key={t.id} t={t} onStart={onStart} admin={isAdmin} onSet={onSet} onRemove={onRemove} busy={busy} />)}
          </div>
        )}
      </div>
      )}
    </Card>
  );
}


// Admin-only vet action on an AI content-strategy recommendation: check the Vault
// for this idea, then send it (with the drafted questions) straight to the blog
// topic queue as an 'ai-idea' topic — the same approve → write → post flow.

/* AI content-strategy analysis: reads the client's profile (services, cities,
   SEO focus notes) + existing content and recommends the types of projects and
   locations to focus on next. Prescriptive by design (unlike traffic/campaign).
   Cached 24h; Refresh forces a fresh pass. Reuses the trafficIQ panel styles. */
/* The AI content strategy panel was removed on 2026-10-24: never used, and
   the only thing a client saw on the old Write tab — an empty state telling
   them to open a screen they cannot reach. Services and the coverage ledger
   now answer a sharper version of the same question (what has this company
   never told us, per service, with a source behind every claim), so anything
   replacing it should build on those. Its worker route is untouched, and the
   component is in git. */


/* ---- Authenticity Vault (admin-only) — grounded knowledge base -------- */

const VAULT_KIND_META: Record<VaultKind, { label: string; variant: 'accent' | 'success' | 'warning' | 'neutral' | 'outline' }> = {
  transcript: { label: 'Transcript', variant: 'accent' },
  website_page: { label: 'Website page', variant: 'success' },
  sitemap: { label: 'Sitemap', variant: 'neutral' },
  profile: { label: 'Profile', variant: 'warning' },
  article: { label: 'Existing article', variant: 'outline' },
  qa: { label: 'Client Q&A', variant: 'accent' },
};
const PASTE_KINDS: VaultKind[] = ['transcript', 'profile', 'article'];
const VAULT_KIND_ORDER: VaultKind[] = ['transcript', 'qa', 'website_page', 'sitemap', 'profile', 'article'];

function fmtTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

function VaultChips({ s }: { s: VaultStructured }) {
  const chips = [...(s.projects || []).map((p) => p.name), ...(s.topics || [])].filter(Boolean);
  const uniq = [...new Set(chips)].slice(0, 6);
  if (!uniq.length) return null;
  return <div className="vault-chips">{uniq.map((c, i) => <span key={i} className="vault-chip">{c}</span>)}</div>;
}

function VaultSourceView({ id }: { id: string }) {
  const { data, isLoading } = useQuery({ queryKey: [...qk.vault, id], queryFn: () => getVaultSource(id) });
  if (isLoading || !data) return <div className="vault-view"><p className="dash-muted">Loading source…</p></div>;
  const s = data.structured;
  return (
    <div className="vault-view">
      {s?.summary && <p className="vault-view__sum">{s.summary}</p>}
      {s && (s.projects?.length || s.entities?.brands?.length || s.entities?.products?.length || s.locations?.length) ? (
        <div className="vault-view__meta">
          {!!s.projects?.length && <div><strong>Projects detected</strong><span>{s.projects.map((p) => p.name).join(' · ')}</span></div>}
          {!!s.entities?.brands?.length && <div><strong>Brands</strong><span>{s.entities.brands.join(' · ')}</span></div>}
          {!!s.entities?.products?.length && <div><strong>Products</strong><span>{s.entities.products.join(' · ')}</span></div>}
          {!!s.locations?.length && <div><strong>Locations</strong><span>{s.locations.join(' · ')}</span></div>}
        </div>
      ) : null}
      <details className="vault-raw">
        <summary>Raw text ({data.rawText.length.toLocaleString()} chars)</summary>
        <pre>{data.rawText}</pre>
      </details>
    </div>
  );
}

function CrawlFilterEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['crawlFilter'], queryFn: getCrawlFilter });
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const value = text ?? (data?.patterns ?? []).join(', ');
  const save = async () => {
    setSaving(true);
    try {
      await setCrawlFilter(value.split(',').map((s) => s.trim()).filter(Boolean));
      qc.invalidateQueries({ queryKey: ['crawlFilter'] });
      setSaved(true); setTimeout(() => setSaved(false), 1600);
    } finally { setSaving(false); }
  };
  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> Sitemap URL filters
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="vault-add__hint">When crawling a sitemap or building anchors, skip any URL containing these (comma-separated) — so previously AI-written blogs aren&rsquo;t indexed. <strong>Clear the field to disable filtering.</strong></p>
          <Input placeholder="/blog, /article" value={value} onChange={(e) => setText(e.target.value)} />
          <div className="vault-add__actions">
            <Button variant="primary" size="sm" disabled={saving} onClick={save} iconLeft={<Icon name={saved ? 'check' : 'settings'} size={14} />}>
              {saved ? 'Saved' : saving ? 'Saving…' : 'Save filters'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const VAULT_KIND_LABEL: Record<string, string> = {
  transcript: 'Interviews', qa: 'Client Q&A', website_page: 'Website pages', sitemap: 'Sitemap', profile: 'Profile', article: 'Articles',
};

// Client-facing, read-only view of the Vault's size (source count + tokens by kind).
// The Vault itself stays admin-only; this just shows clients how much grounded
// material backs their content. Renders only for non-admins (admins see VaultPanel).
function VaultBudgetBar() {
  const isAdmin = useIsAdmin();
  const { data } = useQuery({ queryKey: ['vaultStats'], queryFn: getVaultStats, enabled: !isAdmin });
  if (isAdmin || !data || data.sourceCount === 0) return null;
  const STORAGE_SOFT = 500000;
  const pct = Math.min(100, Math.round((data.totalTokens / STORAGE_SOFT) * 100));
  const kinds = Object.entries(data.byKind).filter(([, v]) => v.count > 0);
  return (
    <Card padding={false} className="dash-leads vault">
      <div className="dash-panelhead dash-panelhead--pad">
        <div>
          <h2><Icon name="shield-check" size={17} /> Authenticity Vault</h2>
          <p>The grounded knowledge base your content is written from — the more real sources it holds, the stronger and more authentic your articles.</p>
        </div>
      </div>
      <div className="vault-budget">
        <div className="vault-budget__bar"><span style={{ width: `${pct}%` }} /></div>
        <span className="vault-budget__label">
          {data.sourceCount} source{data.sourceCount === 1 ? '' : 's'} · ~{fmtTokens(data.totalTokens)} tokens stored
        </span>
      </div>
      {kinds.length > 0 && (
        <div className="vault-kinds">
          {kinds.map(([k, v]) => <span key={k} className="vault-kind"><strong>{v.count}</strong> {VAULT_KIND_LABEL[k] || k}</span>)}
        </div>
      )}
    </Card>
  );
}

function VaultPanel() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: qk.vault, queryFn: getVault, enabled: isAdmin });
  // Once a client's site map lives in the builder, the Vault's two sitemap imports step aside.
  const { data: siteMap } = useQuery({ queryKey: SITE_MAP_KEY, queryFn: getSiteMap, enabled: isAdmin });
  const [adding, setAdding] = useState(false);
  const [method, setMethod] = useState<'paste' | 'upload' | 'gdrive' | 'crawl' | 'sitemap' | 'sitemap_paste'>('paste');
  const [kind, setKind] = useState<VaultKind>('transcript');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [openKinds, setOpenKinds] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState('');

  if (!isAdmin) return null;

  const reset = () => { setTitle(''); setUrl(''); setText(''); setErr(''); };
  const toggleKind = (k: string) => setOpenKinds((cur) => {
    const next = new Set(cur);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });
  const refresh = () => qc.invalidateQueries({ queryKey: qk.vault });

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      if (method === 'upload') {
        if (!file) { setErr('Choose a file to upload.'); return; }
        const name = file.name.toLowerCase();
        if (name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.markdown') || file.type.startsWith('text/')) {
          await addVaultSource({ method: 'paste', kind, title: title.trim() || file.name, text: await file.text() });
        } else if (name.endsWith('.pdf') || file.type === 'application/pdf') {
          await uploadVaultFile(file, kind, title.trim() || file.name);
        } else {
          setErr('Unsupported file. Upload a PDF or .txt/.md file; for Word (.docx), open it in Google Docs and use Drive import.');
          return;
        }
      } else {
        await addVaultSource({
          method, kind: method === 'paste' || method === 'gdrive' ? kind : undefined,
          title: title.trim() || undefined,
          text: (method === 'paste' || method === 'sitemap_paste') ? text : undefined,
          url: method === 'paste' ? undefined : url.trim(),
        });
      }
      reset(); setFile(null); setAdding(false); refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const onReanalyze = async (id: string) => { setBusy(true); try { await reanalyzeVaultSource(id); refresh(); } finally { setBusy(false); } };
  const onAnchors = async (id: string) => {
    setBusy(true); setErr(''); setStatus('Building anchor text from page content…');
    try {
      let offset = 0; let total = 0;
      do {
        const r = await buildVaultAnchors(id, offset);
        offset = r.nextOffset; total = r.total;
        setStatus(`Building anchor text — ${Math.min(offset, total)} / ${total} pages…`);
      } while (offset < total);
      refresh();
      setOpenId(id); // open the source so the new anchor lines are visible
      setStatus(`Anchor text built for all ${total} page${total === 1 ? '' : 's'}. Click “View” to see it.`);
      setTimeout(() => setStatus(''), 6000);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setStatus(''); }
    finally { setBusy(false); }
  };
  const runCrawl = async (id: string, overwrite: boolean) => {
    setBusy(true); setErr(''); setStatus(overwrite ? 'Refreshing…' : 'Starting…');
    try {
      let offset = 0; let total = 0; let excluded = 0; let added = 0; let refreshed = 0;
      do {
        const r = await crawlSitemapPages(id, offset, 10, overwrite);
        offset = r.nextOffset; total = r.total; excluded = r.excluded; added += r.stored; refreshed += r.updated;
        setStatus(`${overwrite ? 'Refreshed' : 'Crawled'} ${Math.min(offset, total)} / ${total} pages…`);
        refresh();
      } while (offset < total);
      const parts = [`${added} added`, overwrite ? `${refreshed} refreshed` : '', excluded ? `${excluded} skipped by URL filters` : ''].filter(Boolean);
      setStatus(`Done — ${total} page${total === 1 ? '' : 's'} (${parts.join(', ')}).`);
      setTimeout(() => setStatus(''), 8000);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setStatus(''); }
    finally { setBusy(false); }
  };
  const onCrawlPages = (id: string) => runCrawl(id, false);
  const onRefreshPages = (id: string) => runCrawl(id, true);
  const onDelete = async (id: string) => {
    if (!confirm('Remove this source from the vault? This cannot be undone.')) return;
    setBusy(true); try { await deleteVaultSource(id); if (openId === id) setOpenId(null); refresh(); } finally { setBusy(false); }
  };
  /* Redesigns and crawls that missed the ignore filter: clear the pages in one
     go, keep or drop the sitemap, then crawl again. */
  const onClearPages = async (count: number) => {
    if (!confirm(`Remove all ${count} crawled website page${count === 1 ? '' : 's'} from the vault? This cannot be undone.`)) return;
    const withSitemap = confirm(['Also remove the sitemap?', '', 'OK = remove it too (resubmit a sitemap before recrawling).', 'Cancel = keep it, so "Crawl all pages" works right away.'].join(String.fromCharCode(10)));
    setBusy(true); setErr('');
    try { const r = await deleteVaultPages(withSitemap); setStatus(`Removed ${r.pages} page${r.pages === 1 ? '' : 's'}${r.sitemaps ? ` and ${r.sitemaps} sitemap${r.sitemaps === 1 ? '' : 's'}` : ''}.`); setOpenId(null); refresh(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const sources = data?.sources ?? [];
  const totalTokens = data?.totalTokens ?? 0;
  // This is total STORAGE, not what's sent per generation. Each piece sends
  // transcripts/articles in full (gold standard) first, then caps supplemental
  // website-page content at ~30k tokens — so the vault can grow well past this.
  const STORAGE_SOFT = 500000; // ~500k tokens; just a fullness cue for the bar
  const pct = Math.min(100, Math.round((totalTokens / STORAGE_SOFT) * 100));

  const methods = (['paste', 'upload', 'gdrive', 'crawl', 'sitemap', 'sitemap_paste'] as const).filter((m) => !siteMap?.migrated || (m !== 'sitemap' && m !== 'sitemap_paste'));

  return (
    <>
    <Card padding={false} className="dash-leads vault">
      <div className="dash-panelhead dash-panelhead--pad">
        <div>
          <h2><Icon name="shield-check" size={17} /> Authenticity Vault</h2>
          <p>The grounded knowledge base for this client — every transcript, page, and sitemap that AI content is written from. Admin&nbsp;only.</p>
        </div>
        <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={15} />} onClick={() => { setAdding((v) => !v); setErr(''); }}>Add source</Button>
      </div>

      <div className="vault-budget">
        <div className="vault-budget__bar"><span style={{ width: `${pct}%` }} /></div>
        <span className="vault-budget__label">
          {sources.length} source{sources.length === 1 ? '' : 's'} · ~{fmtTokens(totalTokens)} tokens stored · each piece sends transcripts first, then ~30k tokens of page support
        </span>
      </div>
      {status && <p className="gen-note" style={{ padding: '0 20px 10px' }}><Icon name="refresh-cw" size={13} /> {status}</p>}
      <CrawlFilterEditor />

      {adding && (
        <div className="vault-add">
          <div className="vault-add__methods">
            {methods.map((m) => (
              <button key={m} type="button" className={`vault-tab ${method === m ? 'is-on' : ''}`} onClick={() => { setMethod(m); setErr(''); }}>
                {m === 'paste' ? 'Paste text' : m === 'upload' ? 'Upload file' : m === 'gdrive' ? 'Google Drive' : m === 'crawl' ? 'Crawl a page' : m === 'sitemap' ? 'Import sitemap' : 'Paste sitemap'}
              </button>
            ))}
          </div>
          <div className="vault-add__body">
            {method === 'paste' ? (
              <>
                <div className="vault-add__row">
                  <Select
                    value={kind}
                    onChange={(e) => setKind(e.target.value as VaultKind)}
                    aria-label="Source type"
                    options={PASTE_KINDS.map((k) => ({ value: k, label: VAULT_KIND_META[k].label }))}
                  />
                  <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <Textarea rows={6} placeholder="Paste the interview transcript, page copy, or article text…" value={text} onChange={(e) => setText(e.target.value)} />
              </>
            ) : method === 'upload' ? (
              <>
                <div className="vault-add__row">
                  <Select
                    value={kind}
                    onChange={(e) => setKind(e.target.value as VaultKind)}
                    aria-label="Source type"
                    options={PASTE_KINDS.map((k) => ({ value: k, label: VAULT_KIND_META[k].label }))}
                  />
                  <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <input type="file" accept=".pdf,.txt,.md,.markdown,text/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </>
            ) : method === 'gdrive' ? (
              <>
                <div className="vault-add__row">
                  <Select
                    value={kind}
                    onChange={(e) => setKind(e.target.value as VaultKind)}
                    aria-label="Source type"
                    options={PASTE_KINDS.map((k) => ({ value: k, label: VAULT_KIND_META[k].label }))}
                  />
                  <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <Input placeholder="https://docs.google.com/document/d/…" value={url} onChange={(e) => setUrl(e.target.value)} />
              </>
            ) : method === 'sitemap_paste' ? (
              <>
                <div className="vault-add__row">
                  <Input placeholder="Site URL (e.g. https://client-site.com)" value={url} onChange={(e) => setUrl(e.target.value)} />
                  <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <Textarea rows={8} placeholder={"Paste the planned sitemap rows from your Google Sheet (tab-separated).\nThe first filled column sets the nesting level; deeper columns nest the slug (/main/sub/sub-sub/). Cells to the right of the page name are captured as notes."} value={text} onChange={(e) => setText(e.target.value)} />
              </>
            ) : (
              <div className="vault-add__row">
                <Input
                  placeholder={method === 'crawl' ? 'https://client-site.com/page-to-index' : 'https://client-site.com/sitemap.xml'}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
                <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
            )}
            <p className="vault-add__hint">
              {method === 'paste' && 'Stored raw and AI-indexed (topics, projects, brands) for grounded writing.'}
              {method === 'upload' && 'Upload a PDF (read via Claude) or a .txt/.md file. For Word (.docx), open it in Google Docs and use Drive import.'}
              {method === 'gdrive' && 'Imports a Google Doc as text. The Doc must be shared with the Triton service account (see setup notes).'}
              {method === 'crawl' && 'Fetches one page, extracts the readable text, and indexes it.'}
              {method === 'sitemap' && 'Imports the page list only (no content indexed yet). Then, on the sitemap source: “Build anchor text” for AI anchors, and “Crawl all pages” to index page content into the vault.'}
              {method === 'sitemap_paste' && 'Planned sitemap for the INITIAL build — pages + slugs + notes for the web handoff. Separate from the live XML sitemap (import that later once the site is up).'}
            </p>
            {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}
            <div className="vault-add__actions">
              <Button variant="ghost" size="sm" onClick={() => { setAdding(false); reset(); }}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={busy} onClick={submit} iconLeft={busy ? undefined : <Icon name="plus" size={14} />}>
                {busy ? 'Adding…' : 'Add to vault'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="lead-none">Loading vault…</div>
      ) : sources.length === 0 ? (
        <div className="lead-none">Nothing in the vault yet. Add the onboarding transcripts, website pages, and sitemap to ground this client&rsquo;s content.</div>
      ) : (
        <div className="vault-groups">
          {VAULT_KIND_ORDER.filter((k) => sources.some((s) => s.kind === k)).map((kind) => {
            const group = sources.filter((s) => s.kind === kind);
            const open = openKinds.has(kind);
            /* 'Indexed' means a structured index was built from a long source —
               the AI pass that pulls projects and topics out of a transcript or a
               crawled page. A Q&A has nothing to pull: it IS a question and an
               answer. Flagging it reads as broken on a source that is already
               grounding content, so it is excluded like a sitemap is. */
            const notIndexed = group.filter((s) => !s.analyzed && s.kind !== 'sitemap' && s.kind !== 'qa').length;
            return (
              <div key={kind} className="vault-group">
                <div className="vault-group__row">
                  <button type="button" className="vault-group__head" onClick={() => toggleKind(kind)}>
                    <Icon name={open ? 'chevron-down' : 'chevron-right'} size={15} />
                    <span className="vault-group__label">{VAULT_KIND_META[kind].label}s</span>
                    <span className="vault-group__count">{group.length}</span>
                    {notIndexed > 0 && <Badge variant="warning">{notIndexed} not indexed</Badge>}
                  </button>
                  {kind === 'website_page' && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => onClearPages(group.length)} iconLeft={<Icon name="trash" size={13} />}>Clear all pages</Button>
                  )}
                </div>
                {open && (
                  <ul className="vault-list">
                    {group.map((s) => {
                      const isOpen = openId === s.id;
                      return (
                        <li key={s.id} className="vault-item">
                          <div className="vault-item__main">
                            <div className="vault-item__head">
                              {!s.analyzed && s.kind !== 'sitemap' && s.kind !== 'qa' && <Badge variant="warning">Not indexed</Badge>}
                              <h4>{s.title}</h4>
                            </div>
                            {s.structured && <VaultChips s={s.structured} />}
                            <div className="vault-item__foot">
                              <span>~{fmtTokens(s.tokens)} tokens</span>
                              {s.sourceUrl && <><i>·</i><a href={s.sourceUrl} target="_blank" rel="noreferrer" className="vault-link"><Icon name="external" size={12} /> source</a></>}
                              <i>·</i><span>{s.origin}</span>
                            </div>
                          </div>
                          <div className="vault-item__actions">
                            <Button variant="ghost" size="sm" onClick={() => setOpenId(isOpen ? null : s.id)}>{isOpen ? 'Hide' : 'View'}</Button>
                            {s.kind === 'sitemap' && <Button variant="outline" size="sm" disabled={busy} onClick={() => onCrawlPages(s.id)} iconLeft={<Icon name="download" size={13} />}>Crawl all pages</Button>}
                            {s.kind === 'sitemap' && <Button variant="outline" size="sm" disabled={busy} onClick={() => onRefreshPages(s.id)} iconLeft={<Icon name="refresh-cw" size={13} />}>Refresh pages</Button>}
                            {s.kind === 'sitemap' && <Button variant="outline" size="sm" disabled={busy} onClick={() => onAnchors(s.id)} iconLeft={<Icon name="sparkles" size={13} />}>Build anchor text</Button>}
                            {s.kind !== 'sitemap' && <IconBtn label="Re-index" onClick={() => onReanalyze(s.id)} disabled={busy} icon="refresh-cw" />}
                            <IconBtn label="Delete source" onClick={() => onDelete(s.id)} disabled={busy} />
                          </div>
                          {isOpen && <div className="vault-item__view"><VaultSourceView id={s.id} /></div>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
    <SitemapBuilder />
    </>
  );
}

/* ---- Content generator (admin-only) — grounded output ---------------- */

function download(filename: string, text: string, type = 'text/plain') {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function CopyBtn({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button variant="ghost" size="sm" iconLeft={<Icon name={done ? 'check' : 'quote'} size={13} />}
      onClick={async () => { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1400); }}>
      {done ? 'Copied' : label}
    </Button>
  );
}

function CoveredTopicsEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['coveredTopics'], queryFn: getCoveredTopics });
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const covered = data?.covered ?? [];

  const doImport = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await importCoveredTopics(text.trim()); setText(''); qc.invalidateQueries({ queryKey: ['coveredTopics'] }); }
    finally { setBusy(false); }
  };
  const remove = async (id: string) => { await deleteCoveredTopic(id); qc.invalidateQueries({ queryKey: ['coveredTopics'] }); };

  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
        Covered topics (avoid repeats) <Badge variant={covered.length ? 'success' : 'neutral'}>{covered.length}</Badge>
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="vault-add__hint">
            Past topics you&rsquo;ve already published, so &ldquo;Suggest from vault&rdquo; won&rsquo;t repeat them or close variants. <strong>Paste straight from your Google Sheet</strong> (with the <code>Title</code> … <code>Subheadings</code> header row) — or one per line as <code>Topic | subtopic, subtopic</code>. Generated blogs are added here automatically.
          </p>
          <Textarea rows={4} placeholder={'Paste the Google Sheet rows (Title + Subheadings columns)…\n\nor:  Tankless water heaters | sizing, cost, top brands'} value={text} onChange={(e) => setText(e.target.value)} />
          <div className="vault-add__actions">
            <Button variant="primary" size="sm" disabled={busy || !text.trim()} onClick={doImport} iconLeft={<Icon name="plus" size={14} />}>
              {busy ? 'Importing…' : 'Import topics'}
            </Button>
          </div>
          {covered.length > 0 && (
            <ul className="covered-list">
              {covered.map((c) => (
                <li key={c.id} className="covered-item">
                  <div className="covered-item__body">
                    <strong>{c.topic}</strong>
                    {c.subtopics && <span className="covered-item__subs"> — {c.subtopics}</span>}
                    {c.source === 'generated' && <Badge variant="accent">generated</Badge>}
                  </div>
                  <IconBtn label="Remove covered topic" onClick={() => remove(c.id)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

type QueueUse = (t: string, q: string[], sourceIds: string[], notes?: string, support?: BlogSupport | null) => void;

const agoLabel = (iso: string) => {
  const d = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`;
};
/* One line that answers "what happened to this?" — the question a sender
   could not answer before, because a sent topic vanished into the board. */
function QueueStatusLine({ item }: { item: Pick<BlogQueueItem, 'status' | 'waitingOn' | 'decidedAt' | 'createdAt' | 'autoWrite'> }) {
  if (item.status === 'pending') {
    return <span className="bq-status"><Icon name="clock" size={12} /> Waiting on {item.waitingOn ? <strong>{item.waitingOn.name}</strong> : 'the SEO team'}{item.waitingOn ? ` (${item.waitingOn.role})` : ''} · sent {agoLabel(item.createdAt)}</span>;
  }
  if (item.status === 'approved') {
    const aw = item.autoWrite;
    if (aw?.status === 'done' && aw.articleId) return <span className="bq-status bq-status--ok"><Icon name="check" size={12} /> Approved · written → <a href={`/content?article=${encodeURIComponent(aw.articleId)}`}>review the draft</a></span>;
    if (aw?.status === 'queued' || aw?.status === 'writing') return <span className="bq-status"><Icon name="clock" size={12} /> Approved · being written automatically</span>;
    if (aw?.status === 'failed') return <span className="bq-status" style={{ color: 'var(--danger)' }}><Icon name="alert-circle" size={12} /> Couldn’t write it automatically{aw.error ? `: ${aw.error.slice(0, 140)}` : ''} — write it by hand</span>;
    return <span className="bq-status bq-status--ok"><Icon name="check" size={12} /> Approved {item.decidedAt ? agoLabel(item.decidedAt) : ''} — ready to write</span>;
  }
  return <span className="bq-status"><Icon name="x" size={12} /> Rejected {item.decidedAt ? agoLabel(item.decidedAt) : ''}</span>;
}

function BlogQueueRow({ item, onChanged, onUse, onGenerate }: { item: BlogQueueItem; onChanged: () => void; onUse: QueueUse; onGenerate: QueueUse }) {
  const support: BlogSupport | null = item.supportUrl ? { url: item.supportUrl, anchor: item.supportAnchor, keywords: item.keywords } : null;
  const [topic, setTopic] = useState(item.topic);
  const [qs, setQs] = useState<string[]>(item.questions.length ? item.questions : ['']);
  const [guidance, setGuidance] = useState('');
  const [busy, setBusy] = useState(false);
  const dirty = topic !== item.topic || JSON.stringify(qs.filter(Boolean)) !== JSON.stringify(item.questions);
  const run = async (fn: () => Promise<void>) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const save = () => run(async () => { await updateBlogTopic(item.id, { topic, questions: qs.filter(Boolean) }); onChanged(); });
  const setStatus = (status: BlogQueueItem['status']) => run(async () => { await updateBlogTopic(item.id, { topic, questions: qs.filter(Boolean), status }); onChanged(); });
  const del = () => run(async () => { await deleteBlogTopic(item.id); onChanged(); });
  const regenTopic = () => run(async () => { const r = await regenerateBlogPiece({ topic, questions: qs.filter(Boolean), target: 'topic', guidance }); if (r.topic) setTopic(r.topic); if (r.questions?.length) setQs(r.questions); });
  const regenQs = () => run(async () => { const r = await regenerateBlogPiece({ topic, questions: qs.filter(Boolean), target: 'questions', guidance }); if (r.questions?.length) setQs(r.questions); });
  const regenQ = (i: number) => run(async () => { const r = await regenerateBlogPiece({ topic, questions: qs.filter(Boolean), target: 'question', index: i, guidance }); if (r.question) setQs(qs.map((q, j) => (j === i ? r.question! : q))); });
  const stVar = item.status === 'approved' ? 'success' : item.status === 'rejected' ? 'neutral' : 'warning';

  return (
    <div className={`bq-item ${item.status === 'rejected' ? 'bq-item--muted' : ''}`}>
      <div className="bq-item__head">
        <Badge variant={stVar}>{item.status}</Badge>
        {item.origin === 'interview' && item.sourceIds.length > 0 && <Badge variant="accent">from interview</Badge>}
        {item.supportUrl && <Badge variant="accent">supports {item.supportAnchor || item.supportUrl}</Badge>}
        <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Topic" />
        <IconBtn label="Regenerate topic" icon="sparkles" onClick={regenTopic} disabled={busy} />
      </div>
      <QueueStatusLine item={item} />
      <div className="bq-qs">
        {qs.map((q, i) => (
          <div key={i} className="bq-q">
            <Input value={q} onChange={(e) => setQs(qs.map((v, j) => (j === i ? e.target.value : v)))} placeholder={`Question ${i + 1}`} />
            <IconBtn label="Regenerate question" icon="refresh-cw" onClick={() => regenQ(i)} disabled={busy} />
            <IconBtn label="Remove question" onClick={() => setQs(qs.filter((_, j) => j !== i))} disabled={busy} />
          </div>
        ))}
        <button type="button" className="bq-add" onClick={() => setQs([...qs, ''])}>+ question</button>
      </div>
      {item.keywords && <p className="dash-muted" style={{ margin: '2px 0 0', fontSize: 'var(--text-xs)' }}>Target keywords: {item.keywords}</p>}
      <Input placeholder="Guidance for regenerate (optional)…" value={guidance} onChange={(e) => setGuidance(e.target.value)} />
      <div className="bq-actions">
        <Button size="sm" variant="outline" disabled={busy} onClick={regenQs} iconLeft={<Icon name="sparkles" size={13} />}>Regen questions</Button>
        {dirty && <Button size="sm" variant="ghost" disabled={busy} onClick={save}>Save</Button>}
        {item.status !== 'approved' && <Button size="sm" variant="primary" disabled={busy} onClick={() => setStatus('approved')}>Approve</Button>}
        {item.status !== 'rejected' && <Button size="sm" variant="ghost" disabled={busy} onClick={() => setStatus('rejected')}>Reject</Button>}
        <Button size="sm" variant="primary" disabled={busy} iconLeft={<Icon name="sparkles" size={13} />} onClick={() => onGenerate(topic, qs.filter(Boolean), item.sourceIds, item.notes, support)}>Write now</Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => onUse(topic, qs.filter(Boolean), item.sourceIds, item.notes, support)}>Use</Button>
        <IconBtn label="Delete topic" onClick={del} disabled={busy} />
      </div>
    </div>
  );
}

function BlogQueue({ onUse, onGenerate }: { onUse: QueueUse; onGenerate: QueueUse }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['blogQueue'], queryFn: getBlogQueue });
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState<'approved' | 'pending' | 'rejected'>('approved');
  const items = data?.items ?? [];
  const count = (st: BlogQueueItem['status']) => items.filter((i) => i.status === st).length;
  const pending = count('pending');
  const shown = items.filter((i) => i.status === show);
  const refresh = () => qc.invalidateQueries({ queryKey: ['blogQueue'] });
  /* Approved topics are the writer's work list, so they lead; within a
     status, topics for the same page sit together in request order. */
  const grouped = useMemo(() => {
    const byBatch = new Map<string, BlogQueueItem[]>();
    for (const it of shown) { const k = it.batchId || `single:${it.id}`; const arr = byBatch.get(k); if (arr) arr.push(it); else byBatch.set(k, [it]); }
    return [...byBatch.values()];
  }, [shown]);
  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> Topic queue
        <Badge variant={count('approved') ? 'success' : 'neutral'}>{count('approved')} approved</Badge>
        <Badge variant={pending ? 'warning' : 'neutral'}>{pending} pending</Badge>
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(['approved', 'pending', 'rejected'] as const).map((st) => (
              <Button key={st} size="sm" variant={show === st ? 'primary' : 'outline'} onClick={() => setShow(st)}>{st[0].toUpperCase() + st.slice(1)} ({count(st)})</Button>
            ))}
          </div>
          {shown.length === 0
            ? <p className="dash-muted">{show === 'approved' ? 'Nothing approved yet. Approved topics land here, ready to write.' : show === 'pending' ? 'Nothing awaiting approval.' : 'Nothing rejected.'}</p>
            : grouped.map((group) => (
              <div key={group[0].batchId || group[0].id} className="bq-group">
                {group[0].supportUrl && (
                  <p className="bq-group__head">
                    <Icon name="link" size={13} /> Supports <strong>{group[0].supportAnchor || group[0].supportUrl}</strong>
                    {group[0].requestedBy ? ` · requested by ${group[0].requestedBy}` : ''} · {group[0].createdAt.slice(0, 10)}
                  </p>
                )}
                <div className="bq-list">{group.map((it) => <BlogQueueRow key={it.id} item={it} onChanged={refresh} onUse={onUse} onGenerate={onGenerate} />)}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// Mine deep-dive blog topics from specific project-story interviews — surfacing
// sub-topics the interviewee mentioned but that were never written up in depth.
// Selected candidates go to the topic queue tagged with their source interviews,
// so the write step is weighted toward that real job.
function InterviewTopicMiner({ onUse }: { onUse: (t: string, q: string[], sourceIds: string[], gaps: string[]) => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['blogSources'], queryFn: getBlogSources });
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState<InterviewTopicCandidate[] | null>(null);
  const [note, setNote] = useState('');
  const [sentIdx, setSentIdx] = useState<Set<number>>(new Set());
  const items = data?.items ?? [];
  const ids = [...sel];
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const suggest = async () => {
    if (!ids.length) return;
    setBusy(true); setNote(''); setCandidates(null); setSentIdx(new Set());
    try {
      const r = await suggestTopicsFromInterviews(ids);
      setCandidates(r.candidates);
      if (r.note) setNote(r.note);
    } catch (e) { setNote(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const sendForApproval = async (c: InterviewTopicCandidate, i: number) => {
    await saveBlogTopic({ topic: c.title, questions: c.questions, sourceIds: ids, origin: 'interview' });
    qc.invalidateQueries({ queryKey: ['blogQueue'] });
    setSentIdx((s) => new Set(s).add(i));
  };

  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> Mine topics from interviews
        <Badge variant="neutral">{items.length}</Badge>
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="dash-muted">Pick one or more project-story interviews. The AI surfaces specific sub-topics they mentioned but didn&rsquo;t cover in depth (e.g. &ldquo;installing cedar siding&rdquo;) — each can become its own post, grounded in that job.</p>
          {items.length === 0
            ? <p className="dash-muted">No interviews with a transcript yet. Run an interview or attach a transcript first.</p>
            : (
              <div className="miner-list">
                {items.map((s) => (
                  <label key={s.id} className="miner-src">
                    <input type="checkbox" checked={sel.has(s.id)} onChange={() => toggle(s.id)} />
                    <span><strong>{s.title}</strong>{s.city ? ` · ${s.city}` : ''}</span>
                    {s.kind === 'vault' && <Badge variant="neutral">Vault</Badge>}
                  </label>
                ))}
              </div>
            )}
          <div className="vault-add__actions">
            <Button variant="primary" size="sm" disabled={busy || !ids.length} iconLeft={<Icon name="sparkles" size={13} />} onClick={suggest}>
              {busy ? 'Reading interviews…' : `Suggest topics${ids.length ? ` from ${ids.length}` : ''}`}
            </Button>
          </div>
          {note && <p className="dash-muted" style={{ color: 'var(--amber-700)' }}>{note}</p>}
          {candidates && candidates.length > 0 && (
            <div className="bq-list" style={{ marginTop: 10 }}>
              {candidates.map((c, i) => (
                <div key={i} className="bq-item">
                  <div className="bq-item__head"><strong style={{ flex: 1 }}>{c.title}</strong></div>
                  {c.angle && <p className="dash-muted" style={{ margin: '2px 0 6px' }}>{c.angle}</p>}
                  {c.questions.length > 0 && <ul className="miner-qs">{c.questions.map((q, j) => <li key={j}>{q}</li>)}</ul>}
                  <GapList gaps={c.gaps} />
                  <div className="bq-actions">
                    <Button size="sm" variant="primary" disabled={sentIdx.has(i)} onClick={() => sendForApproval(c, i)}>
                      {sentIdx.has(i) ? 'Sent for approval ✓' : 'Send for approval'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onUse(c.title, c.questions, ids, c.gaps)}>Use in composer</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Vault gaps for a suggestion: questions the Vault can't answer that would make
// the article more unique/credible — candidates to send the client to fill in.
function GapList({ gaps }: { gaps: string[] }) {
  if (!gaps || gaps.length === 0) return null;
  return (
    <div className="gap-list">
      <div className="gap-list__head"><Icon name="info" size={12} /> Vault gaps — answering these would strengthen the article</div>
      <ul>{gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
    </div>
  );
}

// Interactive gaps in the composer: select which to ask, then send to the client.
// Creates a draft + emails them a portal link (Phase 2). Admin composer only.
/* `route` set = the questions are for a site page: they go through the page
   worksheet's sender and the answers land as FAQs ON that page, which is what
   "fix the gaps before writing the page" needs. Unset = a blog's gap set. */
function GapSender({ topic, questions, gaps, sourceIds, route, onSent }: { topic: string; questions: string[]; gaps: string[]; sourceIds: string[]; route?: string; onSent?: (count: number) => void }) {
  const [sel, setSel] = useState<Set<number>>(() => new Set(gaps.map((_, i) => i)));
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  /* Who we are asking, captured here because this is the moment it is known.
     Afterwards the answers are in the Vault and somebody has to remember. */
  const [expertId, setExpertId] = useState('');
  useEffect(() => { setSel(new Set(gaps.map((_, i) => i))); setSent(false); setErr(''); }, [gaps]);
  if (!gaps.length) return null;
  const toggle = (i: number) => setSel((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const selected = gaps.filter((_, i) => sel.has(i));
  const send = async () => {
    if (!topic.trim() || !selected.length) { setErr('Add a topic and select at least one question.'); return; }
    setBusy(true); setErr('');
    try {
      if (route) await askFaqPage(route, selected, expertId || undefined);
      else await sendGapsToClient({ topic, questions: questions.filter(Boolean), gaps: selected, sourceIds, expertId: expertId || undefined });
      setSent(true); onSent?.(selected.length);
    }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="gap-list">
      <div className="gap-list__head"><Icon name="info" size={12} /> Vault gaps — pick what to ask the client, then send</div>
      <ul className="gap-select">
        {gaps.map((g, i) => (
          <li key={i}><label><input type="checkbox" checked={sel.has(i)} onChange={() => toggle(i)} /> <span>{g}</span></label></li>
        ))}
      </ul>
      <div className="gap-send__foot">
        <ExpertPicker value={expertId} onChange={setExpertId} disabled={busy || sent} />
        {err && <span className="err">{err}</span>}
        <Button size="sm" variant="primary" disabled={busy || sent || !topic.trim() || !selected.length} onClick={send}>
          {sent ? 'Sent to client ✓' : busy ? 'Sending…' : `Send ${selected.length} question${selected.length === 1 ? '' : 's'} to client`}
        </Button>
      </div>
    </div>
  );
}

// "Questions for you" — the client (or an admin) answers the gaps Triton sent.
// Shows for both; clients answer, admins can enter answers from a call/email.
/* A set left unanswered beyond this is overdue: it is the point at which the
   client has had a reminder and still not replied, so it stops being something
   they will get to and starts being something that needs a nudge. */
const OVERDUE_DAYS = 7;
const daysSince = (iso: string) => (Date.now() - Date.parse(iso)) / 86_400_000;

function GapAnswers() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['gapArticles'], queryFn: getGapArticles });
  const items = data?.items ?? [];
  if (items.length === 0) return null;

  const onSaved = () => qc.invalidateQueries({ queryKey: ['gapArticles'] });
  const faqs = items.filter((i) => i.kind === 'faq');
  const blogs = items.filter((i) => i.kind !== 'faq');
  const overdue = items.filter((i) => daysSince(i.sentAt) > OVERDUE_DAYS).length;

  return (
    <Card padding={false} className="dash-leads">
      <div className="dash-panelhead dash-panelhead--pad">
        <div>
          <h2><Icon name="quote" size={16} /> Questions for you</h2>
          {/* The case, not the instruction: these answers are the reason their
              content sounds like them and not like everyone else's. */}
          <p>
            Your answers are what make your content sound like you — they go straight into
            everything we write, and the best of them onto your own pages.
            {overdue > 0 && <> <strong>{overdue} {overdue === 1 ? 'set has' : 'sets have'} been waiting over a week.</strong></>}
          </p>
        </div>
      </div>
      <div className="gap-groups">
        <GapGroup title="Questions about your pages" subtitle="Become FAQs on your site" items={faqs} onSaved={onSaved} />
        <GapGroup title="Questions for upcoming articles" subtitle="Shape what we write next" items={blogs} onSaved={onSaved} />
      </div>
    </Card>
  );
}

/* Collapsed by default — four to ten questions is a lot to scroll past when you
   came to do something else — but a set nobody has answered in a week opens
   itself, so the thing that needs doing is the thing you see. */
function GapGroup({ title, subtitle, items, onSaved }: {
  title: string; subtitle: string; items: GapArticle[]; onSaved: () => void;
}) {
  const waiting = items.filter((i) => daysSince(i.sentAt) > OVERDUE_DAYS).length;
  const [open, setOpen] = useState(waiting > 0);
  if (!items.length) return null;
  const unanswered = items.reduce((n, i) => n + (i.total - i.answered - i.declined), 0);

  return (
    <section className={`gap-group ${open ? 'is-open' : ''}`}>
      <button type="button" className="gap-group__head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={16} />
        <span className="gap-group__title">{title}<em>{subtitle}</em></span>
        <span className="gap-group__counts">
          {unanswered > 0 && <Badge variant="accent">{unanswered} to answer</Badge>}
          {waiting > 0 && <Badge variant="warning" dot>{waiting} over a week</Badge>}
        </span>
      </button>
      {open && (
        <div className="gap-answers">
          {items.map((it) => <GapAnswerCard key={it.articleId} item={it} onSaved={onSaved} />)}
        </div>
      )}
    </section>
  );
}

/* One question, with its own save state and its own mic.

   Split per question because the previous card inferred "Saved" by comparing the
   box against the server's stored answer — and the server TRIMS what it stores,
   so any trailing space or newline made the two differ for ever. The button
   worked; the label just never appeared, however many times it was pressed.
   Saved-ness is recorded when the save returns now, not guessed at. */
function GapAnswerRow({ gap, onSaved }: { gap: GapArticle['gaps'][number]; onSaved: () => void }) {
  const [text, setText] = useState(gap.answer);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(gap.status === 'answered');
  const [err, setErr] = useState('');
  const [asking, setAsking] = useState(false); // decline reason picker open
  const dictation = useDictation((next) => { setText(next); setSaved(false); });

  const declined = gap.status === 'declined';
  const dirty = text.trim() !== (gap.answer || '').trim();

  const save = async () => {
    if (saving) return;
    setSaving(true); setErr('');
    try {
      // Trimmed on the way out so what we hold matches what the server stores.
      await answerGap(gap.id, text.trim());
      setSaved(true);
      onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  const decline = async (reason: string) => {
    setSaving(true); setErr('');
    try { await declineGap(gap.id, reason); setAsking(false); onSaved(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  const reopen = async () => {
    setSaving(true); setErr('');
    try { await reopenGap(gap.id); onSaved(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  /* A declined question collapses to a line — it is settled, and the box would
     only invite answering something already skipped. Reopening is one click, so
     a mis-tap is not permanent. */
  if (declined) {
    return (
      <div className="gap-answer__q gap-answer__q--declined">
        <label>{gap.question}</label>
        <div className="gap-answer__save">
          <span className="dash-muted">Skipped{gap.declinedReason ? ` — ${gap.declinedReason}` : ''}</span>
          <Button size="sm" variant="ghost" disabled={saving} onClick={reopen}>Answer it after all</Button>
        </div>
        {err && <p className="vault-err" style={{ margin: '4px 0 0' }}><Icon name="alert-circle" size={13} /> {err}</p>}
      </div>
    );
  }

  return (
    <div className="gap-answer__q">
      <label>{gap.question}</label>
      <Textarea rows={2} value={text} placeholder={dictation.listening ? 'Listening…' : dictation.pending ? 'Transcribing…' : 'Your answer…'}
        disabled={dictation.pending}
        onChange={(e) => { setText(e.target.value); setSaved(false); }}
        /* Answers were lost when the last save completed the set and it left the
           page while other boxes still held unsaved text. Leaving a box saves it. */
        onBlur={() => { if (dirty && text.trim()) void save(); }} />
      {dictation.micError && <p className="vault-err" style={{ margin: '4px 0 0' }}><Icon name="alert-circle" size={13} /> {dictation.micError}</p>}
      {err && <p className="vault-err" style={{ margin: '4px 0 0' }}><Icon name="alert-circle" size={13} /> {err}</p>}
      {asking ? (
        <div className="gap-answer__decline">
          <span className="dash-muted">Skip this one because…</span>
          {GAP_DECLINE_REASONS.map((r) => (
            <Button key={r} size="sm" variant="outline" disabled={saving} onClick={() => decline(r)}>{r}</Button>
          ))}
          <Button size="sm" variant="ghost" disabled={saving} onClick={() => setAsking(false)}>Cancel</Button>
        </div>
      ) : (
        <div className="gap-answer__save">
          {saved && !dirty && <span className="dash-muted">Saved</span>}
          {dirty && !!text.trim() && <span className="gap-answer__unsaved">Unsaved</span>}
          {/* The reason is asked for because it tells Triton something: "not
              relevant" means the question was wrong, "don't know" means ask
              somebody else. */}
          <Button size="sm" variant="ghost" disabled={saving} onClick={() => setAsking(true)}>Skip</Button>
          <MicButton listening={dictation.listening} level={dictation.level}
            onToggle={() => (dictation.listening ? dictation.stop() : dictation.start(text))} />
          <Button size="sm" variant="outline" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      )}
    </div>
  );
}

function GapAnswerCard({ item, onSaved }: { item: GapArticle; onSaved: () => void }) {
  const done = item.answered >= item.total && item.total > 0;
  return (
    <div className="gap-answer">
      <div className="gap-answer__head">
        <strong>{item.title}</strong>
        <Badge variant={done ? 'success' : 'warning'} dot>
          {item.answered}/{item.total} answered{item.declined ? ` · ${item.declined} skipped` : ''}
        </Badge>
      </div>
      {/* Keyed by gap id, so a refetch cannot reset a box being typed in. */}
      {item.gaps.map((g) => <GapAnswerRow key={g.id} gap={g} onSaved={onSaved} />)}
    </div>
  );
}

/* "Close to …" — the near-duplicate warning. One component so the generator,
   the page suggester and the brief form all say it the same way. Advisory:
   it names the title and where it lives, and leaves the decision to the
   person. Nothing is disabled by it. */
function NearBanner({ near, compact }: { near?: NearMatch[]; compact?: boolean }) {
  if (!near?.length) return null;
  const whereLabel = (w: NearMatch['where']) => w === 'covered' ? 'a covered topic' : w === 'queue' ? 'a topic in the queue' : w === 'brief' ? 'a topic idea brief' : 'an article';
  const m = near[0];
  return (
    <p className={`near-banner${compact ? ' near-banner--compact' : ''}`}>
      <Icon name="alert-circle" size={13} />
      <span>
        Close to {whereLabel(m.where)}: <strong>{m.url ? <a href={m.url} target="_blank" rel="noreferrer">{m.title}</a> : m.title}</strong>
        {m.detail ? ` (${m.detail})` : ''}
        {near.length > 1 ? ` · and ${near.length - 1} more` : ''}
      </span>
    </p>
  );
}

/* Result of a Vault coverage check.

   The outline is the point of this card, not the score. A verdict on its own
   told you the vault was thin and left you there; the headings say which
   sections can be written now and which nobody has answered yet, which is the
   difference between a diagnosis and a next move.

   One tick-set feeds both buttons on purpose. Whatever is ticked is the
   article's outline, whether it loads into the composer or goes to the
   Optimization Manager for approval — so the brief somebody approves is the
   brief that gets written. Supported headings start ticked; unsupported ones
   do not, so including a section the vault cannot back is a deliberate act. */
function CoverageCard({ coverage, onUseQuestions, onSend, sending, sent, sentRow, showSend }: {
  coverage: TopicCoverage;
  onUseQuestions: (qs: string[]) => void;
  onSend: (qs: string[]) => void;
  sending: boolean;
  sent: boolean;
  /* The queue row this card created, once the refetch has it. The card then
     shows the row's real status rather than a "Sent" that never changes. */
  sentRow?: BlogQueueItem | null;
  /* false = a layout places its own Send below the outline (v2). */
  showSend?: boolean;
}) {
  const meta = coverage.verdict === 'strong'
    ? { variant: 'success' as const, label: 'Strong coverage' }
    : coverage.verdict === 'partial'
      ? { variant: 'warning' as const, label: 'Partial coverage' }
      : { variant: 'neutral' as const, label: 'Thin coverage' };
  const subs = coverage.subtopics ?? [];
  // Re-ticks when a fresh check arrives; keyed on the headings themselves.
  const [picked, setPicked] = useState<boolean[]>(() => subs.map((x) => x.supported));
  const key = subs.map((x) => x.heading).join('|');
  const lastKey = useRef(key);
  if (lastKey.current !== key) { lastKey.current = key; setPicked(subs.map((x) => x.supported)); }
  const chosen = subs.filter((_, i) => picked[i]).map((x) => x.heading);
  const openCount = subs.filter((x) => !x.supported).length;

  return (
    <div className="cov-card">
      <NearBanner near={coverage.near} />
      <div className="cov-card__head">
        <Badge variant={meta.variant} dot>{meta.label}</Badge>
        <span className="cov-card__score">{coverage.score}/100</span>
      </div>
      {coverage.note && <p className="dash-muted">{coverage.note}</p>}
      {subs.length > 0 && (
        <div className="cov-card__sec">
          <strong>Sub-topics for this post</strong>
          <div className="cov-subs">
            {subs.map((sub, i) => (
              <label key={i} className={`cov-sub${sub.supported ? '' : ' cov-sub--open'}`}>
                <input type="checkbox" checked={!!picked[i]}
                  onChange={(e) => setPicked((v) => v.map((b, j) => (j === i ? e.target.checked : b)))}
                  aria-label={`Include "${sub.heading}"`} />
                <span className="cov-sub__body">
                  <span className="cov-sub__head">
                    {sub.heading}
                    <Badge variant={sub.supported ? 'success' : 'warning'}>{sub.supported ? 'in the vault' : 'nobody has answered this'}</Badge>
                  </span>
                  {sub.why && <span className="cov-sub__why">{sub.why}</span>}
                </span>
              </label>
            ))}
          </div>
          <div className="bq-actions" style={{ marginTop: 8 }}>
            <Button size="sm" variant="outline" disabled={!chosen.length} onClick={() => onUseQuestions(chosen)}>
              Use {chosen.length} as the outline
            </Button>
            {showSend !== false && <Button size="sm" variant="primary" disabled={sending || !chosen.length} iconLeft={<Icon name="send" size={13} />} onClick={() => onSend(chosen)}>
              {sending ? 'Sending…' : 'Send for approval'}
            </Button>}
            {showSend !== false && (sent
              ? <span className="dash-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-xs)' }}>
                  {sentRow
                    ? <QueueStatusLine item={sentRow} />
                    : <><Badge variant="success" dot>Sent</Badge> With the Optimization Manager — they can edit the sub-topics before approving.</>}
                </span>
              : <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>
                  Goes to the Optimization Manager as topic + sub-topics, theirs to edit or approve.
                </span>)}
          </div>
          {openCount > 0 && (
            <p className="dash-muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
              <Icon name="info" size={12} /> {openCount} sub-topic{openCount === 1 ? ' is' : 's are'} not in the vault yet. Send the questions below to the client and they become writable.
            </p>
          )}
        </div>
      )}
      {coverage.supporting.length > 0 && (
        <div className="cov-card__sec">
          <strong>Supporting sources</strong>
          <ul>{coverage.supporting.map((s, i) => <li key={i}><strong>{s.title}</strong>{s.why ? ` — ${s.why}` : ''}</li>)}</ul>
        </div>
      )}
      {coverage.gaps.length > 0 && (
        <div className="cov-card__sec">
          <strong>{coverage.verdict === 'thin' ? 'Why it\u2019s thin' : 'Gaps to fill'}</strong>
          <ul>{coverage.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
          <p className="dash-muted" style={{ margin: '6px 0 0', fontSize: 12 }}>
            <Icon name="info" size={12} /> These are ready to send to the client below — answers come back into the Vault and the topic becomes writable.
          </p>
        </div>
      )}
    </div>
  );
}

// Vet an existing AI/SEO topic suggestion (content_topics) against the Vault —
// pick one and it loads into the composer and runs the coverage check.
function AiIdeaVetter({ onPick }: { onPick: (title: string) => void }) {
  const { data } = useQuery({ queryKey: ['allTopics'], queryFn: getAllTopics });
  const [open, setOpen] = useState(false);
  const ideas = (data ?? []).filter((t) => t.status !== 'used' && t.status !== 'dismissed');
  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> Vet an AI suggestion
        <Badge variant="neutral">{ideas.length}</Badge>
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="dash-muted">
            Pick a topic from <strong>Content topic ideas</strong> above &mdash; the ones drafted by its <strong>Suggest (AI)</strong> button, or added by hand &mdash;
            and check whether the Vault can back it into a real, grounded article. Topics already used or dismissed are not listed.
          </p>
          {ideas.length === 0
            ? <p className="dash-muted">
                Nothing to vet. These come from the <strong>Content topic ideas</strong> panel on this page &mdash; press <strong>Suggest&nbsp;(AI)</strong> there, or add one by hand.
                Suggestions from &ldquo;Suggest from the Vault&rdquo;, the interview miner and the page miner are not saved as topics, so they do not appear here.
              </p>
            : (
              <div className="miner-list">
                {ideas.map((t) => (
                  <div key={t.id} className="miner-src" style={{ cursor: 'default' }}>
                    <span><strong>{t.title}</strong>{t.city ? ` · ${t.city}` : ''}{t.keywords ? <em className="dash-muted"> · {t.keywords}</em> : ''}</span>
                    <Button size="sm" variant="outline" onClick={() => onPick(t.title)}>Check the Vault</Button>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

// Build a topic cluster to support a chosen sitemap (pillar) page. The SEO
// focus pages from the client profile lead, in rank order, each with its
// keywords; every other sitemap page is in the dropdown underneath. Suggestions
// are edited and ticked here, then sent as ONE batch for the SEO team's
// approval — every topic remembers the page it was designed to support.
type DraftTopic = { title: string; questions: string[]; keywords: string; gaps: string[]; angle: string; picked: boolean; nearTo?: NearMatch[] };

function PageTopicSuggester({ onUse }: { onUse: (t: string, q: string[], sourceIds: string[], gaps: string[]) => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['sitemapPages'], queryFn: getSitemapPages });
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState<DraftTopic[] | null>(null);
  const [page, setPage] = useState<{ url: string; anchor: string; keywords: string; crawled: boolean } | null>(null);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState<{ count: number; anchor: string } | null>(null);
  const [sending, setSending] = useState(false);
  const pages = data?.pages ?? [];
  const focus = data?.focus ?? [];
  const pagePath = (u: string) => { try { const x = new URL(u); return x.pathname === '/' ? x.host : x.pathname; } catch { return u; } };
  const isFocus = (u: string) => focus.some((f) => f.url.replace(/\/+$/, '').toLowerCase() === u.replace(/\/+$/, '').toLowerCase());
  const others = pages.filter((pp) => !isFocus(pp.url));

  const suggest = async (target: string) => {
    if (!target) return;
    setUrl(target); setBusy(true); setNote(''); setDrafts(null); setSent(null); setPage(null);
    try {
      const r = await suggestTopicsForPage(target);
      setDrafts(r.candidates.map((c) => ({ title: c.title, questions: c.questions, keywords: (c.keywords ?? []).join(', '), gaps: c.gaps, angle: c.angle, picked: true, nearTo: c.nearTo })));
      setPage(r.page ?? null);
      if (r.note) setNote(r.note);
    } catch (e) { setNote(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const upd = (i: number, patch: Partial<DraftTopic>) => setDrafts((d) => (d ? d.map((t, j) => (j === i ? { ...t, ...patch } : t)) : d));
  const picked = (drafts ?? []).filter((d) => d.picked && d.title.trim());
  const send = async () => {
    if (!page || !picked.length) return;
    setSending(true); setNote('');
    try {
      await sendTopicBatch({ url: page.url, anchor: page.anchor, topics: picked.map((d) => ({ topic: d.title.trim(), questions: d.questions.filter((q) => q.trim()), keywords: d.keywords.split(',').map((k) => k.trim()).filter(Boolean) })) });
      qc.invalidateQueries({ queryKey: ['blogQueue'] });
      setSent({ count: picked.length, anchor: page.anchor || page.url });
      setDrafts(null);
    } catch (e) { setNote(e instanceof Error ? e.message : String(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> Support a sitemap page
        <Badge variant="neutral">{focus.length ? `${focus.length} focus · ` : ''}{pages.length} pages</Badge>
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="dash-muted">Pick the page to strengthen. The AI reads the page from the Vault and its target keywords, then proposes topical-authority articles that support it — none repeating a past or queued topic. Edit, untick what&rsquo;s weak, and send the batch to the SEO team for approval.</p>
          {focus.length > 0 && (
            <div className="focus-pages">
              {focus.map((f, i) => (
                <div key={f.url} className={`focus-page${url === f.url ? ' focus-page--active' : ''}`}>
                  <span className="focus-page__rank">#{i + 1}</span>
                  <div className="focus-page__meta">
                    <span className="focus-page__name">{f.anchor || pagePath(f.url)}</span>
                    <span className="focus-page__kw">{f.keywords || <em>no keywords set</em>}{!f.inSitemap && ' · not in sitemap'}</span>
                  </div>
                  <Button variant={url === f.url ? 'primary' : 'outline'} size="sm" disabled={busy} iconLeft={<Icon name="sparkles" size={13} />} onClick={() => suggest(f.url)}>
                    {busy && url === f.url ? 'Reading…' : 'Suggest articles'}
                  </Button>
                </div>
              ))}
            </div>
          )}
          {pages.length === 0 && focus.length === 0
            ? <p className="dash-muted">No sitemap pages yet — add the client&rsquo;s sitemap to the Vault, or set SEO focus pages on the client profile.</p>
            : others.length > 0 && (
              <div className="page-suggest__row">
                <select className="tr-select" value={isFocus(url) ? '' : url} onChange={(e) => setUrl(e.target.value)} aria-label="Other page">
                  <option value="">{focus.length ? 'Any other page…' : 'Choose a page…'}</option>
                  {others.map((pp) => <option key={pp.url} value={pp.url}>{pp.anchor ? `${pp.anchor} · ${pagePath(pp.url)}` : pp.url}</option>)}
                </select>
                <Button variant="primary" size="sm" disabled={busy || !url || isFocus(url)} iconLeft={<Icon name="sparkles" size={13} />} onClick={() => suggest(url)}>
                  {busy && !isFocus(url) ? 'Reading…' : 'Suggest articles'}
                </Button>
              </div>
            )}
          {note && <p className="dash-muted" style={{ color: 'var(--amber-700)' }}>{note}</p>}
          {sent && (
            <p className="dash-muted" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Badge variant="success" dot>Sent</Badge> {sent.count} topic{sent.count === 1 ? '' : 's'} supporting <strong>{sent.anchor}</strong> are with the SEO team for approval. They appear under Topic queue once approved.
            </p>
          )}
          {drafts && drafts.length > 0 && page && (
            <div className="bq-list" style={{ marginTop: 10 }}>
              <p className="dash-muted" style={{ margin: 0 }}>
                Designed to support <strong>{page.anchor || pagePath(page.url)}</strong>{page.keywords ? <> · targeting <em>{page.keywords}</em></> : null}{page.crawled ? '' : ' · page not crawled yet'}
              </p>
              {drafts.map((d, i) => (
                <div key={i} className={`bq-item${d.picked ? '' : ' bq-item--muted'}`}>
                  <div className="bq-item__head">
                    <input type="checkbox" checked={d.picked} onChange={(e) => upd(i, { picked: e.target.checked })} aria-label="Include in the batch" />
                    <Input value={d.title} onChange={(e) => upd(i, { title: e.target.value })} placeholder="Topic" />
                  </div>
                  {d.angle && <p className="dash-muted" style={{ margin: '2px 0 6px' }}>{d.angle}</p>}
                  <NearBanner near={d.nearTo} compact />
                  <div className="bq-qs">
                    {d.questions.map((q, j) => (
                      <div key={j} className="bq-q">
                        <Input value={q} onChange={(e) => upd(i, { questions: d.questions.map((v, k) => (k === j ? e.target.value : v)) })} placeholder={`Sub-topic ${j + 1}`} />
                        <IconBtn label="Remove sub-topic" onClick={() => upd(i, { questions: d.questions.filter((_, k) => k !== j) })} />
                      </div>
                    ))}
                    <button type="button" className="bq-add" onClick={() => upd(i, { questions: [...d.questions, ''] })}>+ sub-topic</button>
                  </div>
                  <Input value={d.keywords} onChange={(e) => upd(i, { keywords: e.target.value })} placeholder="Target keywords, comma-separated (for topic selection, not stuffing)" />
                  <GapList gaps={d.gaps} />
                  <div className="bq-actions">
                    <Button size="sm" variant="ghost" onClick={() => onUse(d.title, d.questions.filter(Boolean), [], d.gaps)}>Use in composer</Button>
                  </div>
                </div>
              ))}
              <div className="bq-actions">
                <Button size="sm" variant="primary" disabled={sending || !picked.length} iconLeft={<Icon name="send" size={13} />} onClick={send}>
                  {sending ? 'Sending…' : `Send ${picked.length} topic${picked.length === 1 ? '' : 's'} for approval`}
                </Button>
                <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>Goes to the SEO team (Optimization Manager) as one batch for this page.</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const TEMPLATE_OPTS: { value: TemplateType; label: string }[] = [
  { value: 'case_study_html', label: 'Case study — HTML' },
  { value: 'case_study_astro', label: 'Case study — Astro Markdown' },
  { value: 'blog_html', label: 'Blog — HTML' },
  { value: 'blog_astro', label: 'Blog — Astro Markdown' },
];

function TemplateEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['clientTemplates'], queryFn: getClientTemplates });
  const [open, setOpen] = useState(false);
  // Only the layouts for this client's platform can ever be used, so hide the
  // other two rather than inviting someone to fill in a template that never runs.
  const platform = useContentPlatform();
  const opts = platform
    ? TEMPLATE_OPTS.filter((o) => o.value.endsWith(platform === 'dnn' ? '_html' : '_astro'))
    : TEMPLATE_OPTS;
  const [typeChoice, setTypeChoice] = useState<TemplateType>('case_study_html');
  const type = opts.some((o) => o.value === typeChoice) ? typeChoice : opts[0].value;
  const setType = setTypeChoice;
  const [edited, setEdited] = useState<Partial<Record<TemplateType, string>>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const stored = data?.templates?.[type] ?? '';
  const value = edited[type] ?? stored;
  const isCustom = !!(data?.templates?.[type] || '').trim();

  const save = async () => {
    setBusy(true);
    try { await setClientTemplate(type, value); qc.invalidateQueries({ queryKey: ['clientTemplates'] }); setSaved(true); setTimeout(() => setSaved(false), 1600); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await setClientTemplate(type, ''); setEdited((e) => ({ ...e, [type]: '' })); qc.invalidateQueries({ queryKey: ['clientTemplates'] }); }
    finally { setBusy(false); }
  };
  const onFile = async (f: File | null) => { if (!f) return; const t = await f.text(); setEdited((e) => ({ ...e, [type]: t })); };

  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> Per-client layout template
        <Badge variant={isCustom ? 'success' : 'neutral'}>{isCustom ? 'Custom' : 'Default'}</Badge>
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="vault-add__hint">
            {platform
              ? <>Two layouts for this client&rsquo;s {platform === 'dnn' ? 'DNN' : 'Astro'} site (case study and blog). Pick one, paste that client&rsquo;s example article (or a bracketed template), and Save. Each is saved on its own; leave blank to use the Triton default.</>
              : <>There are <strong>four separate layouts</strong> (Case study / Blog × HTML / Astro). Pick one below, paste that client&rsquo;s example article (or a bracketed template), and Save. Repeat for each one you use &mdash; e.g. <strong>Case study &mdash; Astro</strong>, then <strong>Blog &mdash; Astro</strong>. Each is saved on its own; leave blank to use the Triton default.</>}
          </p>
          <div className="vault-add__methods" style={{ padding: 0, marginBottom: 10 }}>
            {opts.map((o) => {
              const set = !!(data?.templates?.[o.value] || '').trim();
              return (
                <button key={o.value} type="button" className={`vault-tab ${type === o.value ? 'is-on' : ''}`} onClick={() => setType(o.value)}>
                  {o.label} <Badge variant={set ? 'success' : 'neutral'}>{set ? 'Custom' : 'Default'}</Badge>
                </button>
              );
            })}
          </div>
          <div className="vault-add__row">
            <input type="file" accept=".html,.md,.markdown,.txt,text/*" onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
          </div>
          <Textarea rows={8} placeholder={`Paste the ${TEMPLATE_OPTS.find((o) => o.value === type)?.label} example / template here…`} value={value} onChange={(e) => setEdited((prev) => ({ ...prev, [type]: e.target.value }))} />
          <div className="vault-add__actions">
            {isCustom && <Button variant="ghost" size="sm" disabled={busy} onClick={clear}>Reset to default</Button>}
            <Button variant="primary" size="sm" disabled={busy} onClick={save} iconLeft={<Icon name={saved ? 'check' : 'settings'} size={14} />}>
              {saved ? 'Saved' : busy ? 'Saving…' : 'Save template'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SetupItem({ done, label, hint }: { done: boolean; label: string; hint: React.ReactNode }) {
  return (
    <li className={`setup-item ${done ? 'is-done' : ''}`}>
      <span className="setup-item__dot">{done ? <Icon name="check" size={12} /> : ''}</span>
      <div className="setup-item__body"><strong>{label}</strong><span className="setup-item__hint">{hint}</span></div>
    </li>
  );
}

const AVAIL_DAYS: [number, string][] = [[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [7, 'Sun']];
const AVAIL_TZS = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles'];
function hourLabel(h: number): string {
  if (h === 0 || h === 24) return '12:00 AM';
  if (h === 12) return '12:00 PM';
  return h < 12 ? `${h}:00 AM` : `${h - 12}:00 PM`;
}

/* Per-Content-Manager booking hours (keyed to the signed-in user). Drives which
   30-min slots clients see when scheduling a live interview with this CM. */
function AvailabilityEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['myAvailability'], queryFn: getMyAvailability });
  const [av, setAv] = useState<CmAvailability | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (data) setAv((prev) => prev ?? data); }, [data]);
  if (!av) return null;
  const toggle = (d: number) => setAv({ ...av, days: av.days.includes(d) ? av.days.filter((x) => x !== d) : [...av.days, d].sort((a, b) => a - b) });
  const save = async () => {
    setSaving(true); setSaved(false);
    try { await setMyAvailability(av); setSaved(true); qc.invalidateQueries({ queryKey: ['myAvailability'] }); } finally { setSaving(false); }
  };
  return (
    <div className="gen-linkmap">
      <div className="gen-linkmap__toggle" style={{ cursor: 'default' }}><Icon name="calendar" size={14} /> My interview availability</div>
      <div className="gen-linkmap__body">
        <p className="vault-add__hint">Your bookable 30-minute hours for live interviews you run (applies wherever you’re the Content Manager). Clients only ever see open times on your Google Calendar.</p>
        <div className="avail-days">
          {AVAIL_DAYS.map(([d, label]) => (
            <button key={d} type="button" className={`avail-day ${av.days.includes(d) ? 'is-on' : ''}`} onClick={() => toggle(d)}>{label}</button>
          ))}
        </div>
        <div className="cq-fieldrow">
          <Select label="Start" options={Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: hourLabel(h) }))} value={String(av.startHour)} onChange={(e) => setAv({ ...av, startHour: Number(e.target.value) })} />
          <Select label="End" options={Array.from({ length: 24 }, (_, i) => { const h = i + 1; return { value: String(h), label: hourLabel(h) }; })} value={String(av.endHour)} onChange={(e) => setAv({ ...av, endHour: Number(e.target.value) })} />
          <Select label="Timezone" options={AVAIL_TZS.map((t) => ({ value: t, label: t.replace('America/', '').replace('_', ' ') }))} value={av.timezone} onChange={(e) => setAv({ ...av, timezone: e.target.value })} />
        </div>
        <div className="vault-add__row" style={{ alignItems: 'center', gap: 12 }}>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save availability'}</Button>
          {saved && <span className="dash-muted">Saved.</span>}
        </div>
      </div>
    </div>
  );
}

function AssetFolderEditor({ status }: { status: SetupStatus }) {
  const qc = useQueryClient();
  const [val, setVal] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const value = val ?? status.assetFolder;
  const save = async () => { setSaving(true); try { await setAssetFolder(value); qc.invalidateQueries({ queryKey: ['setupStatus'] }); } finally { setSaving(false); } };
  return (
    <div className="gen-linkmap">
      <div className="gen-linkmap__toggle" style={{ cursor: 'default' }}><Icon name="image" size={14} /> R2 image folder</div>
      <div className="gen-linkmap__body">
        <p className="vault-add__hint">Folder in the <code>client-assets</code> R2 bucket (default = the client name as a slug). Published images live at <code>cdn.poweredbytriton.com/{value || '<folder>'}/…</code></p>
        <div className="vault-add__row">
          <Input value={value} onChange={(e) => setVal(e.target.value)} placeholder="client-slug" />
          <Button variant="primary" size="sm" disabled={saving} onClick={save} iconLeft={<Icon name={saving ? 'refresh-cw' : 'settings'} size={14} />}>Save folder</Button>
        </div>
      </div>
    </div>
  );
}

/* `authorPagesOnly`: a DNN client moving to Astro. Its blog still delivers to
   DNN, but author pages can go to the new repo ahead of the switch. */
function GithubEditor({ authorPagesOnly = false }: { authorPagesOnly?: boolean }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['githubConfig'], queryFn: getGithubConfig });
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState<GithubConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const c = cfg ?? data ?? { repo: '', installationId: '', branch: 'main', blogPath: 'src/content/articles/blog', articlePath: 'src/content/articles/case-studies', pagesPath: 'src/pages', legalPath: 'src/content/legal', authorsPath: 'src/content/pages/authors' };
  const set = (k: keyof GithubConfig, v: string) => setCfg({ ...c, [k]: v });
  const configured = !!(data?.repo && data?.installationId);
  const [check, setCheck] = useState<GithubCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const runCheck = async () => {
    setChecking(true); setCheck(null);
    try { setCheck(await checkGithubConfig()); }
    catch (e) { setCheck({ ok: false, branches: [], defaultBranch: '', suggestedInstallationId: '', problems: [e instanceof Error ? e.message : String(e)] }); }
    finally { setChecking(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await setGithubConfig({
        repo: c.repo, installationId: c.installationId, branch: c.branch,
        blogPath: c.blogPath, articlePath: c.articlePath, pagesPath: c.pagesPath,
        legalPath: c.legalPath, authorsPath: c.authorsPath,
      });
      qc.invalidateQueries({ queryKey: ['githubConfig'] });
      setSaved(true); setTimeout(() => setSaved(false), 1600);
    }
    finally { setSaving(false); }
  };
  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> {authorPagesOnly ? 'Astro repo (author pages)' : 'Push to site (GitHub / Astro)'}
        <Badge variant={configured ? 'success' : 'neutral'}>{configured ? 'Set' : 'Not set'}</Badge>
      </button>
      {open && (
        <div className="gen-linkmap__body">
          {data && !data.appConfigured && <p className="vault-err"><Icon name="alert-circle" size={13} /> The GitHub App secrets aren&rsquo;t set on the Worker yet — pushing will fail until they are.</p>}
          <p className="vault-add__hint">Commit generated Markdown into this client&rsquo;s Astro repo. Install the Triton GitHub App on the repo, then set its installation id here.</p>
          {authorPagesOnly && (
            <p className="vault-add__hint">This client still delivers articles to DNN. Setting the repo here only enables pushing Experts&rsquo; author pages to the new Astro site; switch Site delivery to Astro when the site goes live.</p>
          )}
          <div className="vault-add__row">
            <Input placeholder="owner/repo" value={c.repo} onChange={(e) => set('repo', e.target.value)} />
            <Input placeholder="installation id" value={c.installationId} onChange={(e) => set('installationId', e.target.value)} />
          </div>
          <div className="vault-add__row">
            <Input placeholder="branch (main)" value={c.branch} onChange={(e) => set('branch', e.target.value)} />
            <Input placeholder="blog path (src/content/articles/blog)" value={c.blogPath} onChange={(e) => set('blogPath', e.target.value)} />
            <Input placeholder="article path (src/content/articles/case-studies)" value={c.articlePath} onChange={(e) => set('articlePath', e.target.value)} />
          </div>
          <div className="vault-add__row">
            <Input placeholder="pages path (src/pages)" value={c.pagesPath} onChange={(e) => set('pagesPath', e.target.value)} />
            <Input placeholder="legal collection (src/content/legal)" value={c.legalPath ?? 'src/content/legal'} onChange={(e) => set('legalPath', e.target.value)} />
            <Input placeholder="author pages (src/content/pages/authors)" value={c.authorsPath ?? 'src/content/pages/authors'} onChange={(e) => set('authorsPath', e.target.value)} />
          </div>
          <div className="vault-add__actions">
            <Button variant="primary" size="sm" disabled={saving} onClick={save} iconLeft={<Icon name={saved ? 'check' : 'settings'} size={14} />}>{saved ? 'Saved' : saving ? 'Saving…' : 'Save repo config'}</Button>
            <Button variant="outline" size="sm" disabled={checking || !data?.repo} onClick={runCheck} iconLeft={<Icon name={checking ? 'refresh-cw' : 'shield-check'} size={14} />}>
              {checking ? 'Checking…' : 'Check connection'}
            </Button>
          </div>
          {check && (
            check.ok
              ? <p className="gen-note"><Icon name="check-circle" size={13} /> Connected — {check.repo} on branch <code>{check.branch}</code>. Pushes will work.</p>
              : <div className="vault-add__hint">
                  {check.problems.map((pr) => <p key={pr} className="vault-err"><Icon name="alert-circle" size={13} /> {pr}</p>)}
                  {!!check.branches.length && (
                    <p>Branches on the repo: {check.branches.map((b) => <code key={b} style={{ marginRight: 6 }}>{b}</code>)}</p>
                  )}
                  {!!check.suggestedInstallationId && check.suggestedInstallationId !== c.installationId && (
                    <p>
                      GitHub reports installation id <code>{check.suggestedInstallationId}</code> for this repo.{' '}
                      <button type="button" className="vault-link" onClick={() => set('installationId', check.suggestedInstallationId)}>Use it</button> then save.
                    </p>
                  )}
                </div>
          )}
        </div>
      )}
    </div>
  );
}

/* The client's one delivery target. Cached under its own key so the Setup radio,
   the generator, and the article library all read the same value. */
function useContentPlatform(enabled = true): ContentPlatform {
  const { data } = useQuery({ queryKey: ['contentPlatform'], queryFn: getContentPlatform, enabled });
  return data?.platform ?? '';
}

/* Astro or DNN, once per client — the client has one website. Everything
   downstream follows: the generator's output format, which push button appears,
   and whether DNN categories are needed at all. */
function SiteDeliveryEditor({ platform }: { platform: ContentPlatform }) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState<ContentPlatform | null>(null);
  const choose = async (p: ContentPlatform) => {
    if (p === platform) return;
    setSaving(p);
    try {
      await setContentPlatform(p);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['contentPlatform'] }),
        qc.invalidateQueries({ queryKey: ['setupStatus'] }),
      ]);
    } finally { setSaving(null); }
  };
  const opts: { value: 'astro' | 'dnn'; label: string; hint: string }[] = [
    { value: 'astro', label: 'Astro repo (GitHub)', hint: 'Newer sites. Generates Astro Markdown and commits it to the client’s repo.' },
    { value: 'dnn', label: 'DNN (EasyDNNNews)', hint: 'Legacy sites with the Triton-DNN-API module. Generates HTML and posts it as a categorised article.' },
  ];
  return (
    <div className="gen-linkmap">
      <div className="gen-linkmap__toggle" style={{ cursor: 'default' }}>
        <Icon name="external" size={14} /> Site delivery
        <Badge variant={platform ? 'success' : 'warning'}>{platform === 'astro' ? 'Astro' : platform === 'dnn' ? 'DNN' : 'Not set'}</Badge>
      </div>
      <div className="gen-linkmap__body">
        <p className="vault-add__hint">Which platform this client&rsquo;s site runs on. Set it once — the generator then writes in the format that site takes, instead of asking every time.</p>
        <div className="dnn-cats">
          {opts.map((o) => (
            <label key={o.value} className={`dnn-cat ${platform === o.value ? 'is-on' : ''}`} title={o.hint}>
              <input type="radio" name="content-platform" checked={platform === o.value} disabled={!!saving} onChange={() => choose(o.value)} />
              {' '}{saving === o.value ? 'Saving…' : o.label}
            </label>
          ))}
        </div>
        <p className="vault-add__hint" style={{ marginTop: 8 }}>
          {platform ? opts.find((o) => o.value === platform)!.hint
            : 'Until this is set, both push paths stay available and the generator keeps asking for a format per run.'}
        </p>
      </div>
    </div>
  );
}

interface CatNode { id: number; name: string; children: CatNode[] }

/* EasyDNNNews categories arrive flat, with a level and a parent link. Parent ids
   are authoritative; `level` is the fallback for builds that send only a depth,
   where DNN's pre-order listing means "the last node one level up" is the parent.
   Anything left unreachable (a parent we never received, or a cycle) is promoted
   to a root — a category that silently disappears from this picker is how an
   article ends up mis-tagged. */
function buildCategoryTree(cats: DnnCategory[]): { roots: CatNode[]; paths: Map<number, string> } {
  const byId = new Map<number, CatNode>();
  for (const c of cats) byId.set(c.id, { id: c.id, name: c.name || `Category ${c.id}`, children: [] });
  // DNN's root level is 1 on some builds and 0 on others — normalise to 0.
  const base = cats.length ? Math.min(...cats.map((c) => (Number.isFinite(c.level) ? c.level : 0))) : 0;

  const roots: CatNode[] = [];
  const parentOf = new Map<number, number>();
  const trail: CatNode[] = [];
  for (const c of cats) {
    const node = byId.get(c.id)!;
    const depth = Math.max(0, (Number.isFinite(c.level) ? c.level : 0) - base);
    const parent = (c.parentId != null ? byId.get(c.parentId) : undefined) ?? (depth > 0 ? trail[depth - 1] : undefined);
    if (parent && parent.id !== node.id) { parent.children.push(node); parentOf.set(node.id, parent.id); }
    else roots.push(node);
    trail[depth] = node;
    trail.length = depth + 1;
  }

  // Reachability pass: also assigns the breadcrumb each selected chip shows.
  const paths = new Map<number, string>();
  const seen = new Set<number>();
  const walk = (nodes: CatNode[], prefix: string) => {
    for (const n of nodes) {
      if (seen.has(n.id)) continue; // cycle guard
      seen.add(n.id);
      const path = prefix ? `${prefix} › ${n.name}` : n.name;
      paths.set(n.id, path);
      walk(n.children, path);
    }
  };
  walk(roots, '');
  for (const c of cats) {
    const n = byId.get(c.id)!;
    if (seen.has(n.id)) continue;
    const from = parentOf.get(n.id);
    if (from != null) byId.get(from)!.children = byId.get(from)!.children.filter((x) => x.id !== n.id);
    roots.push(n);
    walk([n], '');
  }
  return { roots, paths };
}

/* Nodes to show for a search: every match, its ancestors (for context) and its
   descendants (so you can still reach a child of a matched parent). */
function searchMatches(roots: CatNode[], q: string): Set<number> {
  const keep = new Set<number>();
  const addSubtree = (n: CatNode) => { keep.add(n.id); n.children.forEach(addSubtree); };
  const visit = (n: CatNode, ancestors: CatNode[]) => {
    if (n.name.toLowerCase().includes(q)) { ancestors.forEach((a) => keep.add(a.id)); addSubtree(n); }
    n.children.forEach((c) => visit(c, [...ancestors, n]));
  };
  roots.forEach((r) => visit(r, []));
  return keep;
}

/* The category picker. Same contract as the flat chip row it replaced — cats +
   selected + onToggle — so every push surface gets the tree for free.

   Selecting a child deliberately does NOT select its ancestors: implicit category
   assignment is the same class of mistake as the empty-CategoryIds push that made
   articles invisible, so what you tick is exactly what gets sent. */
function DnnCategoryTree({ cats, selected, onToggle }: { cats: DnnCategory[]; selected: number[]; onToggle: (id: number) => void }) {
  const { roots, paths } = useMemo(() => buildCategoryTree(cats), [cats]);
  const [query, setQuery] = useState('');
  // null = "not touched yet", so the default view derives from the selection:
  // branches holding a selected category open, the rest closed.
  const [collapsed, setCollapsed] = useState<Set<number> | null>(null);
  const autoCollapsed = useMemo(() => {
    const out = new Set<number>();
    const hasSel = (n: CatNode): boolean => {
      const kids = n.children.some(hasSel);
      if (n.children.length && !kids && !n.children.some((c) => selected.includes(c.id))) out.add(n.id);
      return kids || selected.includes(n.id);
    };
    roots.forEach(hasSel);
    return out;
  }, [roots, selected]);
  const shut = collapsed ?? autoCollapsed;
  const q = query.trim().toLowerCase();
  const matches = useMemo(() => (q ? searchMatches(roots, q) : null), [roots, q]);

  const rows: { node: CatNode; depth: number; open: boolean }[] = [];
  const walk = (nodes: CatNode[], depth: number) => {
    for (const n of nodes) {
      if (matches && !matches.has(n.id)) continue;
      const open = !!matches || !shut.has(n.id);
      rows.push({ node: n, depth, open });
      if (n.children.length && open) walk(n.children, depth + 1);
    }
  };
  walk(roots, 0);

  const toggleOpen = (id: number) => setCollapsed(() => {
    const next = new Set(shut);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (!cats.length) return null;
  const nested = cats.some((c) => c.parentId != null) || new Set(cats.map((c) => c.level)).size > 1;
  return (
    <div className="dnn-tree">
      {cats.length > 8 && (
        <input className="dnn-tree__search" type="search" placeholder={`Filter ${cats.length} categories…`}
          value={query} onChange={(e) => setQuery(e.target.value)} />
      )}
      <div className="dnn-tree__rows" role="group" aria-label="DNN categories">
        {rows.map(({ node, depth, open }) => {
          const on = selected.includes(node.id);
          return (
            <div key={node.id} className={`dnn-tree__row ${on ? 'is-on' : ''}`} style={{ paddingLeft: 4 + depth * 16 }}>
              {node.children.length > 0 ? (
                <button type="button" className="dnn-tree__caret" onClick={() => toggleOpen(node.id)}
                  aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${node.name}`}>
                  <Icon name={open ? 'chevron-down' : 'chevron-right'} size={13} />
                </button>
              ) : <span className="dnn-tree__caret dnn-tree__caret--leaf" />}
              <label className="dnn-tree__label">
                <input type="checkbox" checked={on} onChange={() => onToggle(node.id)} />
                <span className="dnn-tree__name">{node.name}</span>
              </label>
              {node.children.length > 0 && !open && (
                <span className="dnn-tree__count">{node.children.length}</span>
              )}
            </div>
          );
        })}
        {matches && !rows.length && <p className="vault-add__hint" style={{ margin: '4px 0' }}>No category matches “{query}”.</p>}
      </div>
      {nested && <p className="dnn-tree__note">Ticking a child doesn’t add its parent — select every category the article should be listed under.</p>}
      <SelectedCategories selected={selected} paths={paths} onToggle={onToggle} />
    </div>
  );
}

/* What will actually be sent, spelled out with full paths — a bare "Roofing"
   chip is ambiguous once two branches can both contain one. */
function SelectedCategories({ selected, paths, onToggle }: { selected: number[]; paths: Map<number, string>; onToggle: (id: number) => void }) {
  if (!selected.length) return null;
  return (
    <div className="dnn-sel">
      {selected.map((id) => (
        <button key={id} type="button" className="dnn-sel__chip" onClick={() => onToggle(id)}
          title="Remove this category">
          {paths.get(id) ?? `Unknown category (id ${id})`} <Icon name="x" size={11} />
        </button>
      ))}
    </div>
  );
}

/* Category selection for a push. Seeds from the client's saved defaults and lets
   the operator override per article. A push with an empty selection is refused by
   the API — EasyDNNNews only lists categorised articles, so pushing without one
   silently drops the piece. */
/* How a story reads in the generator pickers. A Knowledge Session is named as
   such because it decides which tab it belongs on. */
const storyKindLabel = (s: GenStory) => (s.studioKind === 'knowledge' ? 'Knowledge session' : s.kind === 'vault' ? 'Vault transcript' : s.kind === 'ai' ? 'AI' : 'Live');

function useDnnCategories(enabled: boolean, kind: 'blog' | 'case_study' = 'blog') {
  const { data: cfg } = useQuery({ queryKey: ['dnnConfig'], queryFn: getDnnConfig, enabled });
  // The override is tagged with the kind it was made for, so switching the
  // generator between Blog and Case study falls back to that kind's defaults
  // instead of carrying the other kind's hand-picked tags over.
  const [override, setOverride] = useState<{ kind: string; ids: number[] } | null>(null);
  const [cats, setCats] = useState<DnnCategory[]>([]);
  const [mods, setMods] = useState<DnnModule[]>([]);
  const [modOverride, setModOverride] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState('');
  const defaults = (kind === 'case_study' ? cfg?.caseStudyCategoryIds : cfg?.blogCategoryIds) ?? [];
  const selected = override?.kind === kind ? override.ids : defaults;
  // Which page this kind of piece belongs on, unless overridden for this push.
  const defaultModule = (kind === 'case_study' ? cfg?.caseStudyModuleId : cfg?.blogModuleId) ?? 0;
  const moduleId = modOverride ?? defaultModule;
  const load = async () => {
    setLoading(true); setErr('');
    try {
      const [cat, mod] = await Promise.all([
        getDnnCategories(),
        // Modules need module 1.0.3+; on an older site this 502s and the picker
        // just stays empty rather than blocking the categories we did get.
        getDnnModules().catch(() => ({ modules: [] as DnnModule[] })),
      ]);
      setCats(cat.categories); setMods(mod.modules); setLoaded(true);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  };
  const toggle = (id: number) => setOverride({ kind, ids: selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id] });
  return { cats, selected, toggle, mods, moduleId, setModule: setModOverride, load, loading, loaded, err, configured: !!cfg?.siteDomain, isDefault: override?.kind !== kind };
}

// Where a DNN push will land: which page, and under which categories.
function DnnCategoryBar({ p }: { p: ReturnType<typeof useDnnCategories> }) {
  return (
    <div className="gen-dnn-cats">
      <div className="vault-add__row" style={{ alignItems: 'center' }}>
        <Button variant="outline" size="sm" disabled={p.loading} onClick={p.load}>{p.loading ? 'Loading…' : p.cats.length ? 'Reload from DNN' : 'Load categories & pages'}</Button>
        <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>
          {p.selected.length
            ? `${p.selected.length} categor${p.selected.length === 1 ? 'y' : 'ies'}${p.isDefault ? ' from this kind’s Setup defaults' : ' selected'}`
            : 'Pick a category — DNN won’t list an uncategorised article.'}
        </span>
      </div>
      <DnnCategoryTree cats={p.cats} selected={p.selected} onToggle={p.toggle} />
      {p.mods.length > 1 && (
        <div className="vault-add__row" style={{ marginTop: 6 }}>
          <Select
            label="Page"
            value={String(p.moduleId || 0)}
            onChange={(e) => p.setModule(Number(e.target.value))}
            options={[{ value: '0', label: 'Site default (first EasyDNNNews module)' },
              ...p.mods.map((m) => ({ value: String(m.moduleId), label: `${m.tabUrl.replace(/^https?:\/\/[^/]+/i, '') || m.moduleTitle} (id ${m.moduleId})` }))]}
          />
        </div>
      )}
      {p.loaded && !p.cats.length && <p className="vault-add__hint">DNN returned no categories. Create one in EasyDNNNews first.</p>}
      {p.err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {p.err}</p>}
    </div>
  );
}

function DnnEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['dnnConfig'], queryFn: getDnnConfig });
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState<{ siteDomain: string; username: string; password: string; author: string; blogCategoryIds: number[]; caseStudyCategoryIds: number[]; blogModuleId: number; caseStudyModuleId: number } | null>(null);
  const [cats, setCats] = useState<DnnCategory[]>([]);
  const [mods, setMods] = useState<DnnModule[]>([]);
  const [catsLoaded, setCatsLoaded] = useState(false);
  const [loadingCats, setLoadingCats] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const c = cfg ?? {
    siteDomain: data?.siteDomain ?? '', username: data?.username ?? '', password: '', author: data?.author ?? '',
    blogCategoryIds: data?.blogCategoryIds ?? [], caseStudyCategoryIds: data?.caseStudyCategoryIds ?? [],
    blogModuleId: data?.blogModuleId ?? 0, caseStudyModuleId: data?.caseStudyModuleId ?? 0,
  };
  const set = (k: keyof typeof c, v: unknown) => setCfg({ ...c, [k]: v } as typeof c);
  const configured = !!data?.siteDomain; // login comes from per-client creds or the shared DNN secrets

  const loadCats = async () => {
    setLoadingCats(true); setErr('');
    try {
      const [cat, mod] = await Promise.all([
        getDnnCategories(c.siteDomain),
        // 1.0.3+ only — an older module 404s here, which shouldn't block categories.
        getDnnModules(c.siteDomain).catch(() => ({ modules: [] as DnnModule[] })),
      ]);
      setCats(cat.categories); setMods(mod.modules); setCatsLoaded(true);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoadingCats(false); }
  };
  const save = async () => {
    setSaving(true); setErr('');
    try {
      await setDnnConfig({
        siteDomain: c.siteDomain, username: c.username, password: c.password || undefined, author: c.author,
        blogCategoryIds: c.blogCategoryIds, caseStudyCategoryIds: c.caseStudyCategoryIds,
        blogModuleId: c.blogModuleId, caseStudyModuleId: c.caseStudyModuleId,
      });
      qc.invalidateQueries({ queryKey: ['dnnConfig'] }); setSaved(true); setTimeout(() => setSaved(false), 1600);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };
  const toggleCat = (key: 'blogCategoryIds' | 'caseStudyCategoryIds') => (id: number) =>
    set(key, c[key].includes(id) ? c[key].filter((x) => x !== id) : [...c[key], id]);
  // Tests the SAVED config (login + authenticated DnnHello) — save first.
  const test = async () => {
    setTesting(true); setTestMsg('');
    try { const r = await testDnn(); setTestMsg(r.ok ? `✓ Authenticated (HTTP ${r.status}) — JWT is working on this site.` : `✗ Auth failed (HTTP ${r.status}): ${r.message}`); }
    catch (e) { setTestMsg(e instanceof Error ? e.message : String(e)); }
    finally { setTesting(false); }
  };

  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> Push to DNN (EasyDNNNews)
        <Badge variant={configured ? 'success' : 'neutral'}>{configured ? 'Set' : 'Not set'}</Badge>
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="vault-add__hint">For legacy DNN sites with the Triton-DNN-API module. Pushes generated <strong>HTML</strong> articles into EasyDNNNews. Only the <strong>site URL</strong> is required — leave the login blank to use the shared Triton DNN service account; fill it in only if this site needs its own credentials.</p>
          <div className="vault-add__row">
            <Input placeholder="site URL (https://client.com)" value={c.siteDomain} onChange={(e) => set('siteDomain', e.target.value)} />
            <Input placeholder="author (e.g. Team Onyx)" value={c.author} onChange={(e) => set('author', e.target.value)} />
          </div>
          <div className="vault-add__row">
            <Input placeholder="username (optional — shared account)" value={c.username} onChange={(e) => set('username', e.target.value)} />
            <Input type="password" placeholder={data?.hasPassword ? '•••••• (unchanged)' : 'password (optional — shared account)'} value={c.password} onChange={(e) => set('password', e.target.value)} />
          </div>
          <div className="vault-add__row">
            <Button variant="outline" size="sm" disabled={!c.siteDomain || loadingCats} onClick={loadCats}>{loadingCats ? 'Loading…' : 'Load categories & pages'}</Button>
            {!cats.length && (c.blogCategoryIds.length > 0 || c.caseStudyCategoryIds.length > 0) && (
              <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>
                Saved defaults: {c.blogCategoryIds.length} blog, {c.caseStudyCategoryIds.length} case study — load to edit.
              </span>
            )}
          </div>
          {cats.length > 0 && (
            <>
              {/* Categories are topical tags, so each kind needs its own defaults —
                  blogs get their subjects, case studies get "Case Study". */}
              <p className="vault-add__hint" style={{ marginTop: 8 }}>
                <strong>Default categories per kind.</strong> Every push starts pre-selected with these and stays editable. DNN only lists categorised articles, so a kind with no default here has to be tagged by hand each time.
              </p>
              <p className="vault-add__hint" style={{ marginTop: 6 }}>Blogs ({c.blogCategoryIds.length} default{c.blogCategoryIds.length === 1 ? '' : 's'}):</p>
              <DnnCategoryTree cats={cats} selected={c.blogCategoryIds} onToggle={toggleCat('blogCategoryIds')} />
              <p className="vault-add__hint" style={{ marginTop: 6 }}>Case studies ({c.caseStudyCategoryIds.length} default{c.caseStudyCategoryIds.length === 1 ? '' : 's'}):</p>
              <DnnCategoryTree cats={cats} selected={c.caseStudyCategoryIds} onToggle={toggleCat('caseStudyCategoryIds')} />
            </>
          )}
          {catsLoaded && !cats.length && <p className="vault-add__hint">DNN returned no categories — create one in EasyDNNNews before pushing, or articles won’t appear on the site.</p>}
          {mods.length > 0 && (
            <>
              <p className="vault-add__hint" style={{ marginTop: 8 }}>This site has {mods.length === 1 ? 'one EasyDNNNews page' : `${mods.length} EasyDNNNews pages`}. Pick where each kind of piece should land — otherwise DNN uses whichever module it finds first.</p>
              <div className="vault-add__row">
                <Select
                  label="Blogs land on"
                  value={String(c.blogModuleId || 0)}
                  onChange={(e) => set('blogModuleId', Number(e.target.value))}
                  options={[{ value: '0', label: 'Site default' }, ...mods.map((m) => ({ value: String(m.moduleId), label: `${m.tabUrl.replace(/^https?:\/\/[^/]+/i, '') || m.moduleTitle} (id ${m.moduleId})` }))]}
                />
                <Select
                  label="Case studies land on"
                  value={String(c.caseStudyModuleId || 0)}
                  onChange={(e) => set('caseStudyModuleId', Number(e.target.value))}
                  options={[{ value: '0', label: 'Site default' }, ...mods.map((m) => ({ value: String(m.moduleId), label: `${m.tabUrl.replace(/^https?:\/\/[^/]+/i, '') || m.moduleTitle} (id ${m.moduleId})` }))]}
                />
              </div>
            </>
          )}
          {catsLoaded && !mods.length && <p className="vault-add__hint">No EasyDNNNews page list — this site is on Triton-DNN-API 1.0.2 or older, so pushes land on whichever module DNN finds first. Upgrade to 1.0.3 to choose.</p>}
          {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}
          <div className="vault-add__actions">
            <Button variant="primary" size="sm" disabled={saving} onClick={save} iconLeft={<Icon name={saved ? 'check' : 'settings'} size={14} />}>{saved ? 'Saved' : saving ? 'Saving…' : 'Save DNN config'}</Button>
            <Button variant="outline" size="sm" disabled={testing || !data?.siteDomain} onClick={test}>{testing ? 'Testing…' : 'Test connection'}</Button>
          </div>
          {testMsg && <p className="vault-add__hint" style={{ marginTop: 6 }}>{testMsg}</p>}
        </div>
      )}
    </div>
  );
}

function HouseStyleEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['houseStyle'], queryFn: getHouseStyle });
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const val = body ?? data?.body ?? '';
  const configured = !!data?.body?.trim();
  const save = async () => {
    setSaving(true);
    try { await setHouseStyle(val); qc.invalidateQueries({ queryKey: ['houseStyle'] }); setSaved(true); setTimeout(() => setSaved(false), 1600); }
    finally { setSaving(false); }
  };
  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> House style (custom instructions)
        <Badge variant={configured ? 'success' : 'neutral'}>{configured ? 'Set' : 'Not set'}</Badge>
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="vault-add__hint">Client-specific writing rules applied to <strong>every</strong> generated piece for this client (case studies, blogs, web content). They override the default editorial rules on conflict. e.g. &ldquo;Write in first-person plural. Never say &lsquo;affordable.&rsquo; Always mention 24/7 emergency service. Keep paragraphs to two sentences.&rdquo;</p>
          <Textarea rows={6} placeholder="Custom instructions for this client…" value={val} onChange={(e) => setBody(e.target.value)} />
          <div className="vault-add__actions">
            <Button variant="primary" size="sm" disabled={saving} onClick={save} iconLeft={<Icon name={saved ? 'check' : 'settings'} size={14} />}>{saved ? 'Saved' : saving ? 'Saving…' : 'Save house style'}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* What this client's region should LOOK like, used by every AI hero image.
   Derived once from the service area and cached, and editable here — when an
   image comes back with the wrong housing stock or vegetation, this is the fix
   that holds for every future article rather than one prompt. */
function LocaleBriefEditor() {
  const { data } = useQuery({ queryKey: ['heroLocaleBrief'], queryFn: getHeroLocaleBrief });
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const qc = useQueryClient();
  if (data && !data.configured) return null;
  const text = val ?? data?.brief ?? '';
  const save = async () => {
    setSaving(true);
    try {
      await setHeroLocaleBrief(text);
      qc.invalidateQueries({ queryKey: ['heroLocaleBrief'] });
      setSaved(true); setTimeout(() => setSaved(false), 1600);
    } finally { setSaving(false); }
  };
  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> AI image — regional look
        <Badge variant={data?.brief ? 'success' : 'neutral'}>{data?.brief ? 'Set' : 'Auto'}</Badge>
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="vault-add__hint">
            Describes what this client&rsquo;s service area actually looks like &mdash; housing stock, exterior materials, vegetation, weather. It is what keeps a Minnesota roofing article from being illustrated with Spanish tile and palm trees. Written automatically from the service area; correct it here and every future image follows.
          </p>
          <Textarea rows={6} value={text} onChange={(e) => setVal(e.target.value)}
            placeholder="e.g. 1950s–90s suburban ranches and split-levels; asphalt shingle roofs, vinyl and painted cedar siding; mature deciduous cover; long snowy winters with ice-dam damage." />
          <div className="vault-add__actions">
            <Button variant="primary" size="sm" disabled={saving} onClick={save} iconLeft={<Icon name={saved ? 'check' : 'settings'} size={14} />}>
              {saved ? 'Saved' : saving ? 'Saving…' : 'Save regional look'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SetupTab() {
  const { data: s } = useQuery({ queryKey: ['setupStatus'], queryFn: getSetupStatus });
  if (!s) return <div className="lead-none">Loading setup…</div>;
  const anyTpl = Object.values(s.templates).some(Boolean);
  const done = [s.sitemaps > 0, s.websitePages > 0, s.anchorsBuilt, s.transcripts > 0, s.coveredTopics > 0].filter(Boolean).length;
  return (
    <div className="setup">
      <p className="gen-hint" style={{ padding: '12px 20px 6px' }}>
        One-time setup for this client ({done}/5 core steps). The more complete, the better the grounded content, links, and images. Vault steps are on the <strong>Authenticity Vault</strong> panel above.
      </p>
      <ul className="setup-list">
        <SetupItem done={s.sitemaps > 0} label="Sitemap imported" hint="Vault → Add source → Import sitemap" />
        <SetupItem done={s.websitePages > 0} label={`Website pages crawled${s.websitePages ? ` (${s.websitePages})` : ''}`} hint="On the sitemap source → “Crawl all pages” (grounds content in real page copy)" />
        <SetupItem done={s.anchorsBuilt} label="Anchor text built" hint="On the sitemap source → “Build anchor text” (real internal links)" />
        <SetupItem done={s.transcripts > 0} label={`Interview transcripts in vault${s.transcripts ? ` (${s.transcripts})` : ''}`} hint="AI interviews auto-add; attach live transcripts on the interview page" />
        <SetupItem done={s.coveredTopics > 0} label={`Past blog topics imported${s.coveredTopics ? ` (${s.coveredTopics})` : ''}`} hint="So “Suggest from vault” won’t repeat published topics — edit below" />
        <SetupItem done={anyTpl} label="Layout templates (optional)" hint="Override the house layout if the client’s site differs — edit below" />
        <SetupItem done={s.hasHouseStyle} label="House style (optional)" hint="Per-client writing rules applied to every generation — edit below" />
        <SetupItem done={!!s.assetFolder} label="R2 image folder" hint={<>Images publish to <code>cdn.poweredbytriton.com/{s.assetFolder || '<folder>'}/</code></>} />
        <SetupItem
          done={!!s.contentPlatform}
          label={`Site delivery${s.contentPlatform === 'astro' ? ' — Astro repo' : s.contentPlatform === 'dnn' ? ' — DNN' : ''}`}
          hint="Astro repo or DNN — pick one below; the generator then writes in that site’s format"
        />
      </ul>
      <div className="setup-editors">
        <AvailabilityEditor />
        <AssetFolderEditor status={s} />
        <SiteDeliveryEditor platform={s.contentPlatform} />
        {/* Only the chosen platform's config is worth showing; before a choice is
            made both stay visible so nothing is unreachable. */}
        <GithubEditor authorPagesOnly={s.contentPlatform === 'dnn'} />
        {s.contentPlatform !== 'astro' && <DnnEditor />}
        <CoveredTopicsEditor />
        <HouseStyleEditor />
        <TemplateEditor />
        <LocaleBriefEditor />
      </div>
    </div>
  );
}

/* Which rung of the layout ladder produced the file. 'Starter default' is the
   one worth flagging: it means we never saw this client's real page schema. */
const LAYOUT_SOURCE_LABEL: Record<LayoutSource, string> = {
  pasted: 'Pasted', repo: 'From repo', saved: 'Saved layout', default: 'Starter default',
};

/* ---- AI hero image -------------------------------------------------- */

/* Generate a header image for the article being written. The prompt is shown and
   EDITABLE before anything is spent: "the image is wrong" is nearly always a
   wrong prompt (wrong region, wrong trade detail) rather than a bad roll, and
   rerolling the same words just buys a different wrong image.

   Attempts are kept rather than replaced — they are already staged privately, so
   going back to attempt 1 costs nothing. Nothing reaches the client's CDN until
   "Use this image", which is also where the browser does the WebP encode. */
/* A generated image has to be inspected before it is accepted — gibberish text,
   a wrong roofline or a stray hand only show up at size, and the grid thumbnails
   are 300px wide. */
function Lightbox({ src, prompt, onClose }: { src: string; prompt?: string; onClose: () => void }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    // The page behind must not scroll while this is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', esc); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div className="gen-lightbox" onClick={onClose} role="dialog" aria-modal="true" aria-label="Generated image">
      <button type="button" className="gen-lightbox__x" onClick={onClose} aria-label="Close"><Icon name="x" size={20} /></button>
      {/* Stop the click that closes the overlay from firing on the image itself. */}
      <div className="gen-lightbox__inner" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt="Generated image, full size" />
        {prompt && <p className="gen-lightbox__prompt">{prompt}</p>}
      </div>
    </div>
  );
}

/** A staged image's blob url, resolved once — the preview and the lightbox share it. */
function useHeroSrc(id: string): string {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let live = true;
    heroImageUrl(id).then((u) => { if (live) setUrl(u); }).catch(() => {});
    return () => { live = false; };
  }, [id]);
  return url;
}

/* ---- standalone AI images ---------------------------------------------- */

/* Images for anything — a site page, a social post, an ad — rather than for one
   article. Same generation path as the article hero, so the regional look and
   the editorial rules apply identically; what differs is that the result is a
   URL you take away, and that everything generated is kept.

   Keeping them matters: the same photograph gets wanted again, and regenerating
   costs credits and never returns the same picture twice. */
function ImageStudio() {
  const qc = useQueryClient();
  const { data: usage } = useQuery({ queryKey: ['heroUsage'], queryFn: getHeroUsage, staleTime: 60_000 });
  const { data: library } = useQuery({ queryKey: ['heroLibrary'], queryFn: getHeroLibrary });

  const [subject, setSubject] = useState('');
  const [extra, setExtra] = useState('');
  const [prompt, setPrompt] = useState('');
  /* What the brief could not honour. Shown because the writer used to drop a
     request — a person, a screen — and return a different picture in silence. */
  const [note, setNote] = useState('');
  const [allowPeople, setAllowPeople] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [jobs, setJobs] = useState<HeroJob[]>([]);
  const [published, setPublished] = useState<Record<string, string>>({});

  const atCap = !!usage && usage.used >= usage.cap;

  const draft = async () => {
    if (!subject.trim()) return;
    setDrafting(true); setErr('');
    /* The subject goes through the same writer the article hero uses, so the
       client's regional look and the no-people/no-text rules are applied here
       too — the difference is only that there is no article to read. */
    try {
      const r = await buildHeroPromptApi({ topic: subject, extra, allowPeople });
      setPrompt(r.prompt);
      setNote(r.note || '');
    }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setDrafting(false); }
  };

  const watch = async (jobId: string) => {
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, i < 5 ? 2000 : 4000));
      const j = await getHeroJob(jobId).catch(() => null);
      if (!j) continue;
      setJobs((prev) => prev.map((p) => (p.id === jobId ? { ...p, ...j, id: jobId } : p)));
      if (j.status === 'completed' && j.ready) return;
      if (j.status === 'failed' || j.status === 'nsfw' || j.status === 'canceled') {
        throw new Error(j.status === 'nsfw'
          ? 'Higgsfield rejected that prompt as unsafe — reword it and try again.'
          : j.error || `Generation ${j.status}.`);
      }
    }
    throw new Error('The image is taking unusually long — check back shortly.');
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setErr('');
    try {
      const r = await startHeroImage({ prompt });
      setJobs((prev) => [{ id: r.jobId, status: 'queued', error: '', ready: false, cdnUrl: '', prompt, attempt: r.attempt }, ...prev]);
      await watch(r.jobId);
      qc.invalidateQueries({ queryKey: ['heroUsage'] });
      qc.invalidateQueries({ queryKey: ['heroLibrary'] });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  /* Accepting publishes to the client's CDN folder, which is what makes the URL
     usable anywhere. Optimised in the browser first, exactly as an article hero
     is — same WebP pass every upload in the app goes through. */
  const accept = async (job: HeroJob) => {
    setBusy(true); setErr('');
    try {
      const url = await heroImageUrl(job.id);
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `image-${job.id.slice(0, 8)}.jpg`, { type: blob.type || 'image/jpeg' });
      const optimized = await processImage(file).catch(() => file);
      const r = await publishHeroImage(job.id, optimized, subject || 'image', { prefix: 'image' });
      setPublished((p) => ({ ...p, [job.id]: r.url }));
      qc.invalidateQueries({ queryKey: ['heroLibrary'] });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const drop = async (id: string) => {
    setJobs((prev) => prev.filter((p) => p.id !== id));
    await deleteHeroImage(id).catch(() => {});
    qc.invalidateQueries({ queryKey: ['heroLibrary'] });
  };

  if (usage && !usage.configured) {
    return <p className="gen-hint" style={{ padding: '0 20px 18px' }}>
      <Icon name="info" size={13} /> Image generation isn&rsquo;t configured on this Worker yet.
    </p>;
  }

  return (
    <div style={{ padding: '0 20px 18px' }}>
      <p className="gen-hint" style={{ padding: '0 0 12px' }}>
        A picture for anything &mdash; a site page, a social post, an ad. It uses this client&rsquo;s
        {' '}<strong>regional look</strong> (Setup) and the same editorial rules as article images: no people,
        vehicles, logos or text. Accepted images are published to the client&rsquo;s CDN folder and kept below for reuse.
      </p>

      <div className="es-fields">
        <div>
          <span className="es-label">What do you want a picture of?</span>
          <Input placeholder="e.g. a newly shingled roof valley after snowfall" value={subject}
            onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div>
          <span className="es-label">Direction <span style={{ color: 'var(--text-subtle)', fontWeight: 400 }}>(optional)</span></span>
          <Input placeholder="e.g. close-up, overcast, winter" value={extra} onChange={(e) => setExtra(e.target.value)} />
        </div>
      </div>

      <div className="vault-add__actions" style={{ marginTop: 10 }}>
        <Button variant="outline" size="sm" disabled={drafting || !subject.trim()} onClick={draft}
          iconLeft={<Icon name={drafting ? 'refresh-cw' : 'sparkles'} size={14} />}>
          {drafting ? 'Writing prompt…' : prompt ? 'Rewrite prompt' : 'Draft prompt'}
        </Button>
        {/* Off by default and never offered for an article hero: a person on a
            client's blog reads as that client's own crew or customer. */}
        <label className="gen-heroopt">
          <input type="checkbox" checked={allowPeople} onChange={(e) => setAllowPeople(e.target.checked)} />
          <span>Allow people <small>faces stay indistinct; no logos or uniforms</small></span>
        </label>
        {usage && <span className="gen-hint">{usage.used}/{usage.cap} images this month</span>}
      </div>
      {note && (
        <p className="gen-note" style={{ marginTop: 8 }}>
          <Icon name="alert-circle" size={13} /> {note}
        </p>
      )}

      {prompt && (
        <>
          <div style={{ marginTop: 12 }}>
            <Textarea label="Prompt (edit before generating)" rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
          </div>
          <div className="vault-add__actions">
            <Button variant="primary" size="sm" disabled={busy || atCap} onClick={generate}
              iconLeft={<Icon name={busy ? 'refresh-cw' : 'image'} size={14} />}>
              {busy ? 'Generating…' : jobs.length ? 'Generate another' : 'Generate image'}
            </Button>
          </div>
        </>
      )}
      {atCap && <p className="vault-err"><Icon name="alert-circle" size={13} /> This client has used all {usage?.cap} AI images this month. The limit resets on the 1st.</p>}
      {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}

      {jobs.length > 0 && (
        <div className="gen-heroes">
          {jobs.map((j) => (
            <div className="gen-hero" key={j.id}>
              <div className="gen-hero__img">
                {j.ready ? <HeroThumb id={j.id} prompt={j.prompt} /> : <span className="gen-hero__ph">{j.status === 'failed' || j.status === 'nsfw' ? j.status : 'Generating…'}</span>}
              </div>
              <div className="gen-hero__meta">
                {published[j.id]
                  ? <ImageActions url={published[j.id]} />
                  : (
                    <div className="vault-add__actions">
                      <Button variant="outline" size="sm" disabled={busy || !j.ready} onClick={() => accept(j)}>Accept &amp; publish</Button>
                      <IconBtn label="Discard" icon="trash" onClick={() => drop(j.id)} />
                    </div>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!!library?.images.length && (
        <>
          <div className="gen-out__bar" style={{ marginTop: 20 }}>
            <span>Generated images for this client ({library.images.length})</span>
          </div>
          <div className="gen-heroes">
            {library.images.map((im) => (
              <div className="gen-hero" key={im.id}>
                <div className="gen-hero__img">
                  {im.cdnUrl ? <CdnThumb url={im.cdnUrl} prompt={im.prompt} /> : <HeroThumb id={im.id} prompt={im.prompt} />}
                </div>
                <div className="gen-hero__meta">
                  <span className="gen-hero__when">{im.createdAt.slice(0, 10)}</span>
                  {im.cdnUrl
                    ? <ImageActions url={im.cdnUrl} />
                    : <span className="gen-hero__ph">Not published — accept it above to get a URL</span>}
                  <details className="vault-raw"><summary>Prompt</summary><pre>{im.prompt}</pre></details>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* Copy the URL or save the file. The download goes through a blob so the browser
   saves it rather than navigating to the CDN and leaving the page. */
function ImageActions({ url }: { url: string }) {
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const blob = await (await fetch(url)).blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = url.split('/').pop() || 'image.webp';
      a.click();
      URL.revokeObjectURL(href);
    } finally { setSaving(false); }
  };
  return (
    <div className="vault-add__actions">
      <CopyBtn text={url} label="Copy URL" />
      <Button variant="ghost" size="sm" disabled={saving} onClick={save} iconLeft={<Icon name="download" size={13} />}>
        {saving ? 'Saving…' : 'Download'}
      </Button>
    </div>
  );
}

function HeroImagePanel({ articleId, topic, article, slug, autoKey, platform, onApply }: {
  articleId: string; topic: string;
  /** The piece itself — what the image must actually be about. */
  article: string;
  slug: string;
  /** Delivery target — decides the image format. */
  platform: ContentPlatform;
  /* Bumped when an article finishes generating with "Add a hero image" ticked.
     The checkbox is set BEFORE writing, so the image has to start itself once
     there is an article to illustrate. */
  autoKey: number;
  onApply: (url: string, alt: string) => void;
}) {
  const [open, setOpen] = useState(autoKey > 0);
  const [prompt, setPrompt] = useState('');
  const [extra, setExtra] = useState('');
  const [alt, setAlt] = useState('');
  const [jobs, setJobs] = useState<HeroJob[]>([]);
  const [activeId, setActiveId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [applied, setApplied] = useState('');
  const { data: usage } = useQuery({ queryKey: ['heroUsage'], queryFn: getHeroUsage, staleTime: 60_000 });
  const qc = useQueryClient();

  // Draft the prompt from the topic the moment the panel opens.
  useEffect(() => {
    if ((!open && !autoKey) || prompt) return;
    buildHeroPromptApi({ topic, extra, article }).then((r) => setPrompt(r.prompt)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoKey]);

  useEffect(() => {
    if (!open || !articleId) return;
    getHeroJobsForArticle(articleId).then((r) => setJobs(r.jobs)).catch(() => {});
  }, [open, articleId]);

  const refreshPrompt = async () => {
    setErr('');
    try {
      const r = await buildHeroPromptApi({ topic, extra, article });
      setPrompt(r.prompt); setErr(r.note || '');
    }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  /* Poll until terminal. The browser polls rather than the Worker holding a
     request open for the length of a generation. */
  const watch = async (jobId: string) => {
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, i < 5 ? 2000 : 4000));
      const j = await getHeroJob(jobId).catch(() => null);
      if (!j) continue;
      setJobs((prev) => prev.map((p) => (p.id === jobId ? { ...p, ...j, id: jobId } : p)));
      if (j.status === 'completed' && j.ready) return j;
      if (j.status === 'failed' || j.status === 'nsfw' || j.status === 'canceled') {
        throw new Error(j.status === 'nsfw'
          ? 'Higgsfield rejected that prompt as unsafe — reword it and try again.'
          : j.error || `Generation ${j.status}.`);
      }
    }
    throw new Error('The image is taking unusually long — check back shortly.');
  };

  /* Fires once per bump, and only once the prompt is ready — generating before
     the topic-grounded prompt arrives would spend a credit on a generic image. */
  const fired = useRef(0);
  useEffect(() => {
    if (!autoKey || fired.current === autoKey || !prompt.trim() || busy) return;
    fired.current = autoKey;
    setOpen(true);
    void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoKey, prompt]);

  const generate = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setErr('');
    try {
      const r = await startHeroImage({ prompt, articleId: articleId || undefined });
      const stub: HeroJob = { id: r.jobId, status: 'queued', error: '', ready: false, cdnUrl: '', prompt, attempt: r.attempt };
      setJobs((prev) => [stub, ...prev]);
      setActiveId(r.jobId);
      await watch(r.jobId);
      qc.invalidateQueries({ queryKey: ['heroUsage'] });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const use = async (job: HeroJob) => {
    setBusy(true); setErr('');
    try {
      /* Same-origin fetch of the staged original, then the SAME processImage()
         every Studio upload runs: WebP, downscaled, metadata stripped. A
         cross-origin image would taint the canvas and this would throw. */
      const url = await heroImageUrl(job.id);
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `hero-${job.id.slice(0, 8)}.jpg`, { type: blob.type || 'image/jpeg' });
      /* JPEG for DNN. EasyDNNnews downloads the article image and re-processes
         it server-side with .NET imaging, which cannot read WebP — the article
         pushes fine and the image silently never appears. */
      const mime = platform === 'dnn' ? 'image/jpeg' : undefined;
      const optimized = await processImage(file, mime ? { mime } : undefined).catch(() => file);
      const r = await publishHeroImage(job.id, optimized, slug || 'post',
        articleId ? { articleId, alt: alt.trim() } : undefined);
      onApply(r.url, alt.trim());
      setApplied(r.url);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const discard = async (job: HeroJob) => {
    setJobs((prev) => prev.filter((p) => p.id !== job.id));
    await deleteHeroImage(job.id).catch(() => {});
  };

  if (usage && !usage.configured) return null;
  const atCap = !!usage && usage.used >= usage.cap;

  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> AI hero image
        {applied
          ? <Badge variant="success">Added to the article</Badge>
          : <Badge variant="neutral">{usage ? `${usage.used}/${usage.cap} this month` : 'Optional'}</Badge>}
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="vault-add__hint">
            An <strong>illustrative</strong> header image for this article &mdash; never a photo of the client&rsquo;s own work, and never people, vehicles, logos or text. Real project photography stays in Expert Studio.
          </p>
          {atCap && <p className="vault-err"><Icon name="alert-circle" size={13} /> This client has used all {usage?.cap} AI images this month. The limit resets on the 1st.</p>}

          <Textarea label="Prompt (edit before generating)" rows={7} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
          <div className="vault-add__row">
            <Input placeholder="Extra direction, e.g. winter, close-up of flashing" value={extra} onChange={(e) => setExtra(e.target.value)} />
            <Button variant="ghost" size="sm" onClick={refreshPrompt} iconLeft={<Icon name="refresh-cw" size={14} />}>Rebuild prompt</Button>
          </div>
          <Input label="Alt text" placeholder="Describe the image for screen readers and SEO" value={alt} onChange={(e) => setAlt(e.target.value)} />
          <div className="vault-add__actions">
            <Button variant="primary" size="sm" disabled={busy || atCap || !prompt.trim()} onClick={generate}
              iconLeft={<Icon name={busy ? 'refresh-cw' : 'sparkles'} size={14} />}>
              {busy ? 'Generating…' : jobs.length ? 'Generate another' : 'Generate image'}
            </Button>
          </div>
          {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}

          {jobs.length > 0 && (
            <div className="gen-heroes">
              {jobs.map((j) => (
                <div className={`gen-hero${activeId === j.id ? ' is-on' : ''}`} key={j.id}>
                  <div className="gen-hero__img">
                    {j.ready ? <HeroThumb id={j.id} prompt={j.prompt} /> : <span className="gen-hero__ph">{j.status === 'failed' || j.status === 'nsfw' ? j.status : 'Generating…'}</span>}
                  </div>
                  <div className="gen-hero__meta">
                    <strong>Attempt {j.attempt}</strong>
                    {j.cdnUrl && <span className="gen-hero__live"><Icon name="check-circle" size={12} /> published</span>}
                    <div className="vault-add__actions">
                      <Button variant="outline" size="sm" disabled={busy || !j.ready} onClick={() => use(j)}>Use this image</Button>
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => { setPrompt(j.prompt); setOpen(true); }}>Edit its prompt</Button>
                      <IconBtn label="Discard" icon="trash" onClick={() => discard(j)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HeroThumb({ id, prompt }: { id: string; prompt?: string }) {
  const url = useHeroSrc(id);
  const [big, setBig] = useState(false);
  if (!url) return <span className="gen-hero__ph">Loading…</span>;
  return (
    <>
      <img src={url} alt="Generated image" className="gen-hero__zoom" title="Click to view full size" onClick={() => setBig(true)} />
      {big && <Lightbox src={url} prompt={prompt} onClose={() => setBig(false)} />}
    </>
  );
}

/** A published image, straight from the CDN. Same click-to-inspect behaviour. */
function CdnThumb({ url, prompt }: { url: string; prompt?: string }) {
  const [big, setBig] = useState(false);
  return (
    <>
      <img src={url} alt="Generated image" className="gen-hero__zoom" title="Click to view full size" onClick={() => setBig(true)} />
      {big && <Lightbox src={url} prompt={prompt} onClose={() => setBig(false)} />}
    </>
  );
}

function GeneratorPanel({ briefsPanel }: { briefsPanel?: ReactNode }) {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const { data: ctx } = useQuery({ queryKey: ['generateContext'], queryFn: getGenerateContext, enabled: isAdmin });
  const [tab, setTab] = useState<'setup' | 'case_study' | 'blog' | 'prompt' | 'rewrite' | 'web_handoff' | 'site_page' | 'image'>('case_study');
  const [storyId, setStoryId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [promptFormat, setPromptFormat] = useState<'auto' | 'markdown' | 'html' | 'plain'>('auto');
  const [rewriteText, setRewriteText] = useState('');
  const [rewriteFormat, setRewriteFormat] = useState<'auto' | 'markdown' | 'html' | 'plain'>('auto');
  const [rewriteInstruction, setRewriteInstruction] = useState('');
  const [rewriteStrength, setRewriteStrength] = useState<'light' | 'moderate' | 'heavy'>('moderate');
  const [factCheck, setFactCheck] = useState(false);
  const [blogTopic, setBlogTopic] = useState('');
  const [blogQs, setBlogQs] = useState<string[]>(['', '', '', '', '']);
  const [blogSourceIds, setBlogSourceIds] = useState<string[]>([]); // interviews weighting the current blog write
  /* The interview or holding record this blog FULFILS (a Knowledge Session, or
     a gap-answer topic). Saving the blog archives it, the same way a case
     study archives its story; the hero picker reads its photos. */
  const [blogStoryId, setBlogStoryId] = useState('');
  /* The page an approved topic supports and its keywords — carried from the
     queue into the write so the post links to its pillar and uses the terms. */
  const [blogSupport, setBlogSupport] = useState<BlogSupport | null>(null);
  /* Writer + audit are saved settings with the toggle right here: the
     original prompt stays one click away, and the audit is off by default. */
  const { data: blogSettings } = useQuery({ queryKey: ['blogSettings'], queryFn: getBlogSettings, enabled: isAdmin });
  const [writerChoice, setWriterChoice] = useState<BlogWriter | null>(null);
  const [auditChoice, setAuditChoice] = useState<boolean | null>(null);
  const writer: BlogWriter = writerChoice ?? blogSettings?.writer ?? 'evidence';
  const auditOn: boolean = auditChoice ?? blogSettings?.audit ?? false;
  const saveBlogSetting = async (patch: Partial<BlogSettings>) => {
    if (patch.writer) setWriterChoice(patch.writer);
    if (patch.audit !== undefined) setAuditChoice(patch.audit);
    try { await setBlogSettings(patch); qc.invalidateQueries({ queryKey: ['blogSettings'] }); } catch { /* the choice still applies to this session */ }
  };
  const [auditing, setAuditing] = useState(false);
  const [revising, setRevising] = useState(false);
  const [blogNotes, setBlogNotes] = useState(''); // client-submitted background riding a queued topic (Expert Studio blog submissions)
  const [blogGaps, setBlogGaps] = useState<string[]>([]); // vault gaps for the suggested topic
  /* Runners-up from a suggestion, each already scored — switching to one costs
     nothing, where re-rolling the whole suggestion costs another round of calls. */
  const [altTopics, setAltTopics] = useState<SuggestedTopic[]>([]);
  const [blogOrigin, setBlogOrigin] = useState<BlogTopicOrigin>('own'); // how the current topic entered the box
  const [coverage, setCoverage] = useState<TopicCoverage | null>(null);
  const version = useGeneratorVersion();
  const [covSending, setCovSending] = useState(false);
  /* Which coverage result was sent, and the queue row it became. Identity on
     the result does the resetting: a fresh check, an edited topic or a cleared
     card all produce a different object, so the badge cannot outlive the
     result it describes. The id is what lets the card show the row's real
     status afterwards — polled while it is still pending, so an approval on
     the board reaches the sender without a refresh. */
  const [covSent, setCovSent] = useState<{ for: TopicCoverage; id: string } | null>(null);
  const sentIsLive = !!covSent && covSent.for === coverage;
  const { data: queueForSent } = useQuery({
    queryKey: ['blogQueue'], queryFn: getBlogQueue, enabled: sentIsLive,
    refetchInterval: (q) => (sentIsLive && q.state.data?.items.find((i) => i.id === covSent?.id)?.status === 'pending' ? 30_000 : false),
  });
  const sentRow = sentIsLive ? (queueForSent?.items.find((i) => i.id === covSent!.id) ?? null) : null;
  const [checking, setChecking] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [handoffPage, setHandoffPage] = useState('');
  /* Site page: the sitemap route to write, plus the layout skeleton the file must
     match. `sitePageLayout` empty = let the server resolve it (repo → saved →
     default); non-empty = this exact layout wins for this run. */
  const [sitePage, setSitePage] = useState('');
  const [sitePageType, setSitePageType] = useState<SitePageType | ''>('');
  const [sitePageNotes, setSitePageNotes] = useState('');
  /* Who signs this page. Stamped into the front matter after generation, and
     only offered for people who already have an author page to link to. */
  const [sitePageExpert, setSitePageExpert] = useState('');
  const [sitePageLayout, setSitePageLayout] = useState('');
  /* Layout only (default) writes the page fresh from the Vault — the file's own
     text is stripped before the model sees it. Rewrite keeps the page's facts. */
  const [sitePageLayoutUse, setSitePageLayoutUse] = useState<LayoutUse>('layout_only');
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [layoutInfo, setLayoutInfo] = useState<{ source: LayoutSource; repoPath: string; type: SitePageType } | null>(null);
  const [layoutBusy, setLayoutBusy] = useState(false);
  const [layoutSaved, setLayoutSaved] = useState(false);
  const [pushPath, setPushPath] = useState('');
  const [pushBlocked, setPushBlocked] = useState(''); // lint message from a refused site-page push
  /* Front-matter state for the editable site-page file: the first remaining
     problem (with its line), what the last auto-fix changed, and a busy flag. */
  const [fileLint, setFileLint] = useState('');
  const [fileRepairs, setFileRepairs] = useState<string[]>([]);
  const [fileChecking, setFileChecking] = useState(false);
  /* Route→file inference covers the Astro conventions; a repo that names its
     pages differently still needs a way to pick the layout file by hand. */
  const { data: repoFiles } = useQuery({
    queryKey: ['sitePageRepoFiles'], queryFn: getSitePageRepoFiles, enabled: isAdmin && tab === 'site_page',
  });
  const { data: savedLayouts } = useQuery({
    queryKey: ['sitePageTemplates'], queryFn: getSitePageTemplates, enabled: isAdmin && tab === 'site_page',
  });
  // Shares GithubEditor's cache key — the push confirmation names the real branch.
  const { data: ghCfg } = useQuery({ queryKey: ['githubConfig'], queryFn: getGithubConfig, enabled: isAdmin && tab === 'site_page' });
  const githubBranchLabel = ghCfg?.repo ? `${ghCfg.repo}@${ghCfg.branch}` : 'the client repo';
  /* The client's site decides the output format — Astro repos take Markdown, DNN
     takes HTML — so this isn't a per-run choice. The picker only reappears for
     clients whose delivery platform hasn't been set yet. */
  const platform = useContentPlatform(isAdmin);
  const [formatChoice, setFormatChoice] = useState<OutputFormat>('astro_md');
  const format: OutputFormat = platform ? PLATFORM_FORMAT[platform] : formatChoice;
  const [mode, setMode] = useState<GroundingMode>('polished');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<CaseStudyResult | null>(null);
  const [files, setFiles] = useState<HandoffFile[] | null>(null);
  const [note, setNote] = useState('');
  /* The accepted hero's CDN url. Substituted into the article markdown so the
     admin reviews what will actually be pushed, and handed to DNN as its
     ArticleImage where that is the delivery target. */
  const [heroUrl, setHeroUrl] = useState('');
  const [heroAlt, setHeroAlt] = useState('');
  /* Ticked BEFORE writing, like every other generation option. The article has to
     exist before an image can illustrate it, so the checkbox bumps heroAutoKey on
     success and the panel starts itself. */
  const [heroWanted, setHeroWanted] = useState(false);
  const [heroAutoKey, setHeroAutoKey] = useState(0);
  const { data: heroUsage } = useQuery({ queryKey: ['heroUsage'], queryFn: getHeroUsage, enabled: isAdmin });
  const [publishing, setPublishing] = useState(false);
  const [savedAs, setSavedAs] = useState<'' | 'draft' | 'published'>('');
  const [resultStoryId, setResultStoryId] = useState('');
  /* Which project of the story the result is about, and how many the story has:
     the story is archived only when every one has its case study. */
  const [resultProject, setResultProject] = useState<{ name: string; count: number }>({ name: '', count: 1 });
  // After a save: how far through the story's projects that got, for the line under the result.
  const [savedProgress, setSavedProgress] = useState<{ done: number; count: number } | null>(null);

  // Deep link from the article viewer's "Generate site version" button:
  // /content?genStory=<articleId> lands here with that story preselected on the
  // Case study tab and the generator scrolled into view. Two effects because on
  // a fresh page load this panel renders null until the Clerk admin check
  // resolves — a scroll fired on mount hits nothing. The second effect waits
  // for isAdmin, when the panel (and the ref) actually exist.
  const genHeadRef = useRef<HTMLDivElement>(null);
  const [genFlash, setGenFlash] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const story = params.get('genStory');
    /* ?genTopic=<articleId> is the blog handoff: an article the client answered
       gap questions for has a topic and a brief but NO interview, so sending it
       down the case-study path made the writer fall back to the vault and
       produce an unrelated case study. */
    const topicId = params.get('genTopic');
    /* ?genBlog=<articleId>: an interview written as a BLOG rather than a case
       study — a Knowledge Session by default, any interview by choice. The
       transcript becomes the primary source; the session's own topic, audience
       and notes seed the composer. */
    const blogId = params.get('genBlog');
    /* ?genQueue=<blogTopicId>: the "Write this blog" link in the topic-approved
       email. Loads the queued topic the way the queue's own Use button does. */
    const queueId = params.get('genQueue');
    if (!story && !topicId && !blogId && !queueId) return;
    if (queueId) {
      setTab('blog');
      getBlogQueue().then(({ items }) => {
        const t = items.find((i) => i.id === queueId);
        if (t) useQueued(t.topic, t.questions, t.sourceIds, [], t.notes, t.supportUrl ? { url: t.supportUrl, anchor: t.supportAnchor, keywords: t.keywords } : null);
      }).catch(() => {});
    } else if (blogId) {
      setTab('blog');
      getArticle(blogId).then(({ article }) => {
        setBlogTopic(article.title);
        setBlogQs(['', '', '', '', '']);
        setBlogNotes([article.studioNotes || '', article.studioAudience ? `Intended audience: ${article.studioAudience}` : ''].filter(Boolean).join('\n'));
        setBlogOrigin('interview');
        setBlogSourceIds([blogId]);
        setBlogStoryId(blogId);
      }).catch(() => {});
    } else if (topicId) {
      setTab('blog');
      getArticle(topicId).then(({ article }) => {
        setBlogTopic(article.title);
        /* Prefer the brief when one was drafted; otherwise the questions the
           client was actually asked, which are the substance of the piece. */
        const qs = (article.gapQuestions?.length ? article.gapQuestions : article.gapAsked) ?? [];
        setBlogQs([0, 1, 2, 3, 4].map((i) => qs[i] || ''));
        setBlogOrigin('own');
        /* The client's answers are a vault source; emphasising it keeps them
           at the front of a large vault. Linking the topic record means the
           saved blog archives it, so no near-duplicate draft lingers. */
        setBlogSourceIds(article.gapSourceId ? [article.gapSourceId] : []);
        setBlogStoryId(topicId);
      }).catch(() => {});
    } else if (story) {
      setTab('case_study');
      setStoryId(story);
    }
    setGenFlash(true);
    // Clean the param so a refresh doesn't re-trigger the jump.
    window.history.replaceState(null, '', window.location.pathname);
  }, []);
  useEffect(() => {
    if (!genFlash || !isAdmin) return;
    const scroll = setTimeout(() => genHeadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
    const clear = setTimeout(() => setGenFlash(false), 3500);
    return () => { clearTimeout(scroll); clearTimeout(clear); };
  }, [genFlash, isAdmin]);
  const [pushing, setPushing] = useState(false);
  const [pushedUrl, setPushedUrl] = useState('');
  const [dnnBusy, setDnnBusy] = useState(false);
  const [dnnUrl, setDnnUrl] = useState('');
  // Publish date — drives the tracker month AND the pushed post's date. Today by default.
  const [publishDate, setPublishDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dnnDraft, setDnnDraft] = useState(false);
  /** e.g. DNN cannot attach an image when updating an existing article. */
  const [dnnNote, setDnnNote] = useState('');
  const [dnnDupe, setDnnDupe] = useState<{ draft: boolean; message: string } | null>(null);
  const dnnCats = useDnnCategories(isAdmin, tab === 'case_study' ? 'case_study' : 'blog');
  // The story's first published photo becomes the DNN ArticleImage — the module
  // downloads, resizes and builds a one-image gallery from it.
  const { data: dnnHero } = useQuery({
    queryKey: ['dnnHero', resultStoryId, projectName],
    queryFn: () => getDnnHero({ storyId: resultStoryId, project: projectName || undefined }),
    enabled: isAdmin && !!resultStoryId,
  });
  const { data: storyProjects } = useQuery({ queryKey: ['storyProjects', storyId], queryFn: () => getStoryProjects(storyId), enabled: isAdmin && !!storyId });
  /* The next job left to write is picked for you: "strongest (auto)" would
     write the one already done again. Only when nothing is picked yet. */
  useEffect(() => {
    const projects = storyProjects?.projects ?? [];
    if (projects.length < 2 || projectName) return;
    const done = new Set((stories.find((s) => s.id === storyId)?.written ?? []).map((n) => n.trim().toLowerCase()));
    const next = projects.find((p) => !done.has(p.name.trim().toLowerCase()));
    if (next) setProjectName(next.name);
  }, [storyProjects, storyId]);

  if (!isAdmin) return null;

  const onSave = async (stage: 1 | 3) => {
    if (!result) return;
    setPublishing(true);
    try {
      const title = result.title
        || result.output.match(/^title:\s*["']?(.+?)["']?\s*$/m)?.[1]?.trim()
        || (tab === 'blog' ? 'Blog article' : 'Case study');
      const label = `${tab === 'blog' ? 'Blog' : 'Case study'} · ${result.format === 'html' ? 'HTML' : 'Astro'}`;
      // A case study links its interview; a blog written from an answered topic links that topic record. Either is archived on save.
      const sourceStoryId = resultStoryId || undefined;
      const kind = tab === 'blog' ? 'blog' : 'case_study';
      /* Carries the accepted hero. For Astro it is already inside the body; an
         HTML/DNN article has nowhere to put it, and either way the saved article
         should own its lead image rather than it living in this component. */
      const project = kind === 'case_study' ? resultProject : { name: '', count: 1 };
      await publishGenerated({ title, summary: result.summary, body: result.output, label, stage, sourceStoryId, kind, format: result.format, publishDate, heroUrl: heroUrl || undefined, heroAlt: heroAlt || undefined, siteUrl: pushedUrl || undefined, metaTitle: result.metaTitle, metaDescription: result.metaDescription,
        sourceProject: project.name || undefined, projectCount: project.count });
      setSavedAs(stage === 3 ? 'published' : 'draft');
      if (sourceStoryId && project.count > 1) {
        const before = stories.find((s) => s.id === sourceStoryId)?.written ?? [];
        const done = new Set([...before, project.name].map((n) => n.trim().toLowerCase()).filter(Boolean)).size;
        setSavedProgress({ done, count: project.count });
      } else setSavedProgress(null);
      qc.invalidateQueries({ queryKey: qk.articles });
      qc.invalidateQueries({ queryKey: ['generateContext'] }); // refresh picker (source story archived)
    } finally { setPublishing(false); }
  };
  const onPush = async (draft: boolean) => {
    if (!result) return;
    setPushing(true); setErr('');
    try {
      const r = await pushToSite({ body: result.output, kind: tab === 'case_study' ? 'case_study' : 'blog', draft, publishDate, articleId: resultStoryId || undefined });
      setPushedUrl(r.url);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setPushing(false); }
  };

  const stories = ctx?.stories ?? [];
  const pages = ctx?.pages ?? [];
  /* The page's subject, for a vault check: its sitemap anchor and focus
     keywords when it has them, else the last route segment read as words.
     The route itself is what the page-FAQ sender keys on. */
  const { data: sitemapMeta } = useQuery({ queryKey: ['sitemapPages'], queryFn: getSitemapPages, enabled: version === 'v2' && tab === 'site_page' });
  const sitePageRoute = (() => { try { const u = new URL(sitePage); return u.pathname.replace(/\/+$/, '').replace(/^\/*/, '/') + '/'; } catch { return ''; } })();
  const sitePageSubject = (() => {
    if (!sitePage) return '';
    const norm = (u: string) => u.replace(/\/+$/, '').toLowerCase();
    const focus = sitemapMeta?.focus.find((f) => norm(f.url) === norm(sitePage));
    const entry = sitemapMeta?.pages.find((f) => norm(f.url) === norm(sitePage));
    const seg = sitePageRoute.split('/').filter(Boolean).pop() || '';
    const words = seg.replace(/[-_]+/g, ' ').trim();
    const name = focus?.anchor || entry?.anchor || words;
    return focus?.keywords ? `${name} — ${focus.keywords}` : name;
  })();
  const ready = ctx?.ready ?? false;
  const pagePath = (u: string) => u.replace(/^https?:\/\/[^/]+/i, '').replace(/\/$/, '') || '/ (home)';

  const onPushDnn = async (draft: boolean, allowDuplicate = false) => {
    if (!result) return;
    setDnnBusy(true); setErr(''); setDnnDupe(null);
    try {
      const r = await pushToDnn({
        title: result.title || result.metaTitle || 'Article', summary: result.summary, html: result.output,
        metaTitle: result.metaTitle, metaDescription: result.metaDescription, draft,
        kind: tab === 'case_study' ? 'case_study' : 'blog',
        categoryIds: dnnCats.selected,
        moduleId: dnnCats.moduleId || undefined,
        imageUrl: heroUrl || dnnHero?.imageUrl || undefined,
        storyId: resultStoryId || undefined, project: projectName || undefined,
        allowDuplicate,
        publishDate,
      });
      setDnnUrl(r.url); setDnnDraft(r.draft); setDnnNote(r.note || '');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // The title already exists on the site — offer the deliberate override
      // instead of a dead end, since DNN can't update in place.
      if (msg.includes(DNN_TITLE_EXISTS)) setDnnDupe({ draft, message: msg });
      else setErr(msg);
    }
    finally { setDnnBusy(false); }
  };

  const runCaseStudy = async () => {
    setBusy(true); setErr(''); setResult(null); setFiles(null); setSavedAs(''); setPushedUrl(''); setDnnUrl(''); setDnnDupe(null); setResultStoryId(storyId);
    setResultProject({ name: projectName, count: storyId ? Math.max(1, storyProjects?.projects.length ?? 1) : 1 });
    try {
      setResult(await generateCaseStudy({ storyId: storyId || undefined, projectName: projectName || undefined, format, mode }));
      if (heroWanted) setHeroAutoKey((n) => n + 1);
    }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const runHandoff = async () => {
    setBusy(true); setErr(''); setResult(null); setFiles(null); setNote('');
    /* A full site comes back one batch per request — the whole thing is many
       minutes of output, which no single request survives — so loop on `next`
       and show the pages as they land. */
    try {
      const all: HandoffFile[] = [];
      const notes: string[] = [];
      let offset = 0;
      for (let round = 0; round < 200; round++) {
        const r = await generateWebHandoff({ mode, page: handoffPage || undefined, offset });
        all.push(...r.files);
        if (r.note) notes.push(r.note);
        setFiles([...all]);
        if (r.next == null) break;
        offset = r.next;
        const total = r.total ?? 0;
        setNote(`${all.length} file${all.length === 1 ? '' : 's'} so far — working on pages ${offset + 1}–${Math.min(offset + 5, total)} of ${total}…`);
      }
      setNote([...new Set(notes)].join(' '));
    }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const runSitePage = async () => {
    setBusy(true); setErr(''); setResult(null); setFiles(null); setNote(''); setPushedUrl(''); setPushBlocked('');
    try {
      const r = await generateSitePage({
        page: sitePage, mode,
        layout: sitePageLayout.trim() || undefined,
        // Keeps a fetched-then-edited layout pushing back to the file it came from.
        layoutPath: sitePageLayout.trim() ? layoutInfo?.repoPath || undefined : undefined,
        pageType: sitePageType || undefined,
        notes: sitePageNotes.trim() || undefined,
        layoutUse: sitePageLayoutUse,
        expertId: sitePageExpert || undefined,
      });
      setFiles(r.files); setNote(r.note || ''); setPushPath(r.pushPath);
      setFileLint(r.lint || ''); setFileRepairs(r.repairs || []);
      setLayoutInfo({ source: r.layoutSource, repoPath: r.repoPath || '', type: r.pageType });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  /* Edits in the file editor land here; the lint is stale until re-checked. */
  const editFile = (content: string) => {
    setFiles((cur) => (cur ? [{ ...cur[0], content }, ...cur.slice(1)] : cur));
    setPushedUrl(''); setPushBlocked('');
  };
  /* Auto-fix what can be fixed, adopt the repaired text, and show what remains. */
  const checkFile = async () => {
    if (!files?.[0]) return;
    setFileChecking(true); setErr(''); setPushBlocked('');
    try {
      const r = await checkSitePage(files[0].content);
      setFiles((cur) => (cur ? [{ ...cur[0], content: r.text }, ...cur.slice(1)] : cur));
      setFileLint(r.lint); setFileRepairs(r.repairs);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setFileChecking(false); }
  };
  /* Pull the layout this page would use into the editor, so it can be checked (or
     corrected) before a generation is spent on it. */
  const loadLayout = async () => {
    if (!sitePage) return;
    setLayoutBusy(true); setErr('');
    try {
      const r = await getSitePageLayout(sitePage, sitePageType || undefined);
      setSitePageLayout(r.skeleton);
      setLayoutInfo({ source: r.source, repoPath: r.repoPath, type: r.type });
      setLayoutOpen(true);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLayoutBusy(false); }
  };
  /* Save the layout in the editor as this client's default for the page type.
     The type comes from the server's own inference (via the layout fetch or the
     last generation) so the rule lives in one place, not two. */
  const saveType: SitePageType | '' = sitePageType || layoutInfo?.type || '';
  const loadRepoFile = async (path: string) => {
    if (!path) return;
    setLayoutBusy(true); setErr('');
    try {
      const r = await getSitePageRepoFile(path);
      setSitePageLayout(r.content);
      setLayoutInfo((prev) => ({ source: 'pasted', repoPath: r.path, type: sitePageType || prev?.type || 'other' }));
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLayoutBusy(false); }
  };
  const saveLayoutAsDefault = async () => {
    if (!sitePageLayout.trim() || !saveType) return;
    setLayoutBusy(true); setErr('');
    try {
      await setSitePageTemplate(saveType, sitePageLayout);
      qc.invalidateQueries({ queryKey: ['sitePageTemplates'] });
      setLayoutSaved(true); setTimeout(() => setLayoutSaved(false), 1600);
    }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLayoutBusy(false); }
  };
  /* The server refuses (409 frontmatter_invalid) a site page whose front-matter
     would fail Astro's YAML parser; the message names the line. The operator can
     fix the text and push again, or push anyway with `force`. */
  const onPushSitePage = async (file: HandoffFile, force = false) => {
    setPushing(true); setErr(''); setPushBlocked('');
    try { const r = await pushToSite({ body: file.content, kind: 'site_page', draft: false, path: pushPath, force }); setPushedUrl(r.url); }
    catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('frontmatter_invalid')) {
        try {
          const j = JSON.parse(msg.slice(msg.indexOf('{'))) as { lint?: string; text?: string; repairs?: string[] };
          // The server already applied every mechanical fix — adopt that text so
          // the editor shows exactly the file the lint is talking about.
          if (j.text) setFiles((cur) => (cur ? [{ ...cur[0], content: j.text as string }, ...cur.slice(1)] : cur));
          setFileLint(j.lint || ''); setFileRepairs(j.repairs || []);
          setPushBlocked(j.lint || msg);
        } catch { setPushBlocked(msg); }
      } else setErr(msg);
    }
    finally { setPushing(false); }
  };
  const generateBlogWith = async (topic: string, questions: string[], sourceIds: string[] = [], notes = '', support: BlogSupport | null = blogSupport) => {
    setBusy(true); setErr(''); setResult(null); setFiles(null); setSavedAs(''); setPushedUrl(''); setDnnUrl(''); setDnnDupe(null); setResultStoryId(blogStoryId);
    try {
      setResult(await generateBlog({
        topic, questions, format, mode, sourceIds: sourceIds.length ? sourceIds : undefined, notes: notes.trim() || undefined,
        writer, audit: auditOn,
        supportUrl: support?.url || undefined, supportAnchor: support?.anchor || undefined, keywords: support?.keywords || undefined,
      }));
      if (heroWanted) setHeroAutoKey((n) => n + 1);
    }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const runBlog = () => generateBlogWith(blogTopic, blogQs, blogSourceIds, blogNotes);
  const savePending = async () => {
    if (!blogTopic.trim()) return;
    setBusy(true); setErr('');
    try {
      await saveBlogTopic({
        topic: blogTopic, questions: blogQs.filter((q) => q.trim()),
        sourceIds: blogSourceIds.length ? blogSourceIds : undefined,
        origin: blogSourceIds.length ? 'interview' : blogOrigin,
      });
      qc.invalidateQueries({ queryKey: ['blogQueue'] });
      setBlogTopic(''); setBlogQs(['', '', '', '', '']); setBlogSourceIds([]); setBlogStoryId(''); setBlogOrigin('own'); setCoverage(null);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const useAlternative = (alt: SuggestedTopic) => {
    setBlogTopic(alt.topic);
    setBlogQs([0, 1, 2, 3, 4].map((i) => alt.questions[i] || ''));
    setBlogGaps(alt.coverage?.gaps.length ? alt.coverage.gaps : alt.gaps);
    setCoverage(alt.coverage);
    setBlogOrigin('suggest'); setBlogSourceIds([]); setBlogStoryId(''); setErr('');
    setAltTopics((prev) => prev.filter((p) => p !== alt));
  };
  const useQueued = (t: string, q: string[], sourceIds: string[] = [], gaps: string[] = [], notes = '', support: BlogSupport | null = null) => { setBlogTopic(t); setBlogQs([0, 1, 2, 3, 4].map((i) => q[i] || '')); setBlogSourceIds(sourceIds); setBlogStoryId(''); setBlogNotes(notes); setBlogOrigin(sourceIds.length ? 'interview' : 'own'); setCoverage(null); setBlogGaps(gaps); setBlogSupport(support); };
  const suggest = async () => {
    setSuggesting(true); setErr(''); setCoverage(null);
    try {
      const r = await suggestBlogTopics();
      if (r.topic) setBlogTopic(r.topic);
      setBlogQs([0, 1, 2, 3, 4].map((i) => r.questions[i] || ''));
      setBlogGaps(r.gaps || []);
      /* The verdict is proven before the topic is offered, so it is shown
         straight away — checking a suggestion by hand was the work this button
         was supposed to save. */
      setCoverage(r.coverage);
      setAltTopics(r.alternatives || []);
      setBlogSourceIds([]); setBlogStoryId(''); setBlogOrigin('suggest'); // vault-wide suggestion isn't tied to a specific interview
      if (r.note) setErr(r.note);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setSuggesting(false); }
  };
  // Check whether the Vault can back a candidate topic (typed, or picked from an
  // AI suggestion). Read-only; shows the coverage card.
  const checkVault = async (topicArg?: string) => {
    const t = (topicArg ?? blogTopic).trim();
    if (!t) { setErr('Enter a topic to check.'); return; }
    setChecking(true); setErr(''); setCoverage(null);
    try {
      const cov = await assessTopicCoverage(t);
      setCoverage(cov);
      /* The check's `gaps` are the specific facts a writer would still need —
         exactly what to ask the client — but they were only displayed. They now
         feed the sender, so a thin verdict ends with something to DO about it
         rather than a diagnosis and a dead end. */
      setBlogGaps(cov.gaps || []);
    }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setChecking(false); }
  };
  /* What each inbox row does when picked. A queue row keeps its own outline
     and gets a vault check on top; a vault suggestion's coverage is already
     proven, so it loads whole; page and interview candidates load with the
     page or interviews behind them. All of these are the v1 handlers. */
  const inboxHandlers = {
    onPick: (title: string) => pickAiIdea(title),
    onUseQueued: (row: BlogQueueItem) => {
      const sup = row.supportUrl ? { url: row.supportUrl, anchor: row.supportAnchor, keywords: row.keywords } : null;
      useQueued(row.topic, row.questions, row.sourceIds, [], row.notes || '', sup); void checkVault(row.topic);
    },
    onGenerateNow: (row: BlogQueueItem) => {
      const sup = row.supportUrl ? { url: row.supportUrl, anchor: row.supportAnchor, keywords: row.keywords } : null;
      useQueued(row.topic, row.questions, row.sourceIds, [], row.notes || '', sup); void generateBlogWith(row.topic, row.questions, row.sourceIds, row.notes || '', sup);
    },
    onUseVault: (alt: SuggestedTopic) => useAlternative(alt),
    onUsePage: (c: InterviewTopicCandidate, page: { url: string; anchor: string; keywords: string }) => {
      useQueued(c.title, c.questions, [], c.gaps, '', { url: page.url, anchor: page.anchor, keywords: page.keywords }); void checkVault(c.title);
    },
    onUseMined: (c: InterviewTopicCandidate, ids: string[]) => { useQueued(c.title, c.questions, ids, c.gaps); void checkVault(c.title); },
  };
  const pickAiIdea = (title: string) => { setBlogTopic(title); setBlogQs(['', '', '', '', '']); setBlogSourceIds([]); setBlogStoryId(''); setBlogSupport(null); setBlogNotes(''); setBlogOrigin('ai-idea'); setBlogGaps([]); void checkVault(title); };
  /* For a blog the outline IS the five sub-topic boxes. A site page has no
     outline field — its writer reads the brief — so the headings go there,
     as "Cover: …", which the page prompt treats as instructions. */
  const useCoverageQuestions = (qs: string[]) => {
    if (tab === 'site_page') { setSitePageNotes((prev) => [prev.trim(), `Cover: ${qs.join('; ')}`].filter(Boolean).join('\n')); return; }
    setBlogQs([0, 1, 2, 3, 4].map((i) => qs[i] || ''));
  };
  /* The checked outline goes to the Optimization Manager as one topic on the
     same board that already carries the page-support batches — so a blog
     briefed here is reviewed, edited and approved exactly like every other
     one, rather than through a second path nobody is watching. */
  const sendCoverageForApproval = async (ticked: string[]) => {
    const t = blogTopic.trim();
    /* The edited outline wins over the raw ticks when it has been filled in:
       what the Optimization Manager approves must be what gets written, and
       the outline fields are what the writer reads. */
    const typed = blogQs.map((q) => q.trim()).filter(Boolean);
    const qs = typed.length ? typed : ticked;
    if (!t || !qs.length) return;
    setCovSending(true); setErr('');
    try {
      const r = await saveBlogTopic({ topic: t, questions: qs, origin: blogOrigin === 'ai-idea' ? 'ai-idea' : 'own', notify: true });
      qc.invalidateQueries({ queryKey: ['blogQueue'] });
      if (coverage) setCovSent({ for: coverage, id: r.id });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setCovSending(false); }
  };

  const runPrompt = async () => {
    setBusy(true); setErr(''); setResult(null); setFiles(null);
    try { setResult(await generatePrompt({ instruction: promptText, storyId: storyId || undefined, format: promptFormat, mode })); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const runRewrite = async () => {
    setBusy(true); setErr(''); setResult(null); setFiles(null); setSavedAs(''); setPushedUrl('');
    try { setResult(await rewriteContent({ content: rewriteText, format: rewriteFormat, mode, strength: rewriteStrength, instruction: rewriteInstruction.trim() || undefined, factCheck })); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const csExt = tab === 'prompt' ? (promptFormat === 'html' ? 'html' : promptFormat === 'plain' ? 'txt' : 'md')
    : tab === 'rewrite' ? (result?.format === 'html' ? 'html' : 'md')
      : (format === 'html' ? 'html' : 'md');
  const run = tab === 'case_study' ? runCaseStudy : tab === 'blog' ? runBlog : tab === 'prompt' ? runPrompt : tab === 'rewrite' ? runRewrite : tab === 'site_page' ? runSitePage : runHandoff;
  const blockGen = busy || (tab === 'prompt' ? !promptText.trim() : tab === 'rewrite' ? !rewriteText.trim() : !ready || (tab === 'blog' && !blogTopic.trim()) || (tab === 'site_page' && !sitePage));
  const fmtOpts = tab === 'blog'
    ? [{ value: 'html', label: 'HTML — client blog (current sites)' }, { value: 'astro_md', label: 'Astro — client blog (new sites)' }]
    : [{ value: 'html', label: 'HTML — client project story' }, { value: 'astro_md', label: 'Astro — client project story' }];

  /* ---- Slots -------------------------------------------------------------
     Every piece of the generator's UI, built once here and placed by a
     layout. v1 (below) puts each where it has always been; v2
     (GeneratorV2) arranges the same elements into stacked steps. The point
     is that there is ONE copy of every control: the case-study body, the
     Generate controls and the output viewer cannot drift between the two
     layouts because they are literally the same element. State and handlers
     stay in this component; the layouts own only their own navigation. */
  const blogTopicRow = (
    <>
          <div className="gen-blog__topic">
            <Input label="Main topic" placeholder="e.g. Tankless water heater installation" value={blogTopic}
              onChange={(e) => { setBlogTopic(e.target.value); setCoverage(null); setBlogOrigin('own'); setBlogGaps([]); setAltTopics([]); }} />
            <Button variant="outline" size="sm" disabled={checking || !blogTopic.trim()} iconLeft={<Icon name="check-circle" size={14} />} onClick={() => checkVault()}>
              {checking ? 'Checking…' : 'Check the Vault'}
            </Button>
            <Button variant="outline" size="sm" disabled={suggesting} iconLeft={<Icon name="sparkles" size={14} />} onClick={suggest}>
              {suggesting ? 'Reading vault…' : 'Suggest from vault'}
            </Button>
          </div>
    </>
  );
  const blogGroundSelect = (
    <>
          {(() => {
            /* Any interview with a transcript can ground a blog directly. A
               Knowledge Session is the usual pick — it is an expert teaching a
               topic, which is a blog, not a case study — but a project interview
               works too. Choosing one makes its transcript the primary source
               and seeds the topic when the box is empty. */
            // Vault transcripts are case-study picks only: a blog is grounded in the whole Vault already.
            const visible = stories.filter((s) => s.kind !== 'vault' && (!s.archived || s.id === blogStoryId));
            const picked = blogSourceIds.length === 1 && blogSourceIds[0] === blogStoryId ? blogStoryId : '';
            return (
              <Select
                label="Ground in an interview (optional)"
                value={picked}
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) { setBlogSourceIds([]); setBlogStoryId(''); return; }
                  const s = stories.find((x) => x.id === id);
                  setBlogSourceIds([id]); setBlogStoryId(id); setBlogOrigin('interview'); setCoverage(null);
                  if (!blogTopic.trim() && s) setBlogTopic(s.title);
                }}
                options={[{ value: '', label: visible.length ? 'Whole vault (no single interview)' : 'No interviews with a transcript yet' },
                  ...visible.map((s) => ({ value: s.id, label: `${s.title}${s.city ? ` · ${s.city}` : ''} — ${storyKindLabel(s)}${s.archived ? ' · archived' : ''}` }))]}
              />
            );
          })()}
    </>
  );
  const blogSupportBadge = (
    <>
          {blogSupport && (
            <p className="dash-muted" style={{ margin: '-4px 0 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Badge variant="accent">supports {blogSupport.anchor || blogSupport.url}</Badge>
              {blogSupport.keywords && <span style={{ fontSize: 'var(--text-xs)' }}>keywords: {blogSupport.keywords}</span>}
              <button type="button" className="bq-add" onClick={() => setBlogSupport(null)}>clear</button>
            </p>
          )}
    </>
  );
  const blogGroundedBadge = (
    <>
          {blogSourceIds.length > 0 && !(blogSourceIds.length === 1 && blogSourceIds[0] === blogStoryId) && (
            <p className="dash-muted" style={{ margin: '-4px 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Badge variant="accent">grounded in {blogSourceIds.length} interview{blogSourceIds.length > 1 ? 's' : ''}</Badge>
              <button type="button" className="bq-add" onClick={() => { setBlogSourceIds([]); setBlogStoryId(''); }}>clear</button>
            </p>
          )}
    </>
  );
  const blogCoverageFor = (covOpts: { showSend?: boolean } = {}) => (
    <>
          {coverage && <CoverageCard coverage={coverage} onUseQuestions={useCoverageQuestions} onSend={sendCoverageForApproval} sending={covSending} sent={sentIsLive} sentRow={sentRow} showSend={covOpts.showSend} />}
          {altTopics.length > 0 && (
            <div className="cov-alts">
              <span className="cov-alts__lbl">Other angles the vault supports</span>
              {altTopics.map((alt) => (
                <button type="button" key={alt.topic} className="cov-alt" onClick={() => useAlternative(alt)}>
                  <span>{alt.topic}</span>
                  {!!alt.nearTo?.length && <Badge variant="warning">close to existing</Badge>}
                  {alt.coverage && (
                    <Badge variant={alt.coverage.verdict === 'strong' ? 'success' : alt.coverage.verdict === 'partial' ? 'warning' : 'neutral'}>
                      {alt.coverage.score}/100
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          )}
    </>
  );
  const blogOutline = (
    <>
          <div className="gen-blog__qs">
            {blogQs.map((q, i) => (
              <Input key={i} placeholder={`Question ${i + 1}${i > 0 ? ' (optional)' : ''}`} value={q}
                onChange={(e) => setBlogQs(blogQs.map((v, j) => (j === i ? e.target.value : v)))} />
            ))}
          </div>
    </>
  );
  const blogNotesEl = (
    <>
          {blogNotes && (
            <p className="dash-muted" style={{ fontSize: 12 }}>
              <Icon name="info" size={12} /> Client-submitted background rides along with this write (from their Expert Studio blog submission) — it steers the piece but is not stored in the Vault.
              {' '}<button type="button" className="bq-add" onClick={() => setBlogNotes('')}>drop it</button>
            </p>
          )}
    </>
  );
  const gapsFor = (gapOpts: { route?: string; onSent?: (count: number) => void } = {}) => (
    <>
          <GapSender topic={gapOpts.route ? sitePageSubject : blogTopic} questions={gapOpts.route ? [] : blogQs} gaps={blogGaps} sourceIds={gapOpts.route ? [] : blogSourceIds} route={gapOpts.route} onSent={gapOpts.onSent} />
    </>
  );
  const blogManagers = (
    <>
          <AiIdeaVetter onPick={pickAiIdea} />
          <PageTopicSuggester onUse={useQueued} />
          <InterviewTopicMiner onUse={useQueued} />
          <CoveredTopicsEditor />
          <BlogQueue
            onUse={(t, q, sourceIds, notes, support) => useQueued(t, q, sourceIds, [], notes || '', support ?? null)}
            onGenerate={(t, q, sourceIds, notes, support) => { useQueued(t, q, sourceIds, [], notes || '', support ?? null); generateBlogWith(t, q, sourceIds, notes || '', support ?? null); }} />
    </>
  );
  const promptBody = (
    <>
      {tab === 'prompt' && (
        <div className="gen-blog">
          <Textarea rows={5} placeholder={"Ask for anything, grounded in this client's vault. e.g.\n• Write 3 title options for the tankless water heater page\n• Add a paragraph on winter maintenance for the furnace service page\n• Rewrite this more concisely: …"}
            value={promptText} onChange={(e) => setPromptText(e.target.value)} />
        </div>
      )}
    </>
  );
  const rewriteBody = (
    <>
      {tab === 'rewrite' && (
        <div className="gen-blog">
          <p className="gen-hint" style={{ padding: '0 0 8px' }}>
            Paste or upload outdated content to refresh. The <strong>format is preserved</strong> — HTML stays HTML, Markdown keeps its structure and front-matter. It&rsquo;s rewritten against this client&rsquo;s vault + house style, and is <strong>never saved to the vault</strong>.
          </p>
          <Textarea rows={8} placeholder={"Paste existing content here (HTML, Markdown, or plain text)…"}
            value={rewriteText} onChange={(e) => setRewriteText(e.target.value)} />
          <div className="vault-add__row" style={{ marginTop: 8 }}>
            <Select label="Rewrite strength" value={rewriteStrength} onChange={(e) => setRewriteStrength(e.target.value as typeof rewriteStrength)}
              options={[{ value: 'light', label: 'Light — polish & fix facts' }, { value: 'moderate', label: 'Moderate — genuine rewrite' }, { value: 'heavy', label: 'Heavy — thorough rework' }]} />
          </div>
          <div className="vault-add__row" style={{ marginTop: 8 }}>
            <input type="file" accept=".md,.markdown,.html,.htm,.txt,text/*"
              onChange={async (e) => { const f = e.target.files?.[0]; if (f) { setRewriteText(await f.text()); setErr(''); } }} />
            <Input placeholder="Optional instruction (e.g. lead with the cost savings, shorten the intro)"
              value={rewriteInstruction} onChange={(e) => setRewriteInstruction(e.target.value)} />
          </div>
          <label className="gen-story__arch" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={factCheck} onChange={(e) => setFactCheck(e.target.checked)} />
            Fact-check external stats via web — verify non-vault numbers/claims and update outdated ones to current, cited data. Slower + extra cost (web search on Sonnet); leave off for plain refreshes.
          </label>
        </div>
      )}
    </>
  );
  const sitePageSelects = (
    <>
            <Select label="Site page" value={sitePage}
              onChange={(e) => { setSitePage(e.target.value); setSitePageLayout(''); setLayoutInfo(null); setPushedUrl(''); }}
              options={[{ value: '', label: 'Choose a page…' }, ...pages.map((p) => ({ value: p, label: pagePath(p) }))]} />
            <Select label="Page type" value={sitePageType} onChange={(e) => setSitePageType(e.target.value as SitePageType | '')}
              options={[{ value: '', label: 'Auto — from the route' }, ...SITE_PAGE_TYPES.map((t) => ({ value: t, label: t[0].toUpperCase() + t.slice(1) }))]} />
    </>
  );
  /* The site-page picker is part of these controls in v1 and Step 1 of v2;
     the flag is how v2 keeps it out of Step 4 without a second copy. */
  const controlsFor = (opts: { sitePagePicker?: boolean } = {}) => (
      <div className="gen-controls">
        {tab === 'case_study' || tab === 'blog' ? (
          <>
            {platform
              ? <div className="gen-fmt-locked">
                  <span className="gen-fmt-locked__label">Output</span>
                  <span className="gen-fmt-locked__value">
                    {platform === 'dnn' ? 'HTML → DNN (EasyDNNNews)' : 'Astro Markdown → GitHub repo'}
                  </span>
                </div>
              : <Select label="Output" value={format} onChange={(e) => setFormatChoice(e.target.value as OutputFormat)} options={fmtOpts} />}
            {tab === 'case_study' && (() => {
              const visible = stories.filter((s) => showArchived || !s.archived);
              const archivedCount = stories.filter((s) => s.archived).length;
              const progress = (s: GenStory) => {
                const n = s.written?.length ?? 0;
                return s.archived ? ' · done' : n ? ` · ${n} case stud${n === 1 ? 'y' : 'ies'} written` : '';
              };
              const story = stories.find((s) => s.id === storyId);
              const projects = storyProjects?.projects ?? [];
              const doneNames = new Set((story?.written ?? []).map((n) => n.trim().toLowerCase()));
              const isDone = (name: string) => doneNames.has(name.trim().toLowerCase());
              const left = projects.filter((p) => !isDone(p.name)).length;
              return (
                <div className="gen-story">
                  <Select
                    label="Story (interview or Vault transcript)"
                    value={storyId}
                    onChange={(e) => { setStoryId(e.target.value); setProjectName(''); }}
                    options={[{ value: '', label: visible.length ? 'All / strongest (auto)' : 'No unarchived interviews with a transcript' },
                      ...visible.map((s) => ({ value: s.id, label: `${s.title}${s.city ? ` · ${s.city}` : ''} — ${storyKindLabel(s)}${progress(s)}` }))]}
                  />
                  {archivedCount > 0 && (
                    <label className="gen-story__arch">
                      <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show finished ({archivedCount})
                    </label>
                  )}
                  {storyId && projects.length > 1 && (
                    <>
                      <Select label={`Project — ${projects.length} jobs in this transcript, each gets its own case study`}
                        value={projectName} onChange={(e) => setProjectName(e.target.value)}
                        options={[{ value: '', label: 'Strongest project (auto)' },
                          ...projects.map((p) => ({ value: p.name, label: `${isDone(p.name) ? '✓ ' : ''}${p.name}${p.city ? ` · ${p.city}` : ''}${isDone(p.name) ? ' — written' : ''}` }))]} />
                      <p className="vault-add__hint">
                        {left === 0
                          ? 'Every project in this transcript has its case study.'
                          : `${projects.length - left} of ${projects.length} written. The transcript is archived once all ${projects.length} are saved.`}
                      </p>
                    </>
                  )}
                </div>
              );
            })()}
          </>
        ) : tab === 'prompt' ? (
          <>
            <Select label="Focus (optional)" value={storyId} onChange={(e) => setStoryId(e.target.value)}
              options={[{ value: '', label: 'Whole vault' }, ...stories.map((s) => ({ value: s.id, label: `${s.title}${s.city ? ` · ${s.city}` : ''} — ${storyKindLabel(s)}` }))]} />
            <Select label="Output" value={promptFormat} onChange={(e) => setPromptFormat(e.target.value as typeof promptFormat)}
              options={[{ value: 'auto', label: 'Auto' }, { value: 'markdown', label: 'Markdown' }, { value: 'html', label: 'HTML' }, { value: 'plain', label: 'Plain text' }]} />
          </>
        ) : tab === 'rewrite' ? (
          <Select label="Source format" value={rewriteFormat} onChange={(e) => setRewriteFormat(e.target.value as typeof rewriteFormat)}
            options={[{ value: 'auto', label: 'Auto-detect' }, { value: 'markdown', label: 'Markdown' }, { value: 'html', label: 'HTML' }, { value: 'plain', label: 'Plain text' }]} />
        ) : tab === 'site_page' ? (
          <>
            {opts.sitePagePicker === false ? null : sitePageSelects}
          </>
        ) : (
          <Select label="Page" value={handoffPage} onChange={(e) => setHandoffPage(e.target.value)}
            options={[{ value: '', label: 'All pages — quick briefs' }, ...pages.map((p) => ({ value: p, label: pagePath(p) }))]} />
        )}
        {(tab === 'case_study' || tab === 'blog') && heroUsage?.configured && (
          <label className="gen-heroopt">
            <input type="checkbox" checked={heroWanted} onChange={(e) => setHeroWanted(e.target.checked)} />
            <span>Add a hero image <small>{heroUsage.used}/{heroUsage.cap} this month</small></span>
          </label>
        )}
        <Select label="Grounding" value={mode} onChange={(e) => setMode(e.target.value as GroundingMode)}
          options={[{ value: 'polished', label: 'Polished (publish-ready)' }, { value: 'strict', label: 'Strict (flags [NEEDS: …])' }]} />
        {tab === 'blog' && (
          <Select label="Writer" value={writer} onChange={(e) => void saveBlogSetting({ writer: e.target.value as BlogWriter })}
            hint={writer === 'evidence' ? 'Mines the Vault for what this company can say, holds the post to it, and offers optional questions for the client.' : 'The original prompt, unchanged.'}
            options={[{ value: 'evidence', label: 'Evidence-first (new)' }, { value: 'classic', label: 'Classic (original)' }]} />
        )}
        {tab === 'blog' && (
          <label className="gen-check" title="Scores the draft on effort, originality, skill and accuracy, flags unsupported claims and clichés, and offers one revision. Off by default.">
            <input type="checkbox" checked={auditOn} onChange={(e) => void saveBlogSetting({ audit: e.target.checked })} />
            <span>Audit the draft</span>
          </label>
        )}
        {tab === 'blog' && (
          <Button variant="outline" disabled={busy || !blogTopic.trim()} iconLeft={<Icon name="check" size={15} />} onClick={savePending}>
            Save for approval
          </Button>
        )}
        <Button variant="primary" disabled={blockGen}
          iconLeft={busy ? undefined : <Icon name="sparkles" size={15} />}
          onClick={run}>
          {busy ? (tab === 'rewrite' ? 'Rewriting…' : 'Writing…') : (tab === 'prompt' || tab === 'rewrite' || ready) ? (tab === 'rewrite' ? 'Rewrite' : 'Generate') : 'Vault empty'}
        </Button>
      </div>
  );
  const hintsEl = (
    <>
      {tab === 'case_study' && (
        <p className="gen-hint" style={{ padding: '0 20px 12px' }}>
          Stories are your interviews that have a transcript — AI interviews and any live interview you&rsquo;ve added a transcript to. Pick one, or leave on <em>auto</em>.
          {stories.length === 0 && <> <strong>No interviews with a transcript yet — run an AI interview or paste a live transcript on an interview.</strong></>}
        </p>
      )}
      {tab === 'case_study' && format === 'astro_md' && (
        <p className="gen-hint" style={{ padding: '0 20px 12px' }}>
          The client&rsquo;s own project story in Astro Markdown for their new site — client-branded, internal links from the client sitemap.
        </p>
      )}
      {tab === 'blog' && format === 'astro_md' && (
        <p className="gen-hint" style={{ padding: '0 20px 12px' }}>
          Astro Markdown for the client&rsquo;s new site — <strong>client-branded</strong>, links from the client sitemap. No Triton branding.
        </p>
      )}
      {(tab === 'case_study' || tab === 'blog') && platform === 'dnn' && (
        <p className="gen-hint" style={{ padding: '0 20px 12px' }}>
          This client is on DNN, so it delivers as an EasyDNNNews article. Its categories start from the {tab === 'blog' ? 'blog' : 'case study'} defaults in <strong>Setup → Push to DNN</strong> and stay editable before you push — DNN won&rsquo;t list an uncategorised article.
        </p>
      )}
    </>
  );
  const handoffHint = (
    <>
      {tab === 'web_handoff' && (
        <p className="gen-hint" style={{ padding: '0 20px 12px' }}>
          {handoffPage
            ? <>A <strong>deep, EEAT-rich build-pack</strong> for one page: drafted copy, schema, FAQs, internal links, media, and flagged gaps — enough for Claude Code to build a complete page. Depth mirrors the vault, so richer interviews → richer pages.</>
            : <><strong>Quick briefs</strong> for every page (structure + grounded notes) — good for cross-page context. Includes a ready-to-drop <code>CLAUDE.md</code> (brand, NAP, link map, page index, Astro/CloudCannon conventions) to seed the repo. For content-heavy pages, pick a single page above to get the full build-pack.</>}
          {!ctx?.hasSitemap && <> Import a <strong>sitemap</strong> into the vault for the page list.</>}
        </p>
      )}
    </>
  );
  const sitePageBody = (
    <>
      {tab === 'site_page' && (
        <>
          <p className="gen-hint" style={{ padding: '0 20px 12px' }}>
            The <strong>finished page file</strong> for one sitemap route, in this client&rsquo;s own repo layout &mdash; publish-ready copy, not a build-pack. Review it, then push it to the repo. Depth mirrors the vault, so richer interviews &rarr; richer pages.
            {!ctx?.hasSitemap && <> Import a <strong>sitemap</strong> into the vault for the page list.</>}
          </p>
          <div className="gen-linkmap">
            <button type="button" className="gen-linkmap__toggle" onClick={() => setLayoutOpen((v) => !v)}>
              <Icon name={layoutOpen ? 'chevron-down' : 'chevron-right'} size={14} /> Page layout
              <Badge variant={layoutInfo ? (layoutInfo.source === 'default' ? 'warning' : 'success') : 'neutral'}>
                {layoutInfo ? LAYOUT_SOURCE_LABEL[layoutInfo.source] : 'Auto'}
              </Badge>
            </button>
            {layoutOpen && (
              <div className="gen-linkmap__body">
                <p className="vault-add__hint">
                  The generated file copies this layout exactly &mdash; every front-matter key and block name. Left empty, the page&rsquo;s own <code>.md</code> is read from the repo; failing that, this client&rsquo;s saved layout for the page type; failing that, the Triton starter default. Bracketed notes in the file, like <code>[H1 &mdash; under 60 characters, primary keyword]</code>, are followed as instructions.
                </p>
                <div className="vault-add__methods" style={{ padding: 0, marginBottom: 8 }}>
                  <button type="button" className={`vault-tab ${sitePageLayoutUse === 'layout_only' ? 'is-on' : ''}`} onClick={() => setSitePageLayoutUse('layout_only')}
                    title="The file's text is removed before the model sees it, and this page's crawled copy is kept out of the sources. Everything is written from the Vault.">
                    Layout only &mdash; write fresh from the Vault
                  </button>
                  <button type="button" className={`vault-tab ${sitePageLayoutUse === 'rewrite' ? 'is-on' : ''}`} onClick={() => setSitePageLayoutUse('rewrite')}
                    title="Keep the page's existing facts where the Vault confirms them; refresh wording and SEO phrasing.">
                    Rewrite this page &mdash; keep its facts
                  </button>
                </div>
                <Textarea rows={12} value={sitePageLayout} onChange={(e) => setSitePageLayout(e.target.value)}
                  placeholder={'Paste the page\u2019s .md file (front-matter + blocks) \u2014 or click “Fetch layout” to read it from the repo.'} />
                {layoutInfo?.repoPath && <p className="vault-add__hint"><Icon name="check" size={13} /> From the repo: <code>{layoutInfo.repoPath}</code></p>}
                {!!repoFiles?.files.length && (
                  <Select label="…or copy the layout from another repo page" value=""
                    onChange={(e) => loadRepoFile(e.target.value)}
                    options={[{ value: '', label: `Pick a file (${repoFiles.files.length} in the repo)…` }, ...repoFiles.files.map((f) => ({ value: f, label: f }))]} />
                )}
                {savedLayouts && (() => {
                  const set = SITE_PAGE_TYPES.filter((t) => savedLayouts.templates[t]?.trim());
                  return set.length
                    ? <p className="vault-add__hint">Saved layouts for this client: {set.join(', ')}.</p>
                    : <p className="vault-add__hint">No saved layouts yet for this client &mdash; save one per page type and every page of that type matches it.</p>;
                })()}
                <div className="vault-add__actions">
                  <Button variant="outline" size="sm" disabled={layoutBusy || !sitePage} onClick={loadLayout}
                    iconLeft={<Icon name={layoutBusy ? 'refresh-cw' : 'download'} size={14} />}>
                    {layoutBusy ? 'Fetching…' : 'Fetch layout'}
                  </Button>
                  <Button variant="ghost" size="sm" disabled={layoutBusy || !sitePageLayout.trim() || !saveType} onClick={saveLayoutAsDefault}
                    title={saveType ? `Save as this client\u2019s default ${saveType} layout` : 'Fetch or generate once so the page type is known'}
                    iconLeft={<Icon name={layoutSaved ? 'check' : 'settings'} size={14} />}>
                    {layoutSaved ? 'Saved' : `Save as default${saveType ? ` (${saveType})` : ''}`}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div style={{ padding: '0 20px 14px' }}>
            <Textarea label="Brief for this page (optional)" rows={2} value={sitePageNotes} onChange={(e) => setSitePageNotes(e.target.value)}
              placeholder="e.g. lead with the emergency service angle; this page targets Eden Prairie" />
            <div style={{ marginTop: 10 }}>
              <ExpertPicker value={sitePageExpert} onChange={setSitePageExpert} label="Signed by" disabled={busy} approvedOnly />
            </div>
          </div>
        </>
      )}
    </>
  );
  const templateEl = (
    <>
      {(tab === 'case_study' || tab === 'blog') && <TemplateEditor />}
    </>
  );
  const messagesEl = (
    <>
      {err && <p className="vault-err" style={{ padding: '0 20px 14px' }}><Icon name="alert-circle" size={13} /> {err}</p>}
      {note && <p className="gen-note" style={{ padding: '0 20px 12px' }}><Icon name="info" size={13} /> {note}</p>}
    </>
  );
  const outputEl = (
    <>
      {result && (
        <div className="gen-out">
          {(result.title || result.metaTitle || result.metaDescription || result.summary) && (
            <dl className="gen-meta">
              {result.title && <div><dt>Title</dt><dd>{result.title}<CopyBtn text={result.title} /></dd></div>}
              {result.metaTitle && <div><dt>SEO title</dt><dd>{result.metaTitle} <em>({result.metaTitle.length})</em><CopyBtn text={result.metaTitle} /></dd></div>}
              {result.metaDescription && <div><dt>Meta description</dt><dd>{result.metaDescription} <em>({result.metaDescription.length})</em><CopyBtn text={result.metaDescription} /></dd></div>}
              {result.summary && <div><dt>Summary</dt><dd>{result.summary}<CopyBtn text={result.summary} /></dd></div>}
            </dl>
          )}
          <div className="gen-out__bar">
            <span>{tab === 'prompt' ? 'Response' : tab === 'rewrite' ? (result.format === 'html' ? 'Rewritten HTML' : 'Rewritten Markdown') : result.format === 'html' ? 'HTML' : 'Astro Markdown'} · {result.output.length.toLocaleString()} chars</span>
            <div className="gen-out__actions">
              <CopyBtn text={result.output} label={result.format === 'html' ? 'Copy HTML' : 'Copy'} />
              <Button variant="outline" size="sm" iconLeft={<Icon name="download" size={13} />} onClick={() => download(`${tab === 'blog' ? 'blog' : tab === 'prompt' ? 'response' : tab === 'rewrite' ? 'rewritten' : 'case-study'}.${csExt}`, result.output)}>Download</Button>
              {(tab === 'case_study' || tab === 'blog' || tab === 'rewrite') && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-xs)', color: 'var(--muted, #667)' }}
                  title="Publish date — sets the post's date on the client site AND the month it counts toward on the content tracker">
                  <Icon name="calendar" size={13} /> Publish
                  <input type="date" value={publishDate} onChange={(e) => setPublishDate(e.target.value)} style={{ fontSize: 'var(--text-xs)' }} />
                </label>
              )}
              {(tab === 'case_study' || tab === 'blog') && (
                savedAs ? (
                  <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>
                    <Icon name="check" size={13} /> Saved as {savedAs}{!resultStoryId ? ''
                      : savedProgress && savedProgress.done < savedProgress.count
                        ? ` · ${savedProgress.done} of ${savedProgress.count} projects in this transcript written — pick the next one under Project`
                        : tab === 'blog' ? ' · source record archived' : ' · every project in the transcript is written, so it was archived'}
                  </span>
                ) : (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => setResult(null)}>Discard</Button>
                    <Button variant="outline" size="sm" disabled={publishing} onClick={() => onSave(1)}>
                      {publishing ? 'Saving…' : 'Save as Draft'}
                    </Button>
                    <Button variant="primary" size="sm" disabled={publishing} iconLeft={<Icon name="check" size={13} />} onClick={() => onSave(3)}>
                      Save as Published
                    </Button>
                  </>
                )
              )}
              {(tab === 'case_study' || tab === 'blog' || tab === 'rewrite') && result.format === 'astro_md' && (
                pushedUrl
                  ? <a href={pushedUrl} target="_blank" rel="noreferrer" className="vault-link"><Icon name="external" size={13} /> Pushed to repo</a>
                  : <>
                      <Button variant="outline" size="sm" disabled={pushing} onClick={() => onPush(true)}>{pushing ? 'Pushing…' : 'Push draft'}</Button>
                      <Button variant="outline" size="sm" disabled={pushing} onClick={() => onPush(false)}>Push &amp; publish</Button>
                    </>
              )}
              {(tab === 'case_study' || tab === 'blog') && result.format === 'html' && (
                dnnUrl
                  ? <a href={dnnUrl} target="_blank" rel="noreferrer" className="vault-link"><Icon name="external" size={13} /> {dnnDraft ? 'DNN draft (preview only)' : 'Live on DNN'}</a>
                  : <>
                      <Button variant="outline" size="sm" disabled={dnnBusy || !dnnCats.selected.length} title={dnnCats.selected.length ? undefined : 'Select a DNN category first'} onClick={() => onPushDnn(true)}>{dnnBusy ? 'Pushing…' : 'Push to DNN (draft)'}</Button>
                      <Button variant="outline" size="sm" disabled={dnnBusy || !dnnCats.selected.length} title={dnnCats.selected.length ? undefined : 'Select a DNN category first'} onClick={() => onPushDnn(false)}>DNN &amp; publish</Button>
                    </>
              )}
            </div>
          </div>
          {(tab === 'case_study' || tab === 'blog') && result.format === 'html' && !dnnUrl && dnnCats.configured && (
            <>
              <DnnCategoryBar p={dnnCats} />
              {dnnHero?.imageUrl && (
                <p className="gen-hint" style={{ padding: '0 20px 10px' }}>
                  <Icon name="image" size={13} /> Lead image from this story’s photos: <code>{dnnHero.imageUrl.replace(/^https?:\/\//, '')}</code>
                </p>
              )}
            </>
          )}
          {dnnNote && (
            <p className="gen-note" style={{ padding: '0 20px 10px' }}>
              <Icon name="alert-circle" size={13} /> {dnnNote}
            </p>
          )}
          {dnnUrl && dnnDraft && (
            <p className="gen-hint" style={{ padding: '0 20px 10px' }}>
              <Icon name="info" size={13} /> Pushed as a <strong>draft</strong> — that link is a DNN preview, visible only to a signed-in DNN admin. Nothing is public until it’s published.
            </p>
          )}
          {dnnDupe && (
            <div className="gen-dnn-cats">
              <p className="vault-err"><Icon name="alert-circle" size={13} /> {dnnDupe.message.replace(/^API 409:\s*/, '').replace(/^\{.*?"error":"/, '').replace(/"\}$/, '')}</p>
              <div className="vault-add__actions">
                <Button variant="outline" size="sm" disabled={dnnBusy} onClick={() => onPushDnn(dnnDupe.draft, true)}>{dnnBusy ? 'Pushing…' : 'Push anyway (adds a second copy)'}</Button>
                <Button variant="ghost" size="sm" onClick={() => setDnnDupe(null)}>Cancel</Button>
              </div>
            </div>
          )}
          {(tab === 'case_study' || tab === 'blog') && (
            <div style={{ padding: '0 20px 12px' }}>
              <HeroImagePanel
                articleId={resultStoryId || ''}
                topic={blogTopic || projectName || result.title || ''}
                article={result.output}
                slug={(result.title || blogTopic || 'post')}
                autoKey={heroAutoKey}
                platform={platform}
                onApply={(url, alt) => {
                  setHeroUrl(url); setHeroAlt(alt);
                  setResult((r) => (r ? { ...r, output: applyHeroImage(r.output, url, alt) } : r));
                }}
              />
              {result.format === 'astro_md' && hasHeroPlaceholder(result.output) && (
                <p className="gen-hint" style={{ marginTop: 8 }}>
                  <Icon name="info" size={13} /> This article still points at <code>/images/placeholder.jpg</code> &mdash; add a hero image above, or swap it in CloudCannon after pushing.
                </p>
              )}
            </div>
          )}
          <pre className="gen-out__pre">{result.output}</pre>
          {tab === 'rewrite' && result.changes && (
            <>
              <div className="gen-out__bar">
                <span><Icon name="alert-circle" size={13} /> Stats updated — verify each before publishing</span>
                <div className="gen-out__actions"><CopyBtn text={result.changes} label="Copy changes" /></div>
              </div>
              <pre className="gen-out__pre">{result.changes}</pre>
            </>
          )}
          {result.evidence && (
            <div className="gen-evidence">
              <div className="gen-out__bar">
                <span>Evidence from the Vault · <Badge variant={result.evidence.coverage === 'strong' ? 'success' : result.evidence.coverage === 'partial' ? 'warning' : 'neutral'}>{result.evidence.coverage}</Badge></span>
                <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>
                  {result.evidence.claims.length} claims · {result.evidence.quotes.length} quotes · {result.evidence.products.length} products · {result.evidence.numbers.length} numbers · {result.evidence.local.length} local · {result.evidence.process.length} process
                </span>
              </div>
              {result.evidence.summary && <p className="dash-muted" style={{ margin: '6px 0' }}>{result.evidence.summary}</p>}
              {result.evidence.questions.length > 0 && (
                <div className="gen-evidence__ask">
                  <p className="dash-muted" style={{ margin: '6px 0' }}>
                    <strong>Optional:</strong> what the client could add to make this post theirs — real jobs first, then facts. Their answers land in the Vault, and the next write uses them. Skip it for a client who won&rsquo;t answer.
                  </p>
                  <GapSender topic={blogTopic} questions={blogQs} gaps={result.evidence.questions} sourceIds={blogSourceIds} />
                </div>
              )}
            </div>
          )}
          {tab === 'blog' && (
            <div className="gen-out__bar">
              <span>Audit{result.audit ? ` · overall ${result.audit.scores.overall}/10` : ''}</span>
              <div className="gen-out__actions">
                {!result.audit && (
                  <Button variant="outline" size="sm" disabled={auditing || busy} iconLeft={<Icon name="shield-check" size={13} />}
                    onClick={async () => {
                      setAuditing(true); setErr('');
                      try { const r = await auditBlogDraft({ output: result.output, topic: blogTopic, evidence: result.evidence ?? null, supportAnchor: blogSupport?.anchor, keywords: blogSupport?.keywords }); setResult({ ...result, audit: r.audit }); }
                      catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
                      finally { setAuditing(false); }
                    }}>
                    {auditing ? 'Auditing…' : 'Audit this draft'}
                  </Button>
                )}
                {result.audit?.notes && (
                  <Button variant="primary" size="sm" disabled={revising || busy} iconLeft={<Icon name="sparkles" size={13} />}
                    onClick={async () => {
                      setRevising(true); setErr('');
                      try { const r = await reviseBlogDraft({ output: result.output, format: result.format, notes: result.audit!.notes, evidence: result.evidence ?? null }); setResult({ ...result, output: r.output, audit: null }); }
                      catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
                      finally { setRevising(false); }
                    }}>
                    {revising ? 'Revising…' : 'Apply the revision (one pass)'}
                  </Button>
                )}
              </div>
            </div>
          )}
          {result.audit && (
            <div className="gen-audit">
              <div className="gen-audit__scores">
                {(['effort', 'originality', 'skill', 'accuracy'] as const).map((k) => (
                  <span key={k} className="gen-audit__score"><strong>{result.audit!.scores[k]}</strong> {k}</span>
                ))}
              </div>
              {result.audit.unsupported.length > 0 && <div><span className="tr-field__label">Unsupported by the Vault</span><ul className="miner-qs">{result.audit.unsupported.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
              {result.audit.cliches.length > 0 && <div><span className="tr-field__label">Cut</span><ul className="miner-qs">{result.audit.cliches.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
              {result.audit.missing.length > 0 && <div><span className="tr-field__label">Missing</span><ul className="miner-qs">{result.audit.missing.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
              {result.audit.notes && <div><span className="tr-field__label">Revision brief</span><pre className="gen-out__pre" style={{ whiteSpace: 'pre-wrap' }}>{result.audit.notes}</pre></div>}
            </div>
          )}
          {result.faqSchema && (
            <>
              <div className="gen-out__bar">
                <span>FAQ schema · JSON-LD</span>
                <div className="gen-out__actions">
                  <CopyBtn text={result.faqSchema} label="Copy schema" />
                  <Button variant="outline" size="sm" iconLeft={<Icon name="download" size={13} />} onClick={() => download('faq-schema.html', result.faqSchema || '')}>Download</Button>
                </div>
              </div>
              <pre className="gen-out__pre">{result.faqSchema}</pre>
            </>
          )}
        </div>
      )}

      {files && (
        <div className="gen-out">
          <div className="gen-out__bar">
            <span>{files.length} page file{files.length === 1 ? '' : 's'}</span>
            <Button variant="outline" size="sm" iconLeft={<Icon name="download" size={13} />}
              onClick={() => download(tab === 'site_page' ? files[0].filename : 'web-handoff.md', files.map((f) => `<!-- ${f.filename} (${f.page}) -->\n\n${f.content}`).join('\n\n---\n\n'), 'text/markdown')}>
              Download all
            </Button>
          </div>
          {tab === 'site_page' && (
            <div className="gen-dnn-cats">
              <p className="vault-add__hint">
                Pushing commits this file to <code>{githubBranchLabel}</code>, replacing whatever is at that path. Check the front-matter above first &mdash; the page&rsquo;s schema decides whether the site builds.
              </p>
              <div className="vault-add__row">
                <Input value={pushPath} onChange={(e) => setPushPath(e.target.value)} placeholder="src/pages/services/example.md" />
                {pushedUrl
                  ? <a href={pushedUrl} target="_blank" rel="noreferrer" className="vault-link"><Icon name="external" size={13} /> Pushed to repo</a>
                  : <Button variant="primary" size="sm" disabled={pushing || !pushPath.trim()} onClick={() => onPushSitePage(files[0])}
                      iconLeft={<Icon name={pushing ? 'refresh-cw' : 'upload'} size={14} />}>
                      {pushing ? 'Pushing…' : 'Push to repo'}
                    </Button>}
              </div>
              {pushBlocked && (
                <p className="vault-err" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Icon name="alert-circle" size={13} /> <span style={{ flex: 1 }}>{pushBlocked}</span>
                  <Button variant="ghost" size="sm" disabled={pushing} onClick={() => files && onPushSitePage(files[0], true)}>Push anyway</Button>
                </p>
              )}
            </div>
          )}
          <ul className="gen-files">
            {files.map((f) => (
              <li key={f.filename} className="gen-file">
                <div className="gen-file__head">
                  <code>{f.filename}</code><span className="gen-file__page">{f.page}</span>
                  <div className="gen-file__actions">
                    <CopyBtn text={f.content} />
                    <IconBtn label={`Download ${f.filename}`} icon="download" onClick={() => download(f.filename, f.content, 'text/markdown')} />
                  </div>
                </div>
                {tab === 'site_page' ? (
                  <>
                    <div className="gen-checkbar">
                      <Button variant="outline" size="sm" disabled={fileChecking || busy} onClick={checkFile} iconLeft={<Icon name={fileChecking ? 'refresh-cw' : 'check-circle'} size={14} />}>
                        {fileChecking ? 'Checking…' : 'Check & auto-fix'}
                      </Button>
                      {fileLint
                        ? <p className="vault-err"><Icon name="alert-circle" size={13} /> {fileLint}</p>
                        : <p className="gen-note dash-muted"><Icon name="check" size={13} /> Front-matter looks sound. Edit below if you like, then re-check before pushing.</p>}
                      {fileRepairs.length > 0 && (
                        <p className="gen-note dash-muted" style={{ flexBasis: '100%', fontSize: 12 }}><Icon name="sparkles" size={12} /> Auto-fixed: {fileRepairs.join(' ')}</p>
                      )}
                    </div>
                    <LineEditor value={f.content} onChange={editFile} flaggedLine={Number(/line (\d+)/.exec(fileLint)?.[1]) || 0} />
                  </>
                ) : (
                  <details className="vault-raw"><summary>Preview</summary><pre>{f.content}</pre></details>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  if (version === 'v2') {
    return (
      <GeneratorV2
        tab={tab}
        onTab={(t) => { setTab(t); setErr(''); }}
        headRef={genHeadRef}
        flash={genFlash}
        blogTopic={blogTopic}
        hasCoverage={!!coverage}
        checking={checking}
        outlineCount={blogQs.filter((q) => q.trim()).length}
        gapCount={blogGaps.length}
        busy={busy}
        sitePage={sitePage}
        sitePageRoute={sitePageRoute}
        onCheckSitePage={() => void checkVault(sitePageSubject)}
        support={blogSupport}
        onClearSupport={() => setBlogSupport(null)}
        onSendForApproval={() => void sendCoverageForApproval(coverage?.questions ?? [])}
        sendState={{ sending: covSending, sent: sentIsLive, label: sentRow ? <QueueStatusLine item={sentRow} /> : 'Sent — with the Optimization Manager.' }}
        inbox={inboxHandlers}
        slots={{
          blogTopicRow, blogGroundSelect, blogGroundedBadge, blogCoverageFor, blogOutline, blogNotesEl, gapsFor, blogManagers,
          promptBody, rewriteBody, sitePageSelects, controlsFor, hintsEl, handoffHint, sitePageBody, templateEl, messagesEl, outputEl,
          briefsPanel,
          setupTab: <SetupTab />, imageStudio: <ImageStudio />,
        }}
      />
    );
  }

  return (
    <Card padding={false} className={`dash-leads vault gen${genFlash ? ' gen-flash' : ''}`}>
      <div className="dash-panelhead dash-panelhead--pad" ref={genHeadRef}>
        <div>
          <h2><Icon name="sparkles" size={17} /> Content generator</h2>
          <p>Write grounded content from this client&rsquo;s vault. Admin&nbsp;only.</p>
        </div>
      </div>

      <div className="vault-add__methods" style={{ padding: '12px 20px 0' }}>
        {(['setup', 'case_study', 'blog', 'prompt', 'rewrite', 'web_handoff', 'site_page', 'image'] as const).map((t) => (
          <button key={t} type="button" className={`vault-tab ${tab === t ? 'is-on' : ''}`} onClick={() => { setTab(t); setErr(''); }}>
            {t === 'setup' ? 'Setup' : t === 'case_study' ? 'Case study' : t === 'blog' ? 'Blog' : t === 'prompt' ? 'Prompt' : t === 'rewrite' ? 'Rewrite' : t === 'web_handoff' ? 'Web handoff' : t === 'site_page' ? 'Site page' : 'AI image'}
          </button>
        ))}
      </div>
      {tab === 'setup' && <SetupTab />}
      {tab === 'image' && <ImageStudio />}

      {tab === 'blog' && (
        <div className="gen-blog">
          {blogTopicRow}
          {blogGroundSelect}
          {blogSupportBadge}
          {blogGroundedBadge}
          {blogCoverageFor()}
          {blogOutline}
          {blogNotesEl}
          {gapsFor()}
          {blogManagers}
        </div>
      )}

      {promptBody}

      {rewriteBody}

      {tab !== 'setup' && tab !== 'image' && controlsFor()}

      {hintsEl}
      {handoffHint}
      {sitePageBody}
      {templateEl}

      {messagesEl}

      {outputEl}
    </Card>
  );
}

/* A numbered, editable view of a generated page file. Exists because the
   front-matter lint names a LINE, and a read-only preview left nothing to do
   with that information but re-generate. The gutter is a plain column of
   numbers kept in step with the textarea's scroll; the flagged line is tinted
   and scrolled into view when the lint changes. */
function LineEditor({ value, onChange, flaggedLine }: { value: string; onChange: (v: string) => void; flaggedLine: number }) {
  const gutter = useRef<HTMLDivElement>(null);
  const text = useRef<HTMLTextAreaElement>(null);
  const count = value.split('\n').length;
  useEffect(() => {
    if (!flaggedLine || !text.current) return;
    const lineHeight = 18; // matches .gen-editor__text
    text.current.scrollTop = Math.max(0, (flaggedLine - 1) * lineHeight - text.current.clientHeight / 3);
    if (gutter.current) gutter.current.scrollTop = text.current.scrollTop;
  }, [flaggedLine]);
  return (
    <div className="gen-editor">
      <div className="gen-editor__gutter" ref={gutter} aria-hidden="true">
        {Array.from({ length: count }, (_, i) => (
          <span key={i} className={i + 1 === flaggedLine ? 'is-flagged' : ''}>{i + 1}</span>
        ))}
      </div>
      <textarea
        ref={text}
        className="gen-editor__text"
        value={value}
        spellCheck={false}
        aria-label="Page file (editable)"
        onChange={(e) => onChange(e.target.value)}
        onScroll={(e) => { if (gutter.current) gutter.current.scrollTop = e.currentTarget.scrollTop; }}
      />
    </div>
  );
}

/* contentIQ's sections.

   Overview is the client's entire experience and the first thing an admin
   wants too, so it leads and carries no ?tab. The rest are ordered by how
   often they are opened: the Vault is worked continuously, Setup once per
   client. */
const CQ_TABS = [
  { key: 'overview', label: 'Overview', admin: false },
  /* Admin-only: everything on it is. The generator and the strategy panel are
     Triton's tools, and the strategy panel's empty state tells the reader to
     open Admin → Clients → Settings, which a client cannot reach — so a client
     either saw an empty tab or an instruction they could not follow. */
  { key: 'write', label: 'Content', admin: true },
  { key: 'vault', label: 'Vault', admin: true },
  { key: 'seo', label: 'SEO', admin: true },
  { key: 'setup', label: 'Setup', admin: true },
] as const;

function Library({
  onRequest,
  onStart,
  onInterview,
  onOpen,
  onStartTopic,
  aiEnabled,
  jobTypes,
}: {
  onRequest: () => void;
  /** Admin: the client is on the phone now — set up the stories and get the call link. */
  onStart: () => void;
  onInterview: () => void;
  onOpen: (id: string) => void;
  onStartTopic: (t: Topic) => void;
  aiEnabled: boolean;
  jobTypes: string[];
}) {
  const isAdmin = useIsAdmin();
  /* Read here rather than passed down: the tab is this component's own
     concern, and threading it through props would put the parent in charge of
     a thing only this render cares about. */
  const [params, setParams] = useSearchParams();
  const { data: articles = [], isLoading } = useQuery({ queryKey: qk.articles, queryFn: getArticles });
  const published = articles.filter((a) => a.stage === 3).length;
  const drafting = articles.filter((a) => a.stage >= 1 && a.stage <= 2).length;
  const interviews = articles.filter((a) => a.stage === 0).length;

  /* Filter and page in the browser: the list is one request of excerpts, and a
     client's whole back catalogue is small enough that a round trip per page
     would be slower than the filtering it saves. */
  const [kindFilter, setKindFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [originFilter, setOriginFilter] = useState('');
  /* Hidden by DEFAULT, never dropped: an interview a client walked away from is
     still a signal worth being able to look at — repeated abandonment means the
     interview itself is losing people. */
  const [showAbandoned, setShowAbandoned] = useState(false);
  const [page, setPage] = useState(0);
  const PER_PAGE = 10;

  /* An interview and the piece generated from it are ONE piece of work — same
     work, two titles, two statuses, and the reader cannot tell they are the same
     thing. So a story that some other row was generated FROM is folded into that
     row, which links back to it, whether or not the interview was archived (the
     auto-writer never archives it). Only a several-job interview with a job
     still unwritten stays in view. Delete the generated piece and the interview
     returns to the list. */
  const byId = new Map(articles.map((a) => [a.id, a]));
  const superseded = supersededStoryIds(articles);
  const writtenFrom = new Map<string, number>();
  for (const a of articles) if (a.sourceStoryId) writtenFrom.set(a.sourceStoryId, (writtenFrom.get(a.sourceStoryId) ?? 0) + 1);

  const abandoned = articles.filter((a) => isAbandonedInterview(a)).length;
  const shown = articles.filter((a) =>
    !superseded.has(a.id) &&
    (!kindFilter || (a.kind || 'interview') === kindFilter)
    /* Filtered on the DERIVED status, not the raw stage: a pushed article shows
       as "Pushed to site", so filtering Draft must not return it. */
    && (!stageFilter || articleStatus(a) === stageFilter)
    && (!originFilter || (originFilter === 'unknown' ? !a.origin : a.origin === originFilter))
    && (showAbandoned || !isAbandonedInterview(a)));
  const pages = Math.max(1, Math.ceil(shown.length / PER_PAGE));
  // A filter that shortens the list must not strand you on a page past its end.
  const current = Math.min(page, pages - 1);
  const pageItems = shown.slice(current * PER_PAGE, current * PER_PAGE + PER_PAGE);
  const onFilter = (set: (v: string) => void) => (e: { target: { value: string } }) => { set(e.target.value); setPage(0); };
  // Only offer kinds this client actually has.
  const kinds = Array.from(new Set(articles.map((a) => a.kind || 'interview'))).sort();

  /* Thirteen panels stacked on one page, eight of them admin-only, with the
     client's own view buried beneath them. Tabs rather than a longer page:
     every remaining phase adds another surface, and the cost of deciding where
     things live only grows.

     In the URL so a tab can be linked to, survives a reload, and so somebody
     sent "look at SEO for this client" lands on it. */
  const tab = (CQ_TABS.find((t) => t.key === params.get('tab')) ?? CQ_TABS[0]).key;
  const setTab = (next: string) => {
    const p = new URLSearchParams(params);
    if (next === CQ_TABS[0].key) p.delete('tab'); else p.set('tab', next);
    setParams(p, { replace: true });
  };
  const visible = CQ_TABS.filter((t) => !t.admin || isAdmin);
  /* A client arriving on ?tab=seo would otherwise see a page with nothing on
     it and no way to tell why. */
  const activeTab = visible.some((t) => t.key === tab) ? tab : CQ_TABS[0].key;
  const genVersion = useGeneratorVersion();

  return (
    <div className="dash-stack">
      <nav className="cq-tabs" role="tablist">
        {visible.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={activeTab === t.key}
            className={`cq-tab ${activeTab === t.key ? 'is-on' : ''}`}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </nav>

      {activeTab === 'overview' && (
      <Card variant="dark" padding="lg" className="cq-hero">
        <div className="cq-hero__txt">
          <span className="cq-hero__eyebrow"><Icon name="sparkles" size={14} /> Content engine</span>
          <h2>Your expertise, turned into content that ranks</h2>
          <p>Every interview with your team and happy customers becomes SEO-ready articles built to surface in AI search and Google.</p>
        </div>
        <div className="cq-hero__actions">
          {aiEnabled && (
            <Button variant="primary" iconLeft={<Icon name="sparkles" size={16} />} onClick={onInterview}>
              Start AI interview
            </Button>
          )}
          <Button
            variant={aiEnabled ? 'outline' : 'primary'}
            iconLeft={<Icon name="plus" size={16} />}
            onClick={onRequest}
          >
            Request an interview
          </Button>
        </div>
      </Card>
      )}

      {activeTab === 'overview' && <GapAnswers />}
      {/* On Content, not Overview. The panel does have a client mode — it
          hides itself when a client has no topics, and offers "start an
          interview from any of these" when they do — but topics are not
          surfaced to clients in practice, so on Overview it was admin clutter
          on the client's own page. If suggested topics ever become part of
          what a client sees, this is the line to revisit. */}
      {activeTab === 'write' && genVersion === 'v1' && <TopicsPanel onStart={onStartTopic} jobTypes={jobTypes} />}
      {activeTab === 'setup' && isAdmin && <Experts />}
      {activeTab === 'vault' && <VaultPanel />}
      {/* The ledger first: a page missing something the ledger says is COVERED
          is a writing job, and one missing something the ledger says is OPEN
          needs an interview. Crossing those two is the decision this tab
          exists to support, so they sit together. */}
      {activeTab === 'seo' && isAdmin && <Services />}
      {activeTab === 'seo' && isAdmin && <PageHealth />}
      {activeTab === 'seo' && isAdmin && <PageScore />}
      {/* The client's read-only view of how much material backs their content.
          A different component from the admin panel, not a gated one. */}
      {activeTab === 'overview' && <VaultBudgetBar />}
      {activeTab === 'write' && <GeneratorPanel briefsPanel={<TopicsPanel onStart={onStartTopic} jobTypes={jobTypes} />} />}

      {activeTab === 'overview' && (
      <div className="cq-stats">
        <Card padding="lg"><Stat label="Total articles" value={articles.length} sub="all time" /></Card>
        <Card padding="lg"><Stat label="Published" value={published} sub="live on site" /></Card>
        <Card padding="lg"><Stat label="In production" value={drafting} sub="draft + review" /></Card>
        <Card padding="lg"><Stat label="Interviews queued" value={interviews} sub="scheduled / requested" /></Card>
      </div>
      )}


      {activeTab === 'overview' && (
      <Card padding={false} className="dash-leads">
        <div className="dash-panelhead dash-panelhead--pad">
          <div>
            <h2>Interviews &amp; articles</h2>
            <p>Content sourced from your team’s real jobs and customer stories</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isAdmin && (
              <Button variant="primary" size="sm" iconLeft={<Icon name="message-circle" size={15} />} onClick={onStart} title="The client is on the phone now: name the stories, get the Roam link, and the transcript attaches itself when the call ends.">
                Start an interview
              </Button>
            )}
            <Button variant="ghost" size="sm" iconLeft={<Icon name="plus" size={15} />} onClick={onRequest}>New interview</Button>
          </div>
        </div>
        {isLoading ? (
          <div className="lead-none">Loading content…</div>
        ) : articles.length === 0 ? (
          <div className="lead-none">No interviews yet. Request one to get started.</div>
        ) : (
          <>
          {/* Always shown once there is anything to filter. Hiding these until
              the list passed a page in length meant an account with ten articles
              had no filters at all — a control that appears only sometimes reads
              as a missing feature. */}
          {articles.length > 0 ? (
            <div className="cq-filters">
              {/* Each select needs the field chrome around it: a bare .tr-select
                  has no border and stretches to 100%, which is why these sat
                  full-width and stacked. */}
              <label className="cq-filters__field">
                <span className="tr-field__label">Type</span>
                <span className="tr-field__control">
                  <Select value={kindFilter} onChange={onFilter(setKindFilter)}
                    options={[{ value: '', label: `All types (${articles.length})` },
                      ...kinds.map((k) => ({ value: k, label: KIND_FILTER_LABEL[k] || k }))]} />
                </span>
              </label>
              <label className="cq-filters__field">
                <span className="tr-field__label">Status</span>
                <span className="tr-field__control">
                  <Select value={stageFilter} onChange={onFilter(setStageFilter)}
                    options={[{ value: '', label: 'All statuses' },
                      ...ARTICLE_STATUS_ORDER.map((k) => ({ value: k, label: ARTICLE_STATUS_LABEL[k] }))]} />
                </span>
              </label>
              <label className="cq-filters__field">
                <span className="tr-field__label">Started by</span>
                <span className="tr-field__control">
                  <Select value={originFilter} onChange={onFilter(setOriginFilter)}
                    options={[{ value: '', label: 'Anyone' },
                      { value: 'client', label: 'Us' },
                      { value: 'triton', label: 'Triton' },
                      { value: 'unknown', label: 'Unknown' }]} />
                </span>
              </label>
              <span className="cq-filters__count">
                {shown.length === articles.length
                  ? `${shown.length} item${shown.length === 1 ? '' : 's'}`
                  : `${shown.length} of ${articles.length}`}
                {abandoned > 0 && (
                  <> <i>·</i> <button type="button" className="cq-linkbtn" onClick={() => { setShowAbandoned((v) => !v); setPage(0); }}>
                    {showAbandoned ? 'Hide' : 'Show'} {abandoned} unfinished
                  </button></>
                )}
              </span>
            </div>
          ) : null}
          {shown.length === 0 ? (
            <div className="lead-none">Nothing matches those filters.</div>
          ) : (
          <ul className="cq-list">
            {pageItems.map((a) => {
              return (
                <li key={a.id} className="cq-item cq-item--clickable" onClick={() => onOpen(a.id)}>
                  <div className="cq-item__body">
                    <div className="cq-item__top">
                      <div className="cq-tags">
                        <Badge variant="outline">{a.type}</Badge>
                        {a.city && <span className="cq-city"><Icon name="map-pin" size={13} /> {a.city}</span>}
                        {a.archived && <Badge variant="neutral">Archived</Badge>}
                        {/* Still in the list after a case study was written from it: more jobs in it to write. */}
                        {!a.archived && (writtenFrom.get(a.id) ?? 0) > 0 && (
                          <Badge variant="warning">{writtenFrom.get(a.id)} case stud{writtenFrom.get(a.id) === 1 ? 'y' : 'ies'} written · more to write</Badge>
                        )}
                      </div>
                      {isAdmin
                        ? <StageAdmin id={a.id} stage={a.stage} pushedAt={a.pushedAt} />
                        : <StatusPill a={a} />}
                    </div>
                    <h3>{a.title}</h3>
                    {a.sourceStoryId && byId.has(a.sourceStoryId) && (
                      <button type="button" className="cq-fromlink"
                        onClick={(e) => { e.stopPropagation(); onOpen(a.sourceStoryId!); }}>
                        <Icon name="link" size={12} /> From interview: {byId.get(a.sourceStoryId)!.title}
                      </button>
                    )}
                    {a.sourceStoryId?.startsWith('vault:') && (
                      <span className="cq-fromlink"><Icon name="file-text" size={12} /> From a Vault transcript</span>
                    )}
                    <p>{a.summary}</p>
                    <div className="cq-item__foot">
                      <span className="cq-expert">
                        <Avatar name={a.expert} size="sm" tone="cream" />
                        <span><strong>{a.expert}</strong><em>{a.role}</em></span>
                      </span>
                      <Progress stage={a.stage} />
                      <span className="cq-meta">{a.words ? `${a.words.toLocaleString()} words` : 'Not started'} <i>·</i> {a.date}{a.date.startsWith('Answers received') && <> <Badge variant="success" dot>Ready to write</Badge></>}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          )}
          {pages > 1 && (
            <div className="cq-pager">
              <Button variant="outline" size="sm" disabled={current === 0} onClick={() => setPage(current - 1)}
                iconLeft={<Icon name="chevron-left" size={14} />}>Previous</Button>
              <span className="cq-pager__at">Page {current + 1} of {pages}</span>
              <Button variant="outline" size="sm" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}
                iconRight={<Icon name="chevron-right" size={14} />}>Next</Button>
            </div>
          )}
          </>
        )}
      </Card>
      )}
    </div>
  );
}

// Fallback Job Type options when a client has no services set in its profile.
const DEFAULT_JOB_TYPES = ['AC Repair', 'AC Replacement', 'Furnace Install', 'Heat Pump', 'Duct Cleaning', 'Maintenance', 'New Build', 'Water Heater', 'Other'];
/** Effective Job Type options for a client: its profile services (+ Other), or the default set. */
function jobTypeOptions(fromProfile: string[]): string[] {
  if (!fromProfile.length) return DEFAULT_JOB_TYPES;
  return fromProfile.includes('Other') ? fromProfile : [...fromProfile, 'Other'];
}

function PhotoUploader({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const add = (list: FileList | null) => {
    if (!list) return;
    onChange([...files, ...Array.from(list).slice(0, 8 - files.length)]);
  };
  return (
    <div className="cq-up">
      <div
        className="cq-up__zone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
      >
        <span className="cq-up__icon"><Icon name="image" size={24} /></span>
        <strong>Drop job photos here or click to upload</strong>
        <em>Before/after, crews on-site, finished installs · up to 8 photos</em>
        <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => add(e.target.files)} />
      </div>
      {files.length > 0 && (
        <div className="cq-up__grid">
          {files.map((f, i) => (
            <div key={i} className="cq-up__thumb" style={{ backgroundImage: `url(${URL.createObjectURL(f)})` }}>
              <button aria-label="Remove" onClick={() => onChange(files.filter((_, j) => j !== i))}><Icon name="x" size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const DOC_ACCEPT = '.pdf,.txt,.md,.doc,.docx,application/pdf,text/plain';
const isReadableDoc = (name: string) => /\.(pdf|txt|md)$/i.test(name);

/* Document uploader for AI interviews — proposals, quotes, spec sheets. Claude
   reads PDFs/text natively; other types are stored for Triton but not analyzed. */
export function DocUploader({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const add = (list: FileList | null) => {
    if (!list) return;
    onChange([...files, ...Array.from(list).slice(0, 6 - files.length)]);
  };
  return (
    <div className="cq-up">
      <div
        className="cq-up__zone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
      >
        <span className="cq-up__icon"><Icon name="quote" size={24} /></span>
        <strong>Drop proposals or documents, or click to upload</strong>
        <em>PDF works best — the AI reads it. Proposals, quotes, spec sheets · up to 6 files</em>
        <input ref={inputRef} type="file" accept={DOC_ACCEPT} multiple hidden onChange={(e) => add(e.target.files)} />
      </div>
      {files.length > 0 && (
        <ul className="cq-doclist">
          {files.map((f, i) => (
            <li key={i}>
              <Icon name="quote" size={14} />
              <span className="cq-doclist__name">{f.name}</span>
              {!isReadableDoc(f.name) && <em className="cq-doclist__note">stored, not auto-read</em>}
              <button aria-label="Remove" onClick={() => onChange(files.filter((_, j) => j !== i))}><Icon name="x" size={13} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* Interviewee picker: choose an existing client user (auto-fills name + email for
   the calendar invite) or add a new contact. Falls back to free text if the
   account has no client users yet. */
function IntervieweePicker({ name, email, onName, onEmail }: { name: string; email: string; onName: (v: string) => void; onEmail: (v: string) => void }) {
  const { data } = useQuery({ queryKey: ['interviewContacts'], queryFn: getInterviewContacts, staleTime: 5 * 60_000 });
  const users = data?.users ?? [];
  const [adding, setAdding] = useState(false);
  const matched = users.find((u) => u.email === email);
  const selectValue = adding ? '__new__' : (matched ? email : '');
  const showFields = adding || users.length === 0 || (!!name && !matched);
  const onSelect = (v: string) => {
    if (v === '__new__') { setAdding(true); onName(''); onEmail(''); return; }
    setAdding(false);
    const u = users.find((x) => x.email === v);
    onName(u?.name ?? '');
    onEmail(u?.email ?? '');
  };
  return (
    <>
      {users.length > 0 && (
        <Select
          label="Who should we interview?"
          value={selectValue}
          options={[
            { value: '', label: 'Select a team member' },
            ...users.map((u) => ({ value: u.email, label: u.name ? `${u.name} — ${u.email}` : u.email })),
            { value: '__new__', label: '+ Add a new contact' },
          ]}
          onChange={(e) => onSelect(e.target.value)}
        />
      )}
      {showFields && (
        <div className="cq-fieldrow">
          <Input label={users.length ? 'Contact name' : 'Who should we interview?'} placeholder="Name & role" value={name} onChange={(e) => onName(e.target.value)} />
          <Input label="Contact email" hint="For the calendar invite." placeholder="name@company.com" value={email} onChange={(e) => onEmail(e.target.value)} />
        </div>
      )}
    </>
  );
}

function fmtSlotDay(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz }).format(new Date(iso));
}
function fmtSlotTime(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }).format(new Date(iso));
}

/* Post-save scheduling window: shows the Content Manager's open 30-min slots
   (from their Google Calendar). Booking creates the event + invites; Skip still
   notifies the team. `onDone(scheduled)` closes it. */
export function ScheduleModal({ articleId, intervieweeEmail, onDone }: { articleId: string; intervieweeEmail?: string; onDone: (scheduled: boolean) => void }) {
  const { data, isLoading } = useQuery({ queryKey: ['interviewSlots', articleId], queryFn: () => getInterviewSlots(articleId) });
  const [email, setEmail] = useState(intervieweeEmail ?? '');
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const tz = data?.timezone ?? 'America/New_York';
  const groups: [string, string[]][] = [];
  {
    const map = new Map<string, string[]>();
    for (const s of data?.slots ?? []) { const day = fmtSlotDay(s, tz); const arr = map.get(day) ?? []; arr.push(s); map.set(day, arr); }
    for (const e of map.entries()) groups.push(e);
  }
  const bookable = !data?.noCm && !data?.error && groups.length > 0;

  const book = async () => {
    if (!selected) return;
    setBusy(true); setErr(null);
    try { await scheduleInterview(articleId, selected, email.trim()); onDone(true); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not schedule.'); setBusy(false); }
  };
  const skip = async () => {
    setBusy(true);
    try { await skipInterviewSchedule(articleId); } catch { /* still close */ }
    onDone(false);
  };

  return (
    <div className="admin-modal">
      <div className="admin-modal__scrim" onClick={busy ? undefined : skip} />
      <div className="admin-modal__panel sched-panel">
        <div className="admin-modal__head">
          <div>
            <h3>Schedule your interview</h3>
            <p className="dash-muted">{data?.cm?.name ? `Pick a 30-minute time with ${data.cm.name}.` : 'Pick a 30-minute time.'}</p>
          </div>
        </div>

        {isLoading ? (
          <p className="dash-muted">Loading available times…</p>
        ) : data?.noCm ? (
          <p className="dash-muted">No content manager is assigned yet — our team will reach out to schedule. You can close this.</p>
        ) : data?.error ? (
          <p className="dash-muted">Couldn’t load the calendar right now — our team will reach out to schedule.</p>
        ) : groups.length === 0 ? (
          <p className="dash-muted">No open times in the next two weeks — our team will reach out to schedule.</p>
        ) : (
          <>
            <Input label="Interviewee email" hint="Optional — we’ll add them to the calendar invite." placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            <div className="sched-days">
              {groups.map(([day, slots]) => (
                <div key={day} className="sched-day">
                  <h4>{day}</h4>
                  <div className="sched-slots">
                    {slots.map((s) => (
                      <button key={s} type="button" className={`sched-slot ${selected === s ? 'is-sel' : ''}`} onClick={() => setSelected(s)}>{fmtSlotTime(s, tz)}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {err && <p className="dash-muted" style={{ color: 'var(--red-600)' }}>{err}</p>}
        <div className="sched-actions">
          <button className="cq-cancel" onClick={skip} disabled={busy}>Skip for now</button>
          {bookable && (
            <Button variant="primary" onClick={book} disabled={busy || !selected} iconRight={<Icon name="calendar" size={15} />}>
              {busy ? 'Scheduling…' : 'Confirm time'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/* One story's draft while the form is open. `key` is local only — the real id is
   minted server-side on submit, and that is what the uploads are tagged with. */
interface StoryDraft {
  key: string;
  title: string;
  jobType: string;
  city: string;
  description: string;
  files: File[];
  docFiles: File[];
}
const blankStory = (prefill?: Prefill): StoryDraft => ({
  key: crypto.randomUUID(),
  title: prefill?.title ?? '',
  jobType: prefill?.jobType ?? '',
  city: prefill?.city ?? '',
  description: prefill?.title ? `Suggested topic: ${prefill.title}\n\n` : '',
  files: [],
  docFiles: [],
});

/* A live interview covers up to three stories. They are named and described HERE
   rather than untangled afterwards, because the photos and documents are what
   suffer: uploaded in one pile against the interview, nothing downstream can say
   which job a photo belongs to, and whoever sorts them is working from memory.

   Each story is its own card with the name as its FIRST field, so by the time the
   uploader is on screen the thing being uploaded to already has a name. One card
   is open at a time; the rest collapse to a summary line. */
/* `startNow` is the admin's version of this form: the client is on the phone
   this minute, or turned up to a booked call with two more stories. The same
   stories, the same intake, but Submit starts the interview instead of
   opening the scheduler: the article is marked in progress and a Roam link
   comes back for the call, so Magic Minutes' transcript attaches itself the
   way a scheduled one does. */
function RequestForm({ onBack, prefill, jobTypes, startNow = false }: { onBack: () => void; prefill?: Prefill; jobTypes: string[]; startNow?: boolean }) {
  const qc = useQueryClient();
  const [started, setStarted] = useState<{ roamUrl: string; dialIn: string; roamConfigured: boolean } | null>(null);
  const [stories, setStories] = useState<StoryDraft[]>(() => [blankStory(prefill)]);
  const [openKey, setOpenKey] = useState<string>(() => stories[0].key);
  const [interviewee, setInterviewee] = useState('');
  const [intervieweeEmail, setIntervieweeEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [scheduleId, setScheduleId] = useState<string | null>(null); // set after save → opens the scheduler
  const [sent, setSent] = useState(false);
  const [wasScheduled, setWasScheduled] = useState(false);

  const patch = (key: string, p: Partial<StoryDraft>) =>
    setStories((cur) => cur.map((st) => (st.key === key ? { ...st, ...p } : st)));
  const addStory = () => {
    const next = blankStory();
    setStories((cur) => [...cur, next]);
    setOpenKey(next.key);
  };
  const removeStory = (key: string) => {
    setStories((cur) => {
      const left = cur.filter((st) => st.key !== key);
      const safe = left.length ? left : [blankStory()];
      if (key === openKey) setOpenKey(safe[safe.length - 1].key);
      return safe;
    });
  };
  const incomplete = (st: StoryDraft) => !st.title.trim() || !st.jobType || !st.city.trim() || !st.description.trim();

  const submit = async () => {
    const bad = stories.findIndex(incomplete);
    if (bad >= 0) {
      setOpenKey(stories[bad].key);
      setError(`Story ${bad + 1} needs a name, job type, city, and description.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      setProgress('Saving your stories…');
      const res = await createContentRequest({
        // The first story also fills the interview's own job type / city / brief,
        // which is what the library card and the existing pipeline read.
        jobType: stories[0].jobType,
        city: stories[0].city,
        description: stories[0].description,
        interviewee,
        intervieweeEmail,
        stories: stories.map(({ title, jobType, city, description }) => ({ title, jobType, city, description })),
        // Carries the topic's questions onto the article, so an interview
        // raised from a page scan knows what it is there to answer.
        topicId: prefill?.topicId,
      });
      /* Server order matches what was sent, so the returned ids line up with the
         drafts — that pairing is what keeps each upload on its own story. */
      for (const [i, st] of stories.entries()) {
        const projectId = res.stories[i]?.id;
        const label = stories.length > 1 ? ` for “${st.title}”` : '';
        for (const [n, f] of st.files.entries()) {
          setProgress(`Uploading photo ${n + 1} of ${st.files.length}${label}…`);
          await uploadContentPhoto(res.id, await processImage(f).catch(() => f), { projectId });
        }
        for (const [n, f] of st.docFiles.entries()) {
          setProgress(`Uploading document ${n + 1} of ${st.docFiles.length}${label}…`);
          await uploadContentDocument(res.id, f, { projectId });
        }
      }
      if (prefill?.topicId) await useTopic(prefill.topicId).catch(() => {});
      qc.invalidateQueries({ queryKey: qk.articles });
      if (startNow) {
        setProgress('Starting the interview…');
        setStarted(await startInterviewNow(res.id, intervieweeEmail));
        setSent(true);
        return;
      }
      setScheduleId(res.id); // open the scheduling window
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submission failed.');
    } finally {
      setSubmitting(false);
      setProgress('');
    }
  };

  if (sent && started) {
    return (
      <div className="cq-done">
        <span className="cq-done__icon"><Icon name="check-circle" size={30} /></span>
        <h2>Interview in progress</h2>
        {started.roamUrl ? (
          <>
            <p>Take the call in Roam so Magic Minutes records it — the transcript attaches itself to this interview when the call ends, and each story becomes its own case study.</p>
            <p style={{ margin: '12px 0' }}>
              <a className="tr-btn tr-btn--primary" href={started.roamUrl} target="_blank" rel="noreferrer"><Icon name="external" size={15} /><span>Join the Roam call</span></a>
            </p>
            {started.dialIn && <p className="dash-muted">Dial-in for the client: <strong>{started.dialIn}</strong></p>}
          </>
        ) : (
          <p>{started.roamConfigured
            ? 'Roam could not create a call link just now. Record the call another way and attach the transcript on the interview page.'
            : 'Roam is not connected, so there is no call link. Record the call and attach the transcript on the interview page.'}</p>
        )}
        <p className="dash-muted">If they bring up another job mid-call, add it under Projects on the interview page — the splitter writes one case study per project.</p>
        <Button variant="primary" onClick={onBack} iconLeft={<Icon name="arrow-right" size={15} />}>Back to library</Button>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="cq-done">
        <span className="cq-done__icon"><Icon name="check-circle" size={30} /></span>
        <h2>{wasScheduled ? 'Interview scheduled' : 'Interview request submitted'}</h2>
        <p>{wasScheduled
          ? 'Your interview is on the calendar — you’ll get an invite by email. We’ll turn it into publish-ready content for you to approve.'
          : 'Your Triton content strategist will reach out within one business day to schedule a 15-minute interview. We’ll turn it into publish-ready content for you to approve.'}</p>
        <Button variant="primary" onClick={onBack} iconLeft={<Icon name="arrow-right" size={15} />}>Back to contentIQ</Button>
      </div>
    );
  }

  const multi = stories.length > 1;

  return (
    <div className="cq-form">
      <button className="cq-back" onClick={onBack}><Icon name="chevron-left" size={16} /> Back to library</button>
      <div className="cq-form__head">
        <h2>{startNow ? 'Start an interview now' : 'Request a new interview'}</h2>
        <p>{startNow
          ? `Name each story they want to talk about — up to ${MAX_STORIES} on one call — and who is talking. Start opens the Roam link for the call; the transcript attaches itself when it ends.`
          : `Tell us about a recent job or customer win. We’ll interview the right person on your team and turn their story into content that helps you show up in AI and Google search. One interview can cover up to ${MAX_STORIES} stories — add each one separately so its photos stay with it.`}</p>
      </div>
      <div className="cq-form__grid">
        <div className="cq-form__main">
          {stories.map((st, i) => {
            const open = st.key === openKey;
            const ready = !incomplete(st);
            const attached = st.files.length + st.docFiles.length;
            return (
              <Card key={st.key} padding="lg" className="cq-card">
                <div className="cq-story__head">
                  <button type="button" className="cq-story__toggle" onClick={() => setOpenKey(open ? '' : st.key)}>
                    <Icon name={open ? 'chevron-down' : 'chevron-right'} size={15} />
                    <span className="cq-card__title">
                      {multi ? `Story ${i + 1}` : 'Job details'}
                      {!open && st.title.trim() ? <span className="cq-story__name"> — {st.title}</span> : null}
                    </span>
                    {!open && <Badge variant={ready ? 'success' : 'warning'}>{ready ? 'Ready' : 'Needs details'}</Badge>}
                    {!open && attached > 0 && (
                      <span className="cq-story__count">
                        {st.files.length} photo{st.files.length === 1 ? '' : 's'}
                        {st.docFiles.length ? `, ${st.docFiles.length} doc${st.docFiles.length === 1 ? '' : 's'}` : ''}
                      </span>
                    )}
                  </button>
                  {multi && <IconBtn label={`Remove story ${i + 1}`} icon="trash" onClick={() => removeStory(st.key)} />}
                </div>

                {open && (
                  <>
                    <Input
                      label="Story name" required
                      placeholder="e.g. New windows in a 1970s ranch, Milwaukee"
                      hint="Name it first — the photos and documents you add below stay attached to this story."
                      value={st.title} onChange={(e) => patch(st.key, { title: e.target.value })}
                    />
                    <div className="cq-fieldrow">
                      <Select label="Job type" required options={jobTypes} placeholder="Select a job type"
                        value={st.jobType} onChange={(e) => patch(st.key, { jobType: e.target.value })} />
                      <Input label="City / service area" required placeholder="e.g. Scottsdale, AZ"
                        leadingIcon={<Icon name="map-pin" size={15} />}
                        value={st.city} onChange={(e) => patch(st.key, { city: e.target.value })} />
                    </div>
                    <Textarea label="Describe the job or customer story" required rows={5}
                      placeholder="What was the problem, what did your team do, and why did it matter to the customer? The more specific, the better the article."
                      value={st.description} onChange={(e) => patch(st.key, { description: e.target.value })} />

                    <h4 className="cq-story__sub">Job photos</h4>
                    <p className="cq-card__hint">Real photos make articles more credible and rank better. Add before/after shots, crews on-site, or the finished work{multi ? ' — these stay with this story only' : ''}.</p>
                    <PhotoUploader files={st.files} onChange={(files) => patch(st.key, { files })} />

                    <h4 className="cq-story__sub">Project documents <span className="cq-card__opt">optional</span></h4>
                    <p className="cq-card__hint">Proposals, quotes, spec sheets, or notes about this job. The AI reads PDFs and text and folds the facts into your story — so the interview and article are more accurate and specific.</p>
                    <DocUploader files={st.docFiles} onChange={(docFiles) => patch(st.key, { docFiles })} />
                  </>
                )}
              </Card>
            );
          })}

          {stories.length < MAX_STORIES && (
            <button type="button" className="cq-story__add" onClick={addStory}>
              <Icon name="plus" size={15} /> Add another story
              <span>one interview can cover up to {MAX_STORIES}</span>
            </button>
          )}

          <Card padding="lg" className="cq-card">
            <h3 className="cq-card__title">Who should we interview?</h3>
            <p className="cq-card__hint">{multi ? 'One person covers all of these stories in a single interview.' : 'The person on your team who did the work, or knows it best.'}</p>
            <IntervieweePicker name={interviewee} email={intervieweeEmail} onName={setInterviewee} onEmail={setIntervieweeEmail} />
          </Card>
        </div>
        <div className="cq-form__side">
          <Card variant="dark" padding="lg" className="cq-tips">
            <span className="cq-tips__eyebrow"><Icon name="sparkles" size={14} /> How it works</span>
            <ol className="cq-tips__list">
              <li><span>1</span><p>You submit {multi ? `${stories.length} stories` : 'a job or story'} here.</p></li>
              <li><span>2</span><p>We run one 15-min recorded interview with your expert.</p></li>
              <li><span>3</span><p>Triton drafts an SEO + AI-search optimized article {multi ? 'for each story' : 'from it'}.</p></li>
              <li><span>4</span><p>You review, approve, and we publish to your site.</p></li>
            </ol>
          </Card>
          <div className="cq-submit">
            {error && <p className="dash-muted" style={{ color: 'var(--red-600)' }}>{error}</p>}
            {submitting && progress && <p className="dash-muted">{progress}</p>}
            <Button variant="primary" fullWidth iconRight={<Icon name="arrow-right" size={15} />} onClick={submit} disabled={submitting}>
              {submitting ? (startNow ? 'Starting…' : 'Submitting…') : startNow ? `Start the interview${multi ? ` (${stories.length} stories)` : ''}` : multi ? `Submit ${stories.length} stories` : 'Submit request'}
            </Button>
            <button className="cq-cancel" onClick={onBack}>Cancel</button>
          </div>
        </div>
      </div>

      {scheduleId && (
        <ScheduleModal
          articleId={scheduleId}
          intervieweeEmail={intervieweeEmail}
          onDone={(scheduled) => { setScheduleId(null); setWasScheduled(scheduled); setSent(true); }}
        />
      )}
    </div>
  );
}


/* ---- AI interview (chat) --------------------------------------------- */

// Dictation (SpeechRecognition + the mic button) lives in src/lib/dictation.tsx —
// shared by the AI interview, client gap answers and Expert Studio FAQs.

/* Intake before the AI interview — captures project details and (critically)
   photos upfront, since clients rarely send them afterward. Creates the article
   + uploads photos, then hands the new article id to the chat. */
function InterviewIntake({
  onReady,
  onBack,
  styles,
  defaultStyle,
  prefill,
  jobTypes,
}: {
  onReady: (id: string, style: string) => void;
  onBack: () => void;
  styles: InterviewStyle[];
  defaultStyle: string;
  prefill?: Prefill;
  jobTypes: string[];
}) {
  const [projectName, setProjectName] = useState(prefill?.title ?? '');
  const [jobType, setJobType] = useState(prefill?.jobType && jobTypes.includes(prefill.jobType) ? prefill.jobType : '');
  const [city, setCity] = useState(prefill?.city ?? '');
  const [interviewee, setInterviewee] = useState('');
  const [intervieweeEmail, setIntervieweeEmail] = useState('');
  const [style, setStyle] = useState(defaultStyle);
  const [files, setFiles] = useState<File[]>([]);
  const [docs, setDocs] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const styleDesc = styles.find((s) => s.id === style)?.description;

  const start = async () => {
    if (!projectName.trim() || !jobType || !city.trim()) {
      setError('Please fill in project name, job type, and city.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { id } = await createContentRequest({
        title: projectName,
        jobType,
        city,
        interviewee,
        intervieweeEmail,
        source: 'ai',
        topicId: prefill?.topicId,
      });
      for (const f of files) await uploadContentPhoto(id, await processImage(f).catch(() => f));
      for (const d of docs) await uploadContentDocument(id, d);
      if (prefill?.topicId) await useTopic(prefill.topicId).catch(() => {});
      onReady(id, style);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the interview.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cq-form">
      <button className="cq-back" onClick={onBack}><Icon name="chevron-left" size={16} /> Back to library</button>
      <div className="cq-form__head">
        <h2>Start an AI interview</h2>
        <p>A few details first — especially photos, which make the article far more credible. Add them now; the AI interviewer takes it from here.</p>
      </div>
      <Card padding="lg" className="cq-card">
        <div className="cq-fieldrow">
          <Input label="Project / story name" required placeholder="e.g. Scottsdale AC rescue" value={projectName} onChange={(e) => setProjectName(e.target.value)} />
          <Select label="Job type" required options={jobTypes} placeholder="Select a job type" value={jobType} onChange={(e) => setJobType(e.target.value)} />
        </div>
        <div className="cq-fieldrow">
          <Input label="City / service area" required placeholder="e.g. Scottsdale, AZ" leadingIcon={<Icon name="map-pin" size={15} />} value={city} onChange={(e) => setCity(e.target.value)} />
          <IntervieweePicker name={interviewee} email={intervieweeEmail} onName={setInterviewee} onEmail={setIntervieweeEmail} />
        </div>
        {styles.length > 1 && (
          <div className="cq-fieldrow">
            <Select
              label="Interview style"
              hint={styleDesc}
              options={styles.map((s) => ({ value: s.id, label: s.label }))}
              value={style}
              onChange={(e) => setStyle(e.target.value)}
            />
          </div>
        )}
      </Card>
      <Card padding="lg" className="cq-card">
        <h3 className="cq-card__title">Job photos</h3>
        <p className="cq-card__hint">Before/after, crews on-site, finished installs. Adding them now means Triton has what it needs to build the article.</p>
        <PhotoUploader files={files} onChange={setFiles} />
      </Card>
      <Card padding="lg" className="cq-card">
        <h3 className="cq-card__title">Project documents <span className="cq-card__opt">optional</span></h3>
        <p className="cq-card__hint">Upload a proposal, quote, spec sheet, or sales doc and the AI will read it to ask sharper questions and ground the article. PDFs are read automatically.</p>
        <DocUploader files={docs} onChange={setDocs} />
      </Card>
      <div className="cq-submit" style={{ maxWidth: 320 }}>
        {error && <p className="dash-muted" style={{ color: 'var(--red-600)' }}>{error}</p>}
        <Button variant="primary" fullWidth onClick={start} disabled={busy} iconRight={<Icon name="arrow-right" size={15} />}>
          {busy ? 'Starting…' : 'Start interview'}
        </Button>
        <button className="cq-cancel" onClick={onBack}>Cancel</button>
      </div>
    </div>
  );
}

function AiInterview({
  onBack,
  styles,
  defaultStyle,
  prefill,
  jobTypes,
}: {
  onBack: () => void;
  styles: InterviewStyle[];
  defaultStyle: string;
  prefill?: Prefill;
  jobTypes: string[];
}) {
  const [started, setStarted] = useState<{ id: string; style: string } | null>(null);
  if (!started) {
    return (
      <InterviewIntake
        onBack={onBack}
        onReady={(id, style) => setStarted({ id, style })}
        styles={styles}
        defaultStyle={defaultStyle}
        prefill={prefill}
        jobTypes={jobTypes}
      />
    );
  }
  return <InterviewChat articleId={started.id} style={started.style} onBack={onBack} />;
}

/* Shared by contentIQ and Expert Studio. The two hosts differ only at the edges:
   what the close link says, what happens the moment the draft exists (Studio
   submits the project so the client never sees a review page whose only button
   is "Send to Triton"), and where the finish button leads. `onBack` closes the
   chat; `onDoneBack` is the button on the success screen. */
export function InterviewChat({ articleId, style, onBack, onDoneBack, onFinished, backLabel, doneLabel, doneNote }: {
  articleId: string;
  style: string;
  onBack: () => void;
  onDoneBack?: () => void;
  onFinished?: (title: string) => void | Promise<void>;
  /** The close link while the interview is in progress. */
  backLabel?: string;
  /** The button on the success screen. */
  doneLabel?: string;
  /** The success message, given the draft's title. */
  doneNote?: (title: string) => string;
}) {
  const qc = useQueryClient();
  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedTitle, setSavedTitle] = useState<string | null>(null);
  /* The same dictation the client's gap answers use — one implementation, so a
     browser workaround fixed in one place is fixed in both. */
  const dictation = useDictation(setInput);
  const { listening, micError, pending: transcribing, supported: voiceSupported, level, engine } = dictation;
  const scrollRef = useRef<HTMLDivElement>(null);
  /* The three things a first-time dictator has to be told, each when it is
     true: the mic is live (timer + level bars on a Stop button), the words are
     coming (note under the box), and the next move is Send (the note again,
     and the Send button draws the eye once). The first pilot had none of
     these and did not know it was being heard. */
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!listening) { setElapsed(0); return; }
    const t0 = Date.now();
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 500);
    return () => window.clearInterval(id);
  }, [listening]);
  const [dictated, setDictated] = useState(false);
  useEffect(() => { if (listening) setDictated(false); }, [listening]);
  useEffect(() => { if (!listening && !transcribing && input.trim()) setDictated((d) => d || wasVoiceRef.current); wasVoiceRef.current = listening || transcribing; }, [listening, transcribing, input]);
  const wasVoiceRef = useRef(false);
  const clock = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;

  // Kick off the interview: get Claude's opening question.
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    sendInterviewMessage([], style, articleId)
      .then((r) => {
        if (cancelled) return;
        setMessages([{ role: 'assistant', content: r.reply }]);
        setDone(r.done);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to start interview.'))
      .finally(() => !cancelled && setBusy(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const send = async () => {
    if (busy) return;
    let text = input.trim();
    /* Send while the mic is on: stop it and wait for the last words. The box
       is cleared only after that, or the final phrase would land in an empty
       box a moment after the message went — or not go at all. */
    if (listening) {
      setBusy(true);
      try { text = (await stopDictation()).trim(); } finally { setBusy(false); }
    }
    if (!text) return;
    const next: ChatTurn[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setBusy(true);
    setError(null);
    try {
      const r = await sendInterviewMessage(next, style, articleId);
      setMessages([...next, { role: 'assistant', content: r.reply }]);
      setDone(r.done);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Message failed.');
    } finally {
      setBusy(false);
    }
  };

  const stopDictation = dictation.stop;
  const toggleMic = () => (listening ? dictation.stop() : dictation.start(input));

  const finish = async () => {
    if (listening) await stopDictation();
    setFinishing(true);
    setError(null);
    try {
      const r = await completeInterview(messages, articleId);
      qc.invalidateQueries({ queryKey: qk.articles });
      // Before the success screen, so a failure here is shown as an error
      // rather than a green check over an unsubmitted project.
      await onFinished?.(r.title);
      setSavedTitle(r.title);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the draft.');
    } finally {
      setFinishing(false);
    }
  };

  if (savedTitle) {
    return (
      <div className="cq-done">
        <span className="cq-done__icon"><Icon name="check-circle" size={30} /></span>
        <h2>Draft created</h2>
        <p>{doneNote ? doneNote(savedTitle) : `“${savedTitle}” is now in your library as a draft for Triton to review and publish. Thanks for the interview!`}</p>
        <Button variant="primary" onClick={onDoneBack ?? onBack} iconLeft={<Icon name="arrow-right" size={15} />}>{doneLabel ?? 'Back to contentIQ'}</Button>
      </div>
    );
  }

  return (
    <div className="cq-form">
      <button className="cq-back" onClick={onBack}><Icon name="chevron-left" size={16} /> {backLabel ?? 'Back to library'}</button>
      <div className="cq-form__head">
        <h2>AI interview</h2>
        <p>Chat through one recent job or customer win. Our AI interviewer asks a few questions, then Triton turns your answers into a publish-ready draft.</p>
      </div>

      <Card padding={false} className="cq-chat">
        <div className="cq-chat__log" ref={scrollRef}>
          {messages.map((m, i) => (
            <div key={i} className={`cq-msg cq-msg--${m.role}`}>
              {m.role === 'assistant' && <span className="cq-msg__mark"><Icon name="sparkles" size={14} /></span>}
              <div className="cq-msg__bubble">{m.content}</div>
            </div>
          ))}
          {busy && (
            <div className="cq-msg cq-msg--assistant">
              <span className="cq-msg__mark"><Icon name="sparkles" size={14} /></span>
              <div className="cq-msg__bubble cq-msg__bubble--typing">…</div>
            </div>
          )}
        </div>

        {error && <p className="dash-muted" style={{ color: 'var(--red-600)', padding: '0 16px' }}>{error}</p>}
        {micError && <p className="dash-muted" style={{ color: 'var(--amber-700)', padding: '0 16px' }}>{micError}</p>}

        <div className="cq-chat__composer">
          {voiceSupported && (listening ? (
            <button type="button" className="cq-mic cq-mic--stop" onClick={toggleMic} aria-label="Stop dictating and check the text" title="Stop dictating">
              <LevelBars level={level} />
              <span>Stop</span>
              <span className="cq-mic__time">{clock}</span>
            </button>
          ) : (
            <button
              type="button"
              className="cq-mic"
              onClick={toggleMic}
              disabled={transcribing}
              aria-label="Speak your answer"
              title="Speak your answer"
            >
              <Icon name="mic" size={16} />
            </button>
          ))}
          <textarea
            className="cq-chat__input"
            rows={3}
            placeholder={listening ? 'Listening — say your whole answer, then press Stop.' : transcribing ? 'Transcribing…' : 'Type, or press the mic and speak your answer…'}
            value={input}
            disabled={transcribing}
            onChange={(e) => {
              /* While the mic is on, every recognition event rewrites the box
                 from its own transcript, so a correction typed mid-dictation
                 vanished a moment later. Typing is the signal to stop. */
              if (listening) stopDictation();
              setDictated(false);
              setInput(e.target.value);
            }}
            onInput={(e) => { const el = e.currentTarget; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 320)}px`; }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <Button variant="primary" size="sm" className={dictated && input.trim() ? 'cq-send--ready' : ''} onClick={() => { setDictated(false); void send(); }} disabled={busy || transcribing || !input.trim()} iconRight={<Icon name="arrow-right" size={15} />}>
            Send
          </Button>
        </div>
        <p className="cq-chat__note">
          {listening
            ? (engine === 'whisper'
              ? 'Listening. Your words appear each time you pause. When you have said it all, press Stop.'
              : 'Listening. When you have said it all, press Stop, then check the text before sending.')
            : transcribing
              ? 'Transcribing your last words…'
              : dictated && input.trim()
                ? 'Read it over, fix anything — names and places are worth a glance — then press Send.'
                : 'Enter sends · Shift+Enter for a new line. Dictated text can be edited before sending.'}
        </p>
      </Card>

      <div className="cq-chat__finish">
        <span className="dash-muted">
          {done
            ? 'The interview is complete — create your draft whenever you’re ready.'
            : 'Answer the questions above. You can finish and generate a draft at any point.'}
        </span>
        <Button
          variant={done ? 'primary' : 'outline'}
          onClick={finish}
          disabled={finishing || messages.length < 2}
          iconRight={<Icon name="check" size={15} />}
        >
          {finishing ? 'Creating draft…' : 'Finish & create draft'}
        </Button>
      </div>
    </div>
  );
}

/* ---- article detail (extract/save for building the website article) --- */

function AuthedDoc({ id, filename }: { id: string; filename: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    getDocumentUrl(id)
      .then((u) => { objectUrl = u; if (active) setUrl(u); else URL.revokeObjectURL(u); })
      .catch(() => {});
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id]);
  return (
    <a className="cq-doc" href={url ?? undefined} target="_blank" rel="noreferrer" download={filename}>
      <Icon name="quote" size={15} /><span className="cq-doc__name">{filename}</span><Icon name="download" size={14} />
    </a>
  );
}

const SOCIAL_PLATFORMS: { key: SocialPlatform; label: string }[] = [
  { key: 'facebook', label: 'Facebook' }, { key: 'instagram', label: 'Instagram' },
  { key: 'linkedin', label: 'LinkedIn' }, { key: 'x', label: 'X' },
];
const SOCIAL_TONES = [
  { value: 'company', label: 'Company tone (from vault)' }, { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' }, { value: 'humorous', label: 'Humorous' },
  { value: 'serious', label: 'Serious' }, { value: 'inspirational', label: 'Inspirational' },
];

function SocialPostItem({ post, onChanged, client }: { post: SocialPost; onChanged: () => void; client?: boolean }) {
  const [body, setBody] = useState(post.body);
  const [busy, setBusy] = useState(false);
  const dirty = body !== post.body;
  const act = async (fn: () => Promise<void>) => { setBusy(true); try { await fn(); onChanged(); } finally { setBusy(false); } };
  const label = SOCIAL_PLATFORMS.find((p) => p.key === post.platform)?.label || post.platform;
  return (
    <div className={`soc-item ${post.status === 'approved' ? 'is-approved' : ''}`}>
      <div className="soc-item__head">
        <Badge variant="outline">{label}</Badge>
        {post.status === 'approved' && <Badge variant="success">Approved</Badge>}
        {client && post.status !== 'approved' && <Badge variant="neutral">Draft · Triton reviews</Badge>}
        <span className="soc-item__chars">{body.length} chars</span>
        <div className="soc-item__actions">
          <CopyBtn text={body} />
          {dirty && <Button variant="ghost" size="sm" disabled={busy} onClick={() => act(() => updateSocialPost(post.id, { body }))}>Save</Button>}
          {!client && <>
            <Button variant={post.status === 'approved' ? 'ghost' : 'outline'} size="sm" disabled={busy}
              onClick={() => act(() => updateSocialPost(post.id, { status: post.status === 'approved' ? 'draft' : 'approved' }))}>
              {post.status === 'approved' ? 'Unapprove' : 'Approve'}
            </Button>
            <IconBtn label="Delete post" onClick={() => act(() => deleteSocialPost(post.id))} disabled={busy} />
          </>}
        </div>
      </div>
      {post.caption && <p className="soc-item__caption"><Icon name="image" size={12} /> {post.caption}</p>}
      <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
    </div>
  );
}

function SocialFeed() {
  const [urls, setUrls] = useState<{ base: string; byPlatform: Record<string, string> } | null>(null);
  const [open, setOpen] = useState(false);
  const load = async () => { setOpen((v) => !v); if (!urls) setUrls(await getSocialFeedUrls()); };
  return (
    <div className="gen-linkmap">
      <button type="button" className="gen-linkmap__toggle" onClick={load}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> RSS feed for auto-posting (dlvr.it)
      </button>
      {open && (
        <div className="gen-linkmap__body">
          <p className="vault-add__hint">Point dlvr.it (or similar) at the per-platform feed — approved posts appear here automatically. Feed is public but unguessable; anyone with the link can read approved posts.</p>
          {!urls ? <p className="dash-muted">Loading…</p> : SOCIAL_PLATFORMS.map((p) => (
            <div key={p.key} className="soc-feed__row">
              <strong>{p.label}</strong>
              <code>{urls.byPlatform[p.key]}</code>
              <CopyBtn text={urls.byPlatform[p.key]} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SocialPosts({ articleId, photos, projects, client }: { articleId: string; photos: ArticlePhotoLike[]; projects: InterviewProject[]; client?: boolean }) {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState('');
  /* Scoped to the selected story: an interview covering three of them has three
     sets of posts, and showing all of them under one story is how they get
     posted against the wrong photo. */
  const { data } = useQuery({ queryKey: ['social', articleId, projectId], queryFn: () => getSocialPosts(articleId, projectId || undefined) });
  const [photoId, setPhotoId] = useState('');
  const [platforms, setPlatforms] = useState<SocialPlatform[]>(['facebook', 'instagram', 'linkedin']);
  const [tone, setTone] = useState('company');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const posts = data?.posts ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['social', articleId] }); // all stories' lists
  const toggle = (p: SocialPlatform) => setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  const visiblePhotos = projectId ? photos.filter((p) => p.projectId === projectId) : photos;

  const generate = async () => {
    if (!platforms.length) return;
    setBusy(true); setErr('');
    // By id, not title: the title can be edited, and every attachment points at the id.
    try { await generateSocialPosts({ articleId, platforms, tone, photoId: photoId || undefined, projectId: projectId || undefined }); refresh(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card padding="lg" className="cq-card">
      <h3 className="cq-card__title">Social posts</h3>
      <p className="cq-card__hint">{client
        ? 'Generate draft social posts from this story. Your Triton team reviews and schedules them.'
        : 'Generate on demand from this interview’s story. Pick a photo to caption it (AI vision) and build the post around it.'}</p>
      <div className="soc-controls">
        {projects.length > 0 && (
          <Select label="Project" value={projectId} onChange={(e) => { setProjectId(e.target.value); setPhotoId(''); }}
            options={[{ value: '', label: 'Whole interview' }, ...projects.map((p) => ({ value: p.id, label: p.title }))]} />
        )}
        <Select label="Image" value={photoId} onChange={(e) => setPhotoId(e.target.value)}
          options={[{ value: '', label: 'No image (text only)' }, ...visiblePhotos.map((p, i) => ({ value: p.id, label: p.filename || `Photo ${i + 1}` }))]} />
        <Select label="Tone" value={tone} onChange={(e) => setTone(e.target.value)} options={SOCIAL_TONES} />
      </div>
      <div className="soc-platforms">
        {SOCIAL_PLATFORMS.map((p) => (
          <button key={p.key} type="button" className={`vault-tab ${platforms.includes(p.key) ? 'is-on' : ''}`} onClick={() => toggle(p.key)}>{p.label}</button>
        ))}
        <Button variant="primary" size="sm" disabled={busy || !platforms.length} iconLeft={<Icon name="sparkles" size={14} />} onClick={generate}>
          {busy ? 'Writing…' : 'Generate posts'}
        </Button>
      </div>
      {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}
      {posts.length > 0 && <div className="soc-list">{posts.map((p) => <SocialPostItem key={p.id} post={p} onChanged={refresh} client={client} />)}</div>}
      {!client && <SocialFeed />}
    </Card>
  );
}

interface ArticlePhotoLike { id: string; filename: string; projectId: string; cdnUrl: string }

const pSlug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

function ProjectsEditor({ id, projects, photos, onChanged }: { id: string; projects: InterviewProject[]; photos: ArticlePhotoLike[]; onChanged: () => void }) {
  const [list, setList] = useState<InterviewProject[]>(projects);
  const [busy, setBusy] = useState(false);
  const [names, setNames] = useState<Record<string, string>>({});
  const dirty = JSON.stringify(list) !== JSON.stringify(projects);
  const named = list.filter((p) => p.title.trim());
  const save = async () => { setBusy(true); try { await setInterviewProjects(id, named); onChanged(); } finally { setBusy(false); } };
  const assign = async (photoId: string, projectId: string) => { await assignPhotoProject(photoId, projectId); onChanged(); };
  const defaultName = (ph: ArticlePhotoLike) => {
    const proj = named.find((p) => p.id === ph.projectId);
    if (proj) {
      const idx = photos.filter((p) => p.projectId === ph.projectId).findIndex((p) => p.id === ph.id) + 1;
      return `${pSlug(proj.title)}-${idx}`;
    }
    return pSlug(ph.filename.replace(/\.[^.]+$/, ''));
  };
  const publish = async (ph: ArticlePhotoLike) => {
    setBusy(true);
    try { await publishPhotoToCdn(ph.id, names[ph.id] ?? defaultName(ph)); onChanged(); } finally { setBusy(false); }
  };
  return (
    <Card padding="lg" className="cq-card">
      <h3 className="cq-card__title">Projects in this interview</h3>
      <p className="cq-card__hint">One recording can cover several projects. Title each one, then assign its photos below — case studies and social posts can then target a single project and use its images.</p>
      <div className="proj-list">
        {list.map((p, i) => (
          <div key={p.id} className="proj-row">
            <Input placeholder={`Project ${i + 1} title`} value={p.title} onChange={(e) => setList(list.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
            <IconBtn label="Remove project" onClick={() => setList(list.filter((_, j) => j !== i))} disabled={busy} />
          </div>
        ))}
      </div>
      <div className="proj-actions">
        <Button variant="outline" size="sm" disabled={busy} onClick={() => setList([...list, { id: crypto.randomUUID(), title: '' }])} iconLeft={<Icon name="plus" size={14} />}>Add project</Button>
        {dirty && <Button variant="primary" size="sm" disabled={busy} onClick={save}>Save projects</Button>}
      </div>
      {photos.length > 0 && (
        <div className="proj-photos">
          <p className="cq-card__hint">Assign each photo to a project, then publish it to the CDN (<code>cdn.poweredbytriton.com</code>) so generated content can use its URL.</p>
          {photos.map((ph) => (
            <div key={ph.id} className="proj-photo">
              <span className="proj-photo__name" title={ph.filename}>{ph.filename}</span>
              <Select value={ph.projectId} onChange={(e) => assign(ph.id, e.target.value)}
                options={[{ value: '', label: '— unassigned —' }, ...named.map((p) => ({ value: p.id, label: p.title }))]} />
              <Input value={names[ph.id] ?? defaultName(ph)} onChange={(e) => setNames((n) => ({ ...n, [ph.id]: e.target.value }))} placeholder="file name" />
              <Button variant="outline" size="sm" disabled={busy} onClick={() => publish(ph)} iconLeft={<Icon name="external" size={13} />}>
                {ph.cdnUrl ? 'Re-publish' : 'Publish'}
              </Button>
              {ph.cdnUrl && <CopyBtn text={ph.cdnUrl} label="URL" />}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ReviewAttach({ id, quote, author, onSaved }: { id: string; quote: string; author: string; onSaved: () => void }) {
  const [q, setQ] = useState(quote);
  const [a, setA] = useState(author);
  const [term, setTerm] = useState('');
  const [matches, setMatches] = useState<ReviewMatch[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const search = async () => {
    setSearching(true);
    try { setMatches((await searchClientReviews(term)).reviews); }
    catch { setMatches([]); }
    finally { setSearching(false); }
  };
  const save = async () => {
    setBusy(true);
    try { await attachReview(id, q.trim(), a.trim()); setDone(true); setTimeout(() => setDone(false), 1600); onSaved(); }
    finally { setBusy(false); }
  };

  return (
    <Card padding="lg" className="cq-card">
      <h3 className="cq-card__title">Customer review</h3>
      <p className="cq-card__hint">The accurate customer review for this project — used verbatim as the case-study quote. Search this client&rsquo;s reviews or paste one.</p>
      <div className="rev-search">
        <Input placeholder="Search reviews by name or keyword…" value={term} onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') search(); }} />
        <Button variant="outline" size="sm" disabled={searching} iconLeft={<Icon name="search" size={14} />} onClick={search}>
          {searching ? 'Searching…' : 'Search'}
        </Button>
      </div>
      {matches && (
        matches.length === 0
          ? <p className="dash-muted" style={{ padding: '4px 0' }}>No matching reviews (or GatherUp isn&rsquo;t connected for this client).</p>
          : <ul className="rev-list">
              {matches.map((m, i) => (
                <li key={i} className="rev-item" onClick={() => { setQ(m.content); setA(m.author); setMatches(null); }}>
                  <div className="rev-item__top">{'★'.repeat(Math.round(m.rating))} <strong>{m.author}</strong> <span>{m.date.slice(0, 10)}</span></div>
                  <p>{m.content}</p>
                </li>
              ))}
            </ul>
      )}
      <Textarea rows={3} placeholder="Review text (verbatim)…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="rev-save">
        <Input placeholder="Reviewer name (first name)" value={a} onChange={(e) => setA(e.target.value)} />
        <Button variant="primary" size="sm" disabled={busy} onClick={save} iconLeft={<Icon name={done ? 'check' : 'plus'} size={14} />}>
          {done ? 'Saved' : busy ? 'Saving…' : quote ? 'Update review' : 'Attach review'}
        </Button>
      </div>
    </Card>
  );
}

function TranscriptAttach({ id, existing, onSaved }: { id: string; existing: string; onSaved: () => void }) {
  const [text, setText] = useState(existing);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await attachTranscript(id, text.trim()); setDone(true); setTimeout(() => setDone(false), 1800); onSaved(); }
    finally { setBusy(false); }
  };
  return (
    <Card padding="lg" className="cq-card">
      <h3 className="cq-card__title">Interview transcript</h3>
      <p className="cq-card__hint">
        {existing
          ? 'The interview transcript. Edits are re-saved to the Authenticity Vault.'
          : 'Paste the live-interview transcript (Zoom, Google Meet, etc.). It is saved to this interview and added to the Authenticity Vault so its stories appear in the content generator.'}
      </p>
      <Textarea rows={8} placeholder="Paste the interview transcript…" value={text} onChange={(e) => setText(e.target.value)} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <Button variant="primary" size="sm" disabled={busy || !text.trim()} onClick={save} iconLeft={<Icon name={done ? 'check' : 'plus'} size={14} />}>
          {done ? 'Saved to vault' : busy ? 'Saving…' : existing ? 'Update transcript' : 'Save to vault'}
        </Button>
      </div>
    </Card>
  );
}

/* Who this article's words belong to.

   Chosen once here, and every transcript that comes off this article inherits
   it — ingestTranscript resolves the person from the article rather than asking
   each of the five places a transcript can arrive from. Which is also why it
   belongs on the article rather than on an interview: the AI interview, a
   pasted live transcript and the client's own gap answers all hang off the same
   record. */
function AttributionPicker({ id, expertId, fallback }: { id: string; expertId: string; fallback: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['experts'], queryFn: getExperts });
  const [busy, setBusy] = useState(false);
  const people = data?.experts ?? [];

  const choose = async (value: string) => {
    setBusy(true);
    try {
      await updateArticle(id, { expertId: value || null });
      await qc.invalidateQueries({ queryKey: ['article', id] });
    } finally { setBusy(false); }
  };

  if (!people.length) {
    return (
      <p className="dash-muted" style={{ fontSize: 12, margin: '6px 0' }}>
        <Icon name="info" size={12} /> No experts on record for this client yet — add one above the Vault
        and this interview can be attributed to them.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
      <span className="dash-muted" style={{ fontSize: 12 }}>Attributed to</span>
      <Select
        value={expertId}
        disabled={busy}
        onChange={(e) => choose(e.target.value)}
        options={[
          { value: '', label: fallback ? `Nobody yet (${fallback})` : 'Nobody yet' },
          ...people.map((p) => ({ value: p.id, label: p.title ? `${p.name} — ${p.title}` : p.name })),
        ]}
      />
    </div>
  );
}

function ArticleDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['article', id], queryFn: () => getArticle(id) });
  /* The Worker says whether the CALLER is an admin; "View as client" masks that
     here so the draft, transcript and admin actions hide like everything else. */
  const adminView = useIsAdmin();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushedUrl, setPushedUrl] = useState('');
  const [pushErr, setPushErr] = useState('');
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState('');
  // An HTML draft edits as the article (visual) or as its code.
  const [editMode, setEditMode] = useState<'visual' | 'code'>('visual');
  const visual = useRef<DraftEditorHandle>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [stageBusy, setStageBusy] = useState(false);
  const [dnnBusy, setDnnBusy] = useState(false);
  const [dnnPushedUrl, setDnnPushedUrl] = useState('');
  const [dnnWasDraft, setDnnWasDraft] = useState(false);
  const [dnnUpdated, setDnnUpdated] = useState(false);
  const [dnnDupe, setDnnDupe] = useState(false);
  const dnnCats = useDnnCategories(adminView && !!data?.isAdmin, data?.article.genKind === 'case_study' ? 'case_study' : 'blog');
  const { data: dnnHero } = useQuery({
    queryKey: ['dnnHero', 'article', id],
    queryFn: () => getDnnHero({ articleId: id }),
    enabled: adminView && !!data?.isAdmin && data?.article.genFormat === 'html',
  });

  if (isLoading) return <div className="lead-none">Loading…</div>;
  /* The reason, not just the fact. A bare "couldn't load" sent us hunting
     through the article data when the answer was a missing column in the query. */
  if (isError || !data) {
    return (
      <div className="lead-none">
        Couldn&rsquo;t load this article.
        {error ? <><br /><code style={{ fontSize: 11 }}>{error instanceof Error ? error.message : String(error)}</code></> : null}
      </div>
    );
  }

  const { article, projects, photos, documents } = data;
  const isAdmin = adminView && data.isAdmin;

  const afterChange = () => {
    qc.invalidateQueries({ queryKey: qk.articles });
    onBack();
  };
  const reject = async () => {
    setBusy(true);
    try { await setArticleRejected(id, true); afterChange(); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await deleteArticle(id); afterChange(); } finally { setBusy(false); }
  };
  // Split the stored body into the drafted article and the raw transcript.
  const [draft, transcript] = article.body.split('\n\n---\nTranscript:\n');
  const exportText = `# ${article.title}\n\n${article.summary}\n\n${article.body}`;

  // A generated piece (blog/case study) — not an interview intake. These can be
  // pushed to the client's Astro repo and don't take transcript attachments.
  const isGenerated = article.source === 'generated';
  const isAstroMd = article.genFormat === 'astro_md' || article.body.trimStart().startsWith('---');
  const pushKind: 'blog' | 'case_study' = article.genKind === 'blog' ? 'blog'
    : article.genKind === 'case_study' ? 'case_study'
    // Fallback for drafts saved before kind was stored: case studies carry client/service frontmatter.
    : /^\s*(client|service|location):/mi.test(article.body) ? 'case_study' : 'blog';
  // Pushable whenever the body is valid Astro frontmatter (generated or hand-edited).
  const canPush = isAstroMd;
  // HTML pieces go to the client's DNN site instead of the Astro repo. Without
  // this the library had no DNN path at all — approved HTML articles were stuck.
  const canPushDnn = isGenerated && article.genFormat === 'html' && !isAstroMd;
  const dnnDelivered = article.dnnUrl || dnnPushedUrl;
  const dnnLinked = article.dnnArticleId > 0;
  const changeStage = async (stage: ArticleStage) => {
    setStageBusy(true);
    try { await setArticleStage(id, stage); await qc.invalidateQueries({ queryKey: ['article', id] }); qc.invalidateQueries({ queryKey: qk.articles }); }
    finally { setStageBusy(false); }
  };
  // The DNN template, shown and edited as the article rather than as code.
  const isHtmlDraft = article.genFormat === 'html' && !isAstroMd;
  // A case study's photos, shown and ordered where they sit in the draft.
  const photoSlot = pushKind === 'case_study' && isHtmlDraft ? () => <DraftGallery articleId={id} /> : undefined;
  /* An Astro case study is the same sections as content_blocks, so it gets the
     same visual editor (astro-case-study.ts). Null when the Markdown can't be
     drawn — someone broke the YAML in the Markdown tab — and that tab is the way in. */
  const astroView = isAstroMd && pushKind === 'case_study' && draft?.trim() ? caseStudyHtml(draft.trim()) : null;
  const astroEdit = isAstroMd && pushKind === 'case_study' && editing ? caseStudyHtml(draftText) : null;
  const visualDraft = isHtmlDraft || (isAstroMd && pushKind === 'case_study');
  // The visual editor holds its edits in the page itself; draftText is the code tab's.
  const currentDraft = () => {
    if (editMode !== 'visual' || !visual.current?.isDirty()) return draftText;
    if (isHtmlDraft) return visual.current.getHtml();
    return astroEdit ? caseStudyFromHtml(draftText, visual.current.getHtml()) : draftText;
  };
  const switchEditMode = (m: string) => {
    if (m === editMode) return;
    if (m === 'code') setDraftText(currentDraft());
    setEditMode(m === 'code' ? 'code' : 'visual');
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const newBody = currentDraft() + (transcript ? `\n\n---\nTranscript:\n${transcript}` : '');
      await updateArticle(id, { body: newBody });
      await qc.invalidateQueries({ queryKey: ['article', id] });
      qc.invalidateQueries({ queryKey: qk.articles });
      setEditing(false);
    } finally { setSavingEdit(false); }
  };
  const onPush = async (isDraft: boolean) => {
    setPushing(true); setPushErr(''); setPushedUrl('');
    try {
      // Push ONLY the draft segment — article.body may carry an attached
      // interview transcript after the marker, which must never reach the site.
      const r = await pushToSite({ body: draft, kind: pushKind, draft: isDraft, articleId: id });
      setPushedUrl(r.url);
    } catch (e) { setPushErr(e instanceof Error ? e.message : String(e)); }
    finally { setPushing(false); }
  };
  const onPushDnn = async (isDraft: boolean, allowDuplicate = false) => {
    setDnnBusy(true); setPushErr(''); setDnnDupe(false);
    try {
      // The meta title and description are not sent from here: the API reads
      // them off the saved article (the SEO card below edits them). articleId
      // lets it recognise a piece it already delivered and PUT Blog/Update
      // instead of inserting a second copy.
      // Looked up again here rather than read from the cached query: a photo
      // uploaded on this page after it loaded would otherwise never be sent.
      const hero = await getDnnHero({ articleId: id }).catch(() => dnnHero);
      const r = await pushToDnn({
        title: article.title, summary: article.summary, html: article.body, draft: isDraft,
        kind: article.genKind === 'case_study' ? 'case_study' : 'blog',
        categoryIds: dnnCats.selected,
        moduleId: dnnCats.moduleId || undefined,
        imageUrl: hero?.imageUrl || undefined,
        articleId: id, allowDuplicate,
      });
      setDnnPushedUrl(r.url); setDnnWasDraft(r.draft); setDnnUpdated(r.updated);
      qc.invalidateQueries({ queryKey: ['article', id] });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(DNN_TITLE_EXISTS)) { setDnnDupe(true); setPushErr(msg.replace(/^API 409:\s*/, '')); }
      else setPushErr(msg);
    }
    finally { setDnnBusy(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(exportText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const download = () => {
    const blob = new Blob([exportText], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${article.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'article'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="cq-form">
      <button className="cq-back" onClick={onBack}><Icon name="chevron-left" size={16} /> Back to library</button>
      <div className="cq-detail__head">
        <div>
          <h2>{article.title}</h2>
          {adminView && <AttributionPicker id={id} expertId={article.expertId} fallback={article.expert} />}
          <div className="cq-tags" style={{ marginTop: 8 }}>
            {article.type && <Badge variant="outline">{article.type}</Badge>}
            {article.city && <span className="cq-city"><Icon name="map-pin" size={13} /> {article.city}</span>}
            <Badge variant={STATUS[article.stage as ArticleStage].variant} dot={article.stage < 3}>
              {STATUS[article.stage as ArticleStage].label}
            </Badge>
          </div>
        </div>
        {isAdmin && (
          <div className="cq-detail__actions">
            <label className="cq-detail__status">
              <span>Status</span>
              <Select
                value={String(article.stage)}
                disabled={stageBusy}
                onChange={(e) => changeStage(Number(e.target.value) as ArticleStage)}
                options={[{ value: '0', label: 'Interview' }, { value: '1', label: 'Draft' }, { value: '2', label: 'Review' }, { value: '3', label: 'Published' }]}
                aria-label="Status"
              />
            </label>
            <Button variant="outline" size="sm" onClick={copy} iconLeft={<Icon name="check" size={14} />}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button variant="primary" size="sm" onClick={download} iconLeft={<Icon name="arrow-right" size={14} />}>
              Download .md
            </Button>
            {canPush ? (
              pushedUrl
                ? <a href={pushedUrl} target="_blank" rel="noreferrer" className="vault-link"><Icon name="external" size={13} /> Pushed to repo</a>
                : <>
                    <Button variant="outline" size="sm" disabled={pushing} onClick={() => onPush(true)}>{pushing ? 'Pushing…' : 'Push draft'}</Button>
                    <Button variant="outline" size="sm" disabled={pushing} onClick={() => onPush(false)}>Push &amp; publish</Button>
                  </>
            ) : canPushDnn ? (
              /* The link and the buttons coexist: a piece already on DNN as a
                 draft still needs a publish push, so delivery must not remove
                 the controls. */
              <>
                {dnnDelivered && (
                  <a href={dnnDelivered} target="_blank" rel="noreferrer" className="vault-link">
                    {/* Submit returns a /preview/true URL for published articles too,
                        so the link is no evidence of state — only our own flag is. */}
                    <Icon name="external" size={13} /> {dnnWasDraft ? 'DNN draft (preview)' : 'On DNN'}{article.dnnPushedAt ? ` · ${article.dnnPushedAt.slice(0, 10)}` : ''}
                  </a>
                )}
                {/* A linked article is UPDATED, and Update takes no categories, so
                    the category gate only applies to a first push. */}
                <Button variant="outline" size="sm" disabled={dnnBusy || (!dnnLinked && !dnnCats.selected.length)} title={dnnLinked || dnnCats.selected.length ? undefined : 'Select a DNN category first'} onClick={() => onPushDnn(true)}>{dnnBusy ? 'Pushing…' : dnnLinked ? 'Update on DNN (draft)' : 'Push to DNN (draft)'}</Button>
                <Button variant="outline" size="sm" disabled={dnnBusy || (!dnnLinked && !dnnCats.selected.length)} title={dnnLinked || dnnCats.selected.length ? undefined : 'Select a DNN category first'} onClick={() => onPushDnn(false)}>{dnnLinked ? 'Update on DNN & publish' : <>DNN &amp; publish</>}</Button>
              </>
            ) : (
              /* Not site-ready (e.g. an AI-interview draft: prose, no Astro
                 frontmatter). A dead disabled button here read as a bug — send
                 the admin to the generator with THIS story preloaded instead. */
              article.gapStatus ? (
                /* A gap article stands for a TOPIC the client answered questions
                   about, not an interview. It belongs in the blog composer with
                   its brief, which is where its answers ground it. */
                <Button variant="primary" size="sm" iconLeft={<Icon name="sparkles" size={14} />}
                  title={article.gapStatus === 'closed'
                    ? 'The client’s answers are in the Vault — write this blog grounded in them.'
                    : 'Still waiting on the client’s answers. You can write it now, but it won’t be grounded in them yet.'}
                  onClick={() => window.location.assign(`/content?genTopic=${encodeURIComponent(id)}`)}>
                  Write this blog
                </Button>
              ) : article.studioKind === 'knowledge' ? (
                /* A Knowledge Session is an expert teaching a topic — a blog,
                   not a project story. The blog composer gets the transcript as
                   its primary source and the blog template + blog destination
                   on the site. The case-study path stays one click away. */
                <>
                  <Button variant="primary" size="sm" iconLeft={<Icon name="sparkles" size={14} />}
                    title="Write this Knowledge Session as a blog article, grounded in the expert's transcript, delivered to the site's blog."
                    onClick={() => window.location.assign(`/content?genBlog=${encodeURIComponent(id)}`)}>
                    Write as blog
                  </Button>
                  <Button variant="outline" size="sm"
                    title="Use the case-study template and destination instead."
                    onClick={() => window.location.assign(`/content?genStory=${encodeURIComponent(id)}`)}>
                    Write as case study
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="primary" size="sm" iconLeft={<Icon name="sparkles" size={14} />}
                    title="This draft isn't site-formatted yet. The generator turns it into a grounded, Astro-formatted piece for this client's site — then push from there."
                    onClick={() => window.location.assign(`/content?genStory=${encodeURIComponent(id)}`)}>
                    Generate site version
                  </Button>
                  <Button variant="outline" size="sm"
                    title="Write a blog article grounded in this interview instead of a case study."
                    onClick={() => window.location.assign(`/content?genBlog=${encodeURIComponent(id)}`)}>
                    Write as blog
                  </Button>
                </>
              )
            )}
            <Button variant="ghost" size="sm" onClick={reject} disabled={busy}>Reject</Button>
            {confirmDel ? (
              <>
                <Button variant="danger" size="sm" onClick={remove} disabled={busy}>{busy ? 'Deleting…' : 'Confirm delete'}</Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDel(false)} disabled={busy}>Cancel</Button>
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setConfirmDel(true)} iconLeft={<Icon name="trash" size={14} />}>Delete</Button>
            )}
          </div>
        )}
      </div>

      {/* The same surface the client sees in Expert Studio, and for the same
          reason: adding a photo, pulling one out of the gallery and choosing
          which one leads the piece are the same job from either side. Staff get
          it here so a case study can be finished without asking the client to
          go and do it. */}
      <Card padding="lg" className="cq-card">
        <CaseStudyPhotos articleId={id} heading={`Job photos`} />
      </Card>

      {isAdmin && documents.length > 0 && (
        <Card padding="lg" className="cq-card">
          <h3 className="cq-card__title">Project documents ({documents.length})</h3>
          <p className="cq-card__hint">Uploaded by the client for the AI to analyze. PDFs and text files were read into the interview brief.</p>
          <div className="cq-doc__grid">
            {documents.map((d) => <AuthedDoc key={d.id} id={d.id} filename={d.filename} />)}
          </div>
        </Card>
      )}

      {isAdmin ? (
        <>
          <Card padding="lg" className="cq-card">
            <div className="cq-draft__head">
              {/* Where this draft goes and how, behind an "i": read once, then in the way. */}
              <span className="cq-draft__title">
                <h3 className="cq-card__title">Draft</h3>
                <Hint label="Where this draft goes">
                  {canPush
                    ? <>Push this {pushKind === 'blog' ? 'blog' : 'case study'} to the client&rsquo;s Astro repo with the buttons above — &ldquo;Push draft&rdquo; keeps it as a draft in CloudCannon, &ldquo;Push &amp; publish&rdquo; goes live.</>
                    : canPushDnn
                      ? <>This is an HTML piece, so it delivers to the client&rsquo;s DNN site (EasyDNNNews) rather than the Astro repo. Pick its categories below — DNN only lists categorised articles.</>
                      : <>This draft isn&rsquo;t site-formatted yet (no Astro frontmatter) — pushing it as-is would break the client&rsquo;s site build. Click <strong>Generate site version</strong> above: the generator writes the site-ready case study grounded in this interview, and you push from there.</>}
                </Hint>
              </span>
              {!editing ? (
                <Button variant="primary" size="sm" iconLeft={<Icon name="edit" size={14} />}
                  onClick={() => { setDraftText(draft?.trim() ?? ''); setEditMode('visual'); setEditing(true); }}>Edit draft</Button>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="primary" size="sm" disabled={savingEdit} onClick={saveEdit}>{savingEdit ? 'Saving…' : 'Save'}</Button>
                  <Button variant="ghost" size="sm" disabled={savingEdit} onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              )}
            </div>
            {article.summary && !editing && <p className="cq-card__hint">{article.summary}</p>}
            {canPushDnn && !editing && dnnCats.configured && !dnnLinked && <DnnCategoryBar p={dnnCats} />}
            {dnnUpdated && <p className="cq-card__hint"><Icon name="check" size={13} /> Updated the existing DNN article in place — no second copy. Note the module’s Update can’t change the <strong>title</strong> or <strong>categories</strong>; those stay as first pushed.</p>}
            {pushErr && <p className="vault-err" style={{ marginTop: 8 }}><Icon name="alert-circle" size={13} /> {pushErr}</p>}
            {dnnDupe && (
              <div className="vault-add__actions">
                <Button variant="outline" size="sm" disabled={dnnBusy} onClick={() => onPushDnn(false, true)}>{dnnBusy ? 'Pushing…' : 'Publish anyway (adds a second copy)'}</Button>
                <Button variant="ghost" size="sm" onClick={() => { setDnnDupe(false); setPushErr(''); }}>Cancel</Button>
              </div>
            )}
            {editing && visualDraft && (
              <Tabs className="cq-draft__modes" value={editMode} onChange={switchEditMode}
                items={[{ value: 'visual', label: 'Visual' }, { value: 'code', label: isHtmlDraft ? 'HTML' : 'Markdown' }]} />
            )}
            {editing && isHtmlDraft && editMode === 'visual' ? (
              <HtmlDraft ref={visual} html={draftText} editing slot={photoSlot} />
            ) : editing && astroEdit && editMode === 'visual' ? (
              <HtmlDraft ref={visual} html={astroEdit} editing />
            ) : editing ? (
              <>
                {visualDraft && !isHtmlDraft && editMode === 'visual' && (
                  <p className="vault-err"><Icon name="alert-circle" size={13} /> This Markdown can&rsquo;t be read as case-study blocks, so it opens as text. Fix it here, then switch to Visual.</p>
                )}
                <Textarea rows={22} value={draftText} onChange={(e) => setDraftText(e.target.value)} />
              </>
            ) : isHtmlDraft && draft?.trim() ? (
              <HtmlDraft html={draft.trim()} editing={false} slot={photoSlot} />
            ) : astroView ? (
              <HtmlDraft html={astroView} editing={false} />
            ) : (
              <pre className="cq-detail__body">{draft?.trim() || 'No draft yet.'}</pre>
            )}
          </Card>
          {isGenerated && <SeoMetaCard articleId={id} metaTitle={article.metaTitle} metaDescription={article.metaDescription} />}
          {article.gapStatus && article.gapAnswers && article.gapAnswers.length > 0 && (
            <Card padding="lg">
              <h3 className="cq-gapqa__title">Client answers</h3>
              <ol className="cq-gapqa">
                {article.gapAnswers.map((g, i) => (
                  <li key={i}>
                    <strong>{g.question}</strong>
                    {g.status === 'answered' && g.answer ? <p>{g.answer}</p> : g.status === 'declined' ? <p className="dash-muted">Declined{g.declinedReason ? ` — ${g.declinedReason}` : ''}</p> : <p className="dash-muted">Not answered yet</p>}
                  </li>
                ))}
              </ol>
              <p className="dash-muted" style={{ fontSize: 'var(--text-xs)', marginBottom: 0 }}>These answers are in the Authenticity Vault as a Q&amp;A source. &ldquo;Write this blog&rdquo; opens the generator with this topic, these questions and that source emphasised; saving the blog archives this record.</p>
            </Card>
          )}
          {/* A draft saved without an image can still get one. Accepting here
              links the photo to this article, and writes the URL into the body
              when the article is Astro and still holds the placeholder. */}
          {isGenerated && isAdmin && (
            <HeroImagePanel
              articleId={id}
              topic={article.title}
              article={article.body}
              slug={article.title}
              autoKey={0}
              platform={article.genFormat === 'html' ? 'dnn' : 'astro'}
              onApply={async (url, alt) => {
                if (isAstroMd && hasHeroPlaceholder(article.body)) {
                  await updateArticle(id, { body: applyHeroImage(article.body, url, alt) }).catch(() => {});
                }
                qc.invalidateQueries({ queryKey: ['article', id] });
                qc.invalidateQueries({ queryKey: qk.articles });
              }}
            />
          )}
          {!isGenerated && (
            <>
              {article.source !== 'ai' && article.stage === 0 && (
                <LiveCallBar id={id} article={article} onChanged={() => qc.invalidateQueries({ queryKey: ['article', id] })} />
              )}
              <TranscriptAttach
                id={id}
                existing={transcript?.trim() || ''}
                onSaved={() => {
                  qc.invalidateQueries({ queryKey: ['article', id] });
                  qc.invalidateQueries({ queryKey: qk.vault });
                  qc.invalidateQueries({ queryKey: ['generateContext'] });
                }}
              />
              <ReviewAttach
                id={id}
                quote={article.reviewQuote}
                author={article.reviewAuthor}
                onSaved={() => qc.invalidateQueries({ queryKey: ['article', id] })}
              />
              <ProjectsEditor id={id} projects={projects} photos={photos} onChanged={() => qc.invalidateQueries({ queryKey: ['article', id] })} />
              <SocialPosts articleId={id} photos={photos} projects={projects} />
            </>
          )}
        </>
      ) : (
        <Card padding="lg" className="cq-card">
          <p className="dash-muted">
            Your Triton team is preparing this content. You’ll be able to review it before it goes live.
          </p>
        </Card>
      )}
    </div>
  );
}

/* The live call, for the admin running it. A booked interview whose client
   rang early, or a request nobody scheduled, is started from here: the row
   is marked in progress and gets its Roam link, so the transcript lands on it
   when the call ends. Stories the client adds mid-call go in Projects below. */
function LiveCallBar({ id, article, onChanged }: { id: string; article: { date: string; roamUrl?: string; scheduledAt?: string; roamDialIn?: string }; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<{ roamUrl: string; roamConfigured: boolean } | null>(null);
  const inProgress = article.date === 'Interview in progress';
  const roamUrl = result?.roamUrl || article.roamUrl || '';
  const start = async () => {
    setBusy(true); setErr('');
    try { setResult(await startInterviewNow(id)); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Could not start the interview.'); }
    finally { setBusy(false); }
  };
  return (
    <Card padding="lg" className="cq-card">
      <div className="cq-card__head">
        <h3 className="cq-card__title">{inProgress ? 'Interview in progress' : article.scheduledAt ? 'Live interview booked' : 'Live interview'}</h3>
        {inProgress && <Badge variant="warning" dot>Recording</Badge>}
      </div>
      <p className="cq-card__hint">
        {inProgress
          ? 'Take the call in Roam so Magic Minutes records it. The transcript attaches itself here when the call ends; add any story they bring up under Projects so the splitter writes one case study per project.'
          : 'Client on the phone now? Start marks this interview in progress and opens the Roam link, so the transcript attaches itself when the call ends.'}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {roamUrl && (
          <a className="tr-btn tr-btn--primary tr-btn--sm" href={roamUrl} target="_blank" rel="noreferrer"><Icon name="external" size={14} /><span>Join the Roam call</span></a>
        )}
        {!inProgress && (
          <Button variant={roamUrl ? 'outline' : 'primary'} size="sm" disabled={busy} onClick={() => void start()} iconLeft={<Icon name="message-circle" size={14} />}>
            {busy ? 'Starting…' : 'Start the call now'}
          </Button>
        )}
        {article.roamDialIn && <span className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>Dial-in: <strong>{article.roamDialIn}</strong></span>}
      </div>
      {result && !result.roamUrl && (
        <p className="dash-muted" style={{ marginTop: 8 }}>{result.roamConfigured ? 'Roam could not create a call link just now — record the call another way and attach the transcript below.' : 'Roam is not connected — record the call and attach the transcript below.'}</p>
      )}
      {err && <p className="dash-muted" style={{ color: 'var(--red-600)', marginTop: 8 }}>{err}</p>}
    </Card>
  );
}

export function ContentIQ() {
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState<'library' | 'request' | 'start' | 'interview'>('library');
  const [openId, setOpenId] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<Prefill | undefined>();
  // Deep-link: /content?article=<id> opens that article (e.g. from Admin → Content).
  useEffect(() => {
    const a = params.get('article');
    if (a) setOpenId(a);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data: settings } = useQuery({ queryKey: ['interview', 'settings'], queryFn: getInterviewSettings });
  const aiEnabled = settings?.enabled ?? false;
  /* Deep-link from the "new article idea" email and the dashboard notice:
     /content?topic=<id>&start=ai|request lands on that topic's interview
     with the form filled, exactly as the panel's Start button does. Waits for
     the interview settings, because "ai" is only honoured when the client
     has the AI interview switched on; otherwise it is the request form. */
  useEffect(() => {
    const t = params.get('topic');
    if (!t || settings === undefined) return;
    const start = params.get('start');
    getTopics().then((list) => {
      const topic = list.find((x) => x.id === t);
      if (!topic) return;
      setPrefill({ title: topic.title, jobType: topic.projectType, city: topic.city, topicId: topic.id });
      setMode(start === 'request' ? 'request' : (settings.enabled ? 'interview' : 'request'));
    }).catch(() => {});
    setParams((p) => { const n = new URLSearchParams(p); n.delete('topic'); n.delete('start'); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings === undefined]);
  const jobTypes = jobTypeOptions(settings?.jobTypes ?? []);

  const back = () => { setMode('library'); setPrefill(undefined); };
  const startTopic = (t: Topic) => {
    setPrefill({ title: t.title, jobType: t.projectType, city: t.city, topicId: t.id });
    setMode(aiEnabled ? 'interview' : 'request');
  };

  const closeArticle = () => {
    setOpenId(null);
    if (params.get('article')) setParams((p) => { const n = new URLSearchParams(p); n.delete('article'); return n; }, { replace: true });
  };
  if (openId) return <ArticleDetail id={openId} onBack={closeArticle} />;
  if (mode === 'request') return <RequestForm onBack={back} prefill={prefill} jobTypes={jobTypes} />;
  if (mode === 'start') return <RequestForm onBack={back} prefill={prefill} jobTypes={jobTypes} startNow />;
  if (mode === 'interview') {
    return (
      <AiInterview
        onBack={back}
        styles={settings?.styles ?? []}
        defaultStyle={settings?.style ?? 'guided'}
        prefill={prefill}
        jobTypes={jobTypes}
      />
    );
  }
  return (
    <Library
      aiEnabled={aiEnabled}
      onRequest={() => { setPrefill(undefined); setMode('request'); }}
      onStart={() => { setPrefill(undefined); setMode('start'); }}
      onInterview={() => { setPrefill(undefined); setMode('interview'); }}
      onOpen={setOpenId}
      onStartTopic={startTopic}
      jobTypes={jobTypes}
    />
  );
}

/* The piece's SEO title and meta description, kept with the article (0122) so
   a push from here, days later, still sends them — DNN stores them as the
   article's TitleTag and MetaDecription. AI writes them: the generator usually
   has, and when a piece arrives with none (saved before 0122, an interview, a
   writer that skipped them) the first look here writes them from the article.
   The limits are the ones the generator is asked to keep to. */
function SeoMetaCard({ articleId, metaTitle, metaDescription }: { articleId: string; metaTitle: string; metaDescription: string }) {
  const qc = useQueryClient();
  const [t, setT] = useState(metaTitle);
  const [d, setD] = useState(metaDescription);
  useEffect(() => { setT(metaTitle); setD(metaDescription); }, [metaTitle, metaDescription]);
  const save = useMutation({
    mutationFn: () => updateArticle(articleId, { metaTitle: t, metaDescription: d }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['article', articleId] }),
  });
  const write = useMutation({
    mutationFn: (force: boolean) => writeArticleMeta(articleId, force),
    onSuccess: (r) => { setT(r.metaTitle); setD(r.metaDescription); void qc.invalidateQueries({ queryKey: ['article', articleId] }); },
  });
  // Once per article, and only when both are missing — never over someone's own.
  const asked = useRef('');
  useEffect(() => {
    if (metaTitle || metaDescription || asked.current === articleId) return;
    asked.current = articleId;
    write.mutate(false);
  }, [articleId, metaTitle, metaDescription]);
  const dirty = t !== metaTitle || d !== metaDescription;
  const err = save.error ?? write.error;
  return (
    <Card padding="lg" className="cq-card">
      <div className="cq-draft__head">
        <h3 className="cq-card__title">SEO</h3>
        <div className="cq-seo__acts">
          <Button variant="ghost" size="sm" iconLeft={<Icon name="sparkles" size={14} />} disabled={write.isPending || save.isPending}
            onClick={() => write.mutate(true)}>
            {write.isPending ? 'Writing…' : metaTitle || metaDescription ? 'Rewrite with AI' : 'Write with AI'}
          </Button>
          <Button variant="primary" size="sm" disabled={!dirty || save.isPending || write.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
      <p className="cq-card__hint">Written by AI from the article; edit and save to change them. Sent with every DNN push.</p>
      <div className="cq-seo">
      <Input label="Meta title" value={t} maxLength={120} onChange={(e) => setT(e.target.value)}
        hint={`${t.length} / 60 characters${t.length > 60 ? ` — Google shows about the first 60 (“${t.slice(0, 60).trim()}…”), so keep the project's keywords in that part` : ''}`} />
      <Textarea label="Meta description" rows={3} value={d} maxLength={300} onChange={(e) => setD(e.target.value)}
        hint={`${d.length} / 155 characters${d.length > 155 ? ' — Google will cut it off' : ''}`} />
      </div>
      {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err instanceof Error ? err.message.replace(/^API \d+:\s*/, '') : 'That did not work.'}</p>}
    </Card>
  );
}

